import {FC, useState, useEffect, useCallback, useRef} from 'react'
import {getPayments, chargeAccount, getPaymentHistory, updatePaymentSettings, updateTrialEnd, addPaymentNote, updatePaymentNote, deletePaymentNote, addPaymentMethod, createPaymentLink, loginAsUser, bulkDisableAutoPayment, lockAccount, unlockAccount, normalizeTrialEod, getAutoChargeEligible, getCardcomTokenCandidates, addCardcomToken, setActivePaymentToken, setClientGender, getWaTemplates, createWaTemplate, updateWaTemplate, deleteWaTemplate, sendClientWhatsapp, getClientWaHistory, recordManualPayment, addAccountImportantNote, getBankDetails, saveBankDetails, generateAiReviews, getContactTasks, flagForContact, unflagContact, addContactTaskNote, deleteContactTaskNote, resolveContactTask, getLinkPricing, saveLinkPricing, getCustomerPaymentLinks, saveCustomerPrices, saveCustomerMessage, sendPaymentLinksWhatsapp, getVirtualNumberActivity} from './_requests'
import type {CardcomTokenCandidate, PaymentMethodsInfo, PaymentMethod, AccountInvoiceMethod, WaTemplate, WaMessage, AiReview, AiVerdict, ContactTask, LinkPricing, ResolvedPrices, PriceOverride, VnActivity} from './_requests'
import {setPostFooterPosition} from '../accounts/core/_requests'
import WatermarkOverridePanel from '../accounts/WatermarkOverridePanel'
import {createIssue, getIssueAdmins} from '../issues/core/_requests'
import {ISSUE_DEPARTMENTS} from '../issues/core/_models'
import {createMeeting, getAdminUsers, getTakenSlots, getMeetings} from '../meetings/core/_requests'
import {MEETING_TYPE_LABELS} from '../meetings/core/_models'
import type {MeetingType} from '../meetings/core/_models'
import {checkAdminAuth} from '../auth/_requests'
import PaymentsCalendar, {AccountTasksModal, AccountTaskPreview} from './PaymentsCalendar'

// Effective post-footer position of an account (mirrors AccountDetailPage).
const footerPosOf = (a: any): 'start' | 'end' | null =>
  (a?.dynamic_options?.post_footer_position as any) || (a?.dynamic_options?.post_footer_enabled ? 'end' : null)

const FB_ACCESS_TOKEN = process.env.REACT_APP_FB_ACCESS_TOKEN || ''

const fbPicUrl = (fbId?: string) => fbId
  ? `https://graph.facebook.com/${fbId}/picture?type=square${FB_ACCESS_TOKEN ? `&access_token=${FB_ACCESS_TOKEN}` : ''}`
  : ''

const STATUS_DOT_COLOR: Record<string, string> = {
  connected: '#4caf50',
  disconnected: '#ff9800',
  error: '#f44336'
}

const hebrewFromNow = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000)
  if (diffSec < 60) return 'לפני רגע'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `לפני ${diffMin} ${diffMin === 1 ? 'דקה' : 'דקות'}`
  const diffHour = Math.floor(diffMin / 60)
  if (diffHour < 24) return `לפני ${diffHour} ${diffHour === 1 ? 'שעה' : 'שעות'}`
  const diffDay = Math.floor(diffHour / 24)
  if (diffDay < 7) return `לפני ${diffDay} ${diffDay === 1 ? 'יום' : 'ימים'}`
  if (diffDay < 30) {
    const weeks = Math.floor(diffDay / 7)
    return `לפני ${weeks} ${weeks === 1 ? 'שבוע' : 'שבועות'}`
  }
  if (diffDay < 365) {
    const months = Math.floor(diffDay / 30)
    return `לפני ${months} ${months === 1 ? 'חודש' : 'חודשים'}`
  }
  const years = Math.floor(diffDay / 365)
  return `לפני ${years} ${years === 1 ? 'שנה' : 'שנים'}`
}

interface PaymentAccount {
  _id: string
  first_name: string
  last_name: string
  email: string
  phone: string
  company_name?: string
  gender?: 'male' | 'female' | null
  monthly_payment_amount: number
  monthly_payment_include_vat: boolean
  billing_day: number
  payment_tokens: any[]
  is_active: boolean
  trial_period_end?: string
  child_accounts?: any[]
  disable_auto_payment?: boolean
  payment_period?: string
  is_locked?: boolean
  createdAt: string
  posted_count_14d?: number   // posts published in the last 14 days (incl. xpost extras)
  dynamic_options?: {post_footer_enabled?: boolean; post_footer_position?: 'start' | 'end' | null; [key: string]: any}
  wa_messages?: WaMessage[]   // recent WhatsApp messages sent to this client (attached by the payments API)
  // Customer pressed "cancel subscription" on their own payment page. Recorded on the
  // account (and as a ticket), but the payments API never returned it, so nobody saw it.
  cancellation_requested?: boolean
  cancellation_requested_at?: string
  cancellation_reason?: string
  // AI's read-only opinion on whether to charge this client (cached server-side).
  ai_review?: AiReview | null
  // Open "we must call them before charging" flag, if one exists.
  contact_task?: {_id: string; reason?: string; created_at: string; notes_count: number} | null
  open_tasks_count?: number
  // The next few open tasks, shown inline on the card.
  open_tasks?: {_id: string; reason?: string; due_date?: string | null; due_time?: string | null; progress?: 'new' | 'in_progress'; notes_count?: number}[]
}

// How each AI verdict is presented on a card and in the contact list.
const VERDICT_META: Record<AiVerdict, {label: string; color: string; bg: string; border: string; icon: string}> = {
  charge: {label: 'לחייב', color: '#1a7f5a', bg: '#e8fff3', border: '#b6ecd5', icon: 'bi-check-circle-fill'},
  // Using the product with no price set — money we're leaving on the table.
  set_price: {label: 'לקבוע מחיר ולחייב', color: '#5c3ec6', bg: '#f4f1ff', border: '#d9cffb', icon: 'bi-tag-fill'},
  contact: {label: 'ליצור קשר', color: '#b76e00', bg: '#fff8dd', border: '#ffe0a3', icon: 'bi-telephone-fill'},
  dont_charge: {label: 'לא לחייב', color: '#c2334a', bg: '#fff5f8', border: '#f9c4cf', icon: 'bi-x-octagon-fill'},
  review: {label: 'לבדוק', color: '#5e6278', bg: '#f4f6f9', border: '#e1e5ec', icon: 'bi-question-circle-fill'},
}

interface Stats {
  total_mrr: number
  total_mrr_with_card: number
  total_mrr_without_card: number
  total_paying: number
  total_with_token: number
  total_without_token: number
  total_auto_payment: number
  total_auto_payment_mrr: number
  total_mrr_with_vat: number
  recent_collected: number
  recent_count: number
  upcoming_charges: PaymentAccount[]
}

const FILTERS = [
  {value: '', label: 'הכל'},
  {value: 'has_balance', label: 'חיוב חודשי > 0'},
  {value: 'has_card', label: 'יש כרטיס'},
  {value: 'active_autopay', label: 'מוכנים לחיוב'},
  {value: 'no_payment_method', label: 'ללא אמצעי תשלום'},
]

const PERIOD_LABELS: Record<string, string> = {
  monthly: 'חודשי',
  '3months': '3 חודשים',
  yearly: 'שנתי',
}

const PERIOD_MULTIPLIER: Record<string, number> = {
  monthly: 1,
  '3months': 3,
  yearly: 12,
}

// Days each period extends the subscription (mirrors backend PERIOD_CONFIG).
const PERIOD_DAYS: Record<string, number> = {
  monthly: 30,
  '3months': 90,
  yearly: 365,
}

// Mirror of renderPaymentLinksMessage() in routes/admin-payments.js, so the
// preview under the editor is exactly what the client will receive. The only
// difference is the links: they don't exist until the send mints them, so the
// preview stands in a label.
const renderLinksMessage = (
  template: string,
  account: {first_name?: string; last_name?: string},
  prices: {monthly: number; yearly: number; monthly_installments: number; yearly_installments: number},
  links: {monthly: string; yearly: string}
): string => String(template || '')
  .replace(/\{first_name\}/g, account.first_name || '')
  .replace(/\{last_name\}/g, account.last_name || '')
  .replace(/\{monthly_price\}/g, String(prices.monthly))
  .replace(/\{yearly_price\}/g, String(prices.yearly))
  .replace(/\{monthly_installments_text\}/g, prices.monthly_installments > 1 ? ` (עד ${prices.monthly_installments} תשלומים)` : '')
  .replace(/\{yearly_installments_text\}/g, prices.yearly_installments > 1 ? ` (עד ${prices.yearly_installments} תשלומים)` : '')
  .replace(/\{monthly_installments\}/g, String(prices.monthly_installments))
  .replace(/\{yearly_installments\}/g, String(prices.yearly_installments))
  .replace(/\{monthly_link\}/g, links.monthly)
  .replace(/\{yearly_link\}/g, links.yearly)

// Tokens without which the message is useless — the client gets no way to pay.
const REQUIRED_LINK_TOKENS = ['{monthly_link}', '{yearly_link}']

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

const PaymentsPage: FC = () => {
  const [accounts, setAccounts] = useState<PaymentAccount[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('')
  const [sort, setSort] = useState('trial_end:asc')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [charging, setCharging] = useState<string | null>(null)
  const [chargeResults, setChargeResults] = useState<Record<string, {success: boolean; message: string}>>({})
  const [historyModal, setHistoryModal] = useState<{accountId: string; name: string} | null>(null)
  const [history, setHistory] = useState<any[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [chargeModal, setChargeModal] = useState<PaymentAccount | null>(null)
  const [chargePeriod, setChargePeriod] = useState('monthly')
  const [chargeInvoiceName, setChargeInvoiceName] = useState('')
  const [chargeInstallments, setChargeInstallments] = useState(1)
  // Payment-link modal (send a charge link to the client)
  const [linkModal, setLinkModal] = useState<PaymentAccount | null>(null)
  const [linkResult, setLinkResult] = useState<{url: string; amount: number; period_label?: string; max_installments?: number} | null>(null)
  const [settingsModal, setSettingsModal] = useState<PaymentAccount | null>(null)
  const [needsCharge, setNeedsCharge] = useState<PaymentAccount[]>([])
  const [trialEditModal, setTrialEditModal] = useState<PaymentAccount | null>(null)
  const [noteModal, setNoteModal] = useState<PaymentAccount | null>(null)
  const [noteText, setNoteText] = useState('')
  const [noteSaving, setNoteSaving] = useState(false)
  const [noteTicketDept, setNoteTicketDept] = useState<'' | 'customer_service' | 'development'>('')
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editingNoteText, setEditingNoteText] = useState('')
  const [bulkDisabling, setBulkDisabling] = useState(false)
  const [normalizingEod, setNormalizingEod] = useState(false)
  const [loginAsLoading, setLoginAsLoading] = useState<string | null>(null)
  const [needsChargeFrom, setNeedsChargeFrom] = useState<string>(() => {
    const d = new Date()
    d.setDate(d.getDate() - 3)
    return d.toISOString().split('T')[0]
  })
  const [needsChargeSearch, setNeedsChargeSearch] = useState('')
  // Debounced copy of the above, and whether the list currently on screen came
  // back from that server-side lookup rather than the billing-date bucket.
  const [ncSearchQ, setNcSearchQ] = useState('')
  const [needsChargeIsSearch, setNeedsChargeIsSearch] = useState(false)
  const [showLocked, setShowLocked] = useState(false)
  const [includeActive, setIncludeActive] = useState(false)
  const [lockModal, setLockModal] = useState<PaymentAccount | null>(null)
  const [lockReason, setLockReason] = useState('')
  const [lockSaving, setLockSaving] = useState(false)
  const [autoChargeModal, setAutoChargeModal] = useState(false)
  const [paymentMethodModal, setPaymentMethodModal] = useState<{account: PaymentAccount; url: string} | null>(null)
  const [paymentMethodLoading, setPaymentMethodLoading] = useState<string | null>(null)
  const [recoverModal, setRecoverModal] = useState<PaymentAccount | null>(null)
  // Saved-cards popup: every card the account ever had, with the option to put
  // an older one back as the active card.
  const [cardsModal, setCardsModal] = useState<PaymentAccount | null>(null)
  const [editingAmount, setEditingAmount] = useState<string | null>(null)
  const [editAmountValue, setEditAmountValue] = useState(0)
  // WhatsApp messaging: per-client send modal + global template manager
  const [waModal, setWaModal] = useState<PaymentAccount | null>(null)
  const [templatesModal, setTemplatesModal] = useState(false)
  // Open a real issue (תקלה) for this customer (dept + assignee).
  const [issueModal, setIssueModal] = useState<PaymentAccount | null>(null)
  const [meetingModal, setMeetingModal] = useState<PaymentAccount | null>(null)
  const [manualPayModal, setManualPayModal] = useState<{account: PaymentAccount; method: 'bank_transfer' | 'bit'} | null>(null)
  // Shared bank-transfer details: loaded once, copied from any client row, edited in one modal.
  const [bankDetails, setBankDetails] = useState('')
  const [bankDetailsModal, setBankDetailsModal] = useState(false)

  // AI billing review — generated on demand, cached server-side.
  const [aiRunning, setAiRunning] = useState(false)
  const [aiSingle, setAiSingle] = useState<string | null>(null)
  const [aiError, setAiError] = useState<string | null>(null)
  // "Need to contact" list
  const [contactTasks, setContactTasks] = useState<ContactTask[]>([])
  const [contactListOpen, setContactListOpen] = useState(false)
  // The task calendar is the top of this page — open on every visit.
  const [calendarOpen, setCalendarOpen] = useState(true)
  const [tasksModal, setTasksModal] = useState<PaymentAccount | null>(null)
  // Bumped whenever a task changes outside the calendar, so an open calendar reloads.
  const [taskRev, setTaskRev] = useState(0)
  const bumpTasks = useCallback(() => setTaskRev(r => r + 1), [])
  const [contactLoading, setContactLoading] = useState(false)
  const [flagging, setFlagging] = useState<string | null>(null)
  // Never-expiring payment links over WhatsApp
  const [linksModal, setLinksModal] = useState<PaymentAccount | null>(null)
  const [pricingModal, setPricingModal] = useState(false)
  // Virtual-number lead activity, loaded in the background after the list renders.
  const [vnActivity, setVnActivity] = useState<Record<string, VnActivity>>({})
  const [vnLoading, setVnLoading] = useState(false)
  // Accounts we already asked about. The response only carries clients who HAVE
  // a number, so "not in vnActivity" can't stand in for "not fetched yet".
  const vnRequested = useRef<Set<string>>(new Set())
  // Same idea for the AI pass: an account that came back without a comment must
  // not be re-requested on every render.
  const aiRequested = useRef<Set<string>>(new Set())
  const isMobile = useIsMobile()

  useEffect(() => {
    getBankDetails().then(r => setBankDetails(r.data?.details || '')).catch(() => {})
  }, [])

  const handleSetGender = async (accountId: string, gender: 'male' | 'female' | null) => {
    const prev = (accounts.find(a => a._id === accountId) || needsCharge.find(a => a._id === accountId))?.gender ?? null
    patchAccount(accountId, {gender})
    try {
      await setClientGender(accountId, gender)
    } catch (e: any) {
      patchAccount(accountId, {gender: prev})
      alert(e.response?.data?.message || 'שגיאה בעדכון מגדר')
    }
  }

  const handleAddPaymentMethod = async (account: PaymentAccount) => {
    setPaymentMethodLoading(account._id)
    try {
      const res = await addPaymentMethod(account._id)
      if (res.data.success && res.data.url) {
        setPaymentMethodModal({account, url: res.data.url})
      } else {
        alert(res.data.error || 'שגיאה ביצירת קישור תשלום')
      }
    } catch (e: any) {
      alert(e.response?.data?.error || 'שגיאה ביצירת קישור תשלום')
    }
    setPaymentMethodLoading(null)
  }

  const handleLoginAs = async (account: PaymentAccount) => {
    setLoginAsLoading(account._id)
    try {
      const res = await loginAsUser(account._id)
      if (res.data.success && res.data.api_token) {
        const authData = JSON.stringify({api_token: res.data.api_token})
        const newWindow = window.open('about:blank', '_blank')
        if (newWindow) {
          newWindow.localStorage.setItem('kt-auth-react-v', authData)
          newWindow.location.href = '/campaigns'
        }
      } else {
        alert(res.data.error || 'שגיאה בכניסה כמשתמש')
      }
    } catch (e: any) {
      alert(e.response?.data?.error || 'שגיאה בכניסה כמשתמש')
    }
    setLoginAsLoading(null)
  }

  const handleLockAccount = async () => {
    if (!lockModal || !lockReason.trim()) return
    setLockSaving(true)
    try {
      const res = await lockAccount(lockModal._id, lockReason.trim())
      if (res.data.success) {
        patchAccount(lockModal._id, {is_locked: true})
        setLockModal(null)
        setLockReason('')
      } else {
        alert(res.data.error || 'שגיאה בנעילת החשבון')
      }
    } catch (e: any) {
      alert(e.response?.data?.error || 'שגיאה בנעילת החשבון')
    }
    setLockSaving(false)
  }

  const mapNotesAcrossAccounts = (noteId: string, mapper: (notes: any[]) => any[]) => {
    const updater = (a: PaymentAccount) => {
      const notes = (a as any).payment_notes as any[] | undefined
      if (!notes?.some(n => n._id === noteId)) return a
      return {...a, payment_notes: mapper(notes)} as PaymentAccount
    }
    setAccounts(prev => prev.map(updater))
    setNeedsCharge(prev => prev.map(updater))
  }

  const handleSaveNoteEdit = async () => {
    if (!editingNoteId || !editingNoteText.trim()) return
    const noteId = editingNoteId
    const newText = editingNoteText.trim()
    try {
      await updatePaymentNote(noteId, newText)
      mapNotesAcrossAccounts(noteId, notes => notes.map(n => n._id === noteId ? {...n, description: newText} : n))
      setEditingNoteId(null)
      setEditingNoteText('')
    } catch (e: any) {
      alert(e.response?.data?.error || 'שגיאה בעדכון הערה')
    }
  }

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('למחוק את ההערה?')) return
    try {
      await deletePaymentNote(noteId)
      mapNotesAcrossAccounts(noteId, notes => notes.filter(n => n._id !== noteId))
    } catch (e: any) {
      alert(e.response?.data?.error || 'שגיאה במחיקת הערה')
    }
  }

  const handleUnlockAccount = async (account: PaymentAccount) => {
    if (!window.confirm(`להפעיל מחדש את החשבון של ${account.first_name} ${account.last_name}?`)) return
    try {
      const res = await unlockAccount(account._id)
      if (res.data.success) patchAccount(account._id, {is_locked: false})
      else alert(res.data.error || 'שגיאה בהפעלת החשבון')
    } catch (e: any) {
      alert(e.response?.data?.error || 'שגיאה בהפעלת החשבון')
    }
  }

  const handleNormalizeEod = async () => {
    if (!window.confirm('לעדכן את כל תאריכי סיום התקופה לסוף היום (שעון ישראל)?')) return
    setNormalizingEod(true)
    try {
      const res = await normalizeTrialEod()
      if (res.data.success) {
        alert(`עודכנו ${res.data.normalized} חשבונות (${res.data.alreadyEod} כבר היו בסוף היום)`)
        loadData()
      } else {
        alert(res.data.error || 'שגיאה בעדכון')
      }
    } catch (e: any) {
      alert(e.response?.data?.error || 'שגיאה בעדכון')
    }
    setNormalizingEod(false)
  }

  const handleBulkDisableAutopay = async () => {
    if (!window.confirm('לכבות חיוב אוטומטי לכל החשבונות? פעולה זו תשפיע על כל הלקוחות המשלמים ולא ניתן לבטל אותה בלחיצה אחת.')) return
    if (!window.confirm('אישור אחרון: לכבות חיוב אוטומטי לכל החשבונות?')) return
    setBulkDisabling(true)
    try {
      const res = await bulkDisableAutoPayment()
      if (res.data.success) {
        alert(`חיוב אוטומטי כובה ל-${res.data.modified_count} חשבונות`)
        loadData()
      } else {
        alert(res.data.error || 'שגיאה בכיבוי חיוב אוטומטי')
      }
    } catch (e: any) {
      alert(e.response?.data?.error || 'שגיאה בכיבוי חיוב אוטומטי')
    }
    setBulkDisabling(false)
  }

  const patchAccount = useCallback((id: string, patch: Partial<PaymentAccount> | ((a: PaymentAccount) => PaymentAccount)) => {
    const apply = (a: PaymentAccount) =>
      a._id === id ? (typeof patch === 'function' ? patch(a) : {...a, ...patch}) : a
    setAccounts(prev => prev.map(apply))
    setNeedsCharge(prev => prev.map(apply))
  }, [])

  // ---- Virtual-number lead activity -------------------------------------
  // Loaded after the list is on screen (one PayCall request per number), so the
  // page never waits on it. Accounts without a tracked number simply don't appear.
  const loadVnActivity = useCallback(async (ids: string[]) => {
    if (!ids.length) return
    ids.forEach(id => vnRequested.current.add(id))
    setVnLoading(true)
    try {
      const res = await getVirtualNumberActivity(ids, {days: 30})
      if (res.data.success) setVnActivity(prev => ({...prev, ...res.data.activity}))
    } catch (e) { console.error('Failed to load virtual-number activity:', e) }
    setVnLoading(false)
  }, [])

  // ---- AI billing review ----------------------------------------------
  // The AI only writes a comment; nothing on the account is ever changed by it.
  const applyAiReviews = useCallback((reviews: Record<string, AiReview>) => {
    const apply = (a: PaymentAccount) => reviews[a._id] ? {...a, ai_review: reviews[a._id]} : a
    setAccounts(prev => prev.map(apply))
    setNeedsCharge(prev => prev.map(apply))
  }, [])

  // Review every account currently on screen. Unchanged accounts come back from
  // the server cache, so re-running is cheap.
  const runAiReview = async (ids: string[], force = false) => {
    if (!ids.length) return
    setAiError(null)
    if (ids.length === 1) setAiSingle(ids[0]); else setAiRunning(true)
    try {
      const res = await generateAiReviews(ids, force)
      if (res.data.success) {
        applyAiReviews(res.data.reviews || {})
        if (res.data.errors?.length) setAiError(res.data.errors[0])
      } else {
        setAiError(res.data.error || 'שגיאה בניתוח AI')
      }
    } catch (e: any) {
      setAiError(e?.response?.data?.error || e?.message || 'שגיאה בניתוח AI')
    } finally {
      setAiRunning(false); setAiSingle(null)
    }
  }

  // ---- "Need to contact" list -------------------------------------------
  const loadContactTasks = useCallback(async () => {
    setContactLoading(true)
    try {
      const res = await getContactTasks('open')
      setContactTasks(res.data.tasks || [])
    } catch (e) { console.error('Failed to load contact tasks:', e) }
    setContactLoading(false)
  }, [])

  useEffect(() => { loadContactTasks() }, [loadContactTasks])

  const handleToggleContactFlag = async (account: PaymentAccount) => {
    setFlagging(account._id)
    try {
      if (account.contact_task) {
        // Closes every open task of the account (server side), so the count drops to 0.
        await unflagContact(account._id)
        patchAccount(account._id, {contact_task: null, open_tasks_count: 0, open_tasks: []})
        bumpTasks()
        setContactTasks(prev => prev.filter(t => t.account?._id !== account._id))
      } else {
        // Seed the reason from the AI comment so the list explains itself.
        const suggested = account.ai_review?.comment || ''
        const reason = window.prompt('סיבת יצירת הקשר (אפשר לערוך):', suggested)
        if (reason === null) { setFlagging(null); return }
        const res = await flagForContact(account._id, reason.trim(), account.ai_review ? 'ai' : 'manual')
        const task = res.data.task
        patchAccount(account._id, {
          contact_task: {_id: task._id, reason: task.reason, created_at: task.createdAt, notes_count: (task.notes || []).length},
          open_tasks_count: Math.max(1, account.open_tasks_count || 0),
          open_tasks: (account.open_tasks || []).some(t => t._id === task._id)
            ? account.open_tasks
            : [...(account.open_tasks || []), {_id: task._id, reason: task.reason, due_date: task.due_date || null, due_time: task.due_time || null, progress: task.progress || 'new', notes_count: (task.notes || []).length}],
        })
        bumpTasks()
        setContactTasks(prev => [task, ...prev.filter(t => t._id !== task._id)])
      }
    } catch (e: any) {
      alert('שגיאה: ' + (e?.response?.data?.error || e?.message || ''))
    } finally { setFlagging(null) }
  }

  // Per-account "just updated" flag — drives a brief highlight in the UI so
  // the admin sees the row changed without losing scroll position.
  const [recentlyUpdated, setRecentlyUpdated] = useState<Record<string, number>>({})
  const markUpdated = useCallback((id: string) => {
    setRecentlyUpdated(prev => ({...prev, [id]: Date.now()}))
    window.setTimeout(() => {
      setRecentlyUpdated(prev => {
        const {[id]: _, ...rest} = prev
        return rest
      })
    }, 2500)
  }, [])

  // Refresh data without flipping `loading=true` — keeps the list mounted so
  // the user's scroll position is preserved. Used after edits where we don't
  // know the resulting server state locally (e.g. Cardcom iframe).
  const silentRefresh = useCallback(async () => {
    try {
      const res = await getPayments({search, filter, sort, page, limit: 50, needs_charge_from: needsChargeFrom, include_active: includeActive, needs_charge_search: ncSearchQ})
      setAccounts(res.data.accounts)
      setNeedsCharge(res.data.needs_charge || [])
      setNeedsChargeIsSearch(!!res.data.needs_charge_is_search)
      setStats(res.data.stats)
      setTotalPages(res.data.pagination.pages)
      setTotal(res.data.pagination.total)
    } catch (e) { /* ignore — keep current state */ }
  }, [search, filter, sort, page, needsChargeFrom, includeActive, ncSearchQ])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getPayments({search, filter, sort, page, limit: 50, needs_charge_from: needsChargeFrom, include_active: includeActive, needs_charge_search: ncSearchQ})
      setAccounts(res.data.accounts)
      setNeedsCharge(res.data.needs_charge || [])
      setNeedsChargeIsSearch(!!res.data.needs_charge_is_search)
      setStats(res.data.stats)
      setTotalPages(res.data.pagination.pages)
      setTotal(res.data.pagination.total)
    } catch (e) {
      console.error('Failed to load payments:', e)
    }
    setLoading(false)
  }, [search, filter, sort, page, needsChargeFrom, includeActive, ncSearchQ])

  useEffect(() => { loadData() }, [loadData])
  useEffect(() => { setPage(1) }, [search, filter, sort])

  // Debounce the "needs charge" box into the query the server actually runs.
  // Typing filters the loaded cards instantly; after a pause the same text goes
  // to the server, which looks the customer up with no billing-date window at
  // all — that's the only way to reach someone who expired months ago or whose
  // subscription is still running.
  useEffect(() => {
    const t = window.setTimeout(() => setNcSearchQ(needsChargeSearch.trim()), 400)
    return () => window.clearTimeout(t)
  }, [needsChargeSearch])

  // Pull lead activity for whoever is on screen once the list settles. Only
  // accounts we haven't fetched yet are requested, so re-renders cost nothing.
  useEffect(() => {
    const missing = needsCharge.map(a => a._id).filter(id => !vnRequested.current.has(id))
    if (missing.length) loadVnActivity(missing)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsCharge, loadVnActivity])

  // Ask for a comment on every client on screen, not only the ones arriving
  // without one: only the SERVER can tell whether a cached comment still matches
  // the client's current numbers, and a comment written before the numbers moved
  // is worse than none. Unchanged clients come straight back from the cache
  // without touching the model, so the extra ids cost nothing.
  useEffect(() => {
    if (loading) return
    const pending = needsCharge.map(a => a._id).filter(id => !aiRequested.current.has(id))
    if (!pending.length) return
    pending.forEach(id => aiRequested.current.add(id))
    runAiReview(pending)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsCharge, loading])

  const handleCharge = async (account: PaymentAccount, period: string, invoiceName: string, installments: number) => {
    const totalAmount = getTotalMonthly(account)
    const childCount = account.child_accounts?.filter((c: any) => c.monthly_amount > 0).length || 0
    const childInfo = childCount > 0 ? `\nכולל ${childCount} תתי-חשבונות` : ''
    const installmentsInfo = installments > 1 ? `\nתשלומים: ${installments}` : ''
    if (!window.confirm(`לחייב את ${account.first_name} ${account.last_name}\nשם לחשבונית: ${invoiceName}\nסכום: ₪${totalAmount.toLocaleString()}${childInfo}\nתקופה: ${PERIOD_LABELS[period] || period}${installmentsInfo}`)) return

    setCharging(account._id)
    setChargeModal(null)
    try {
      const res = await chargeAccount(account._id, {period, invoice_name: invoiceName, installments})
      setChargeResults(prev => ({
        ...prev,
        [account._id]: {
          success: res.data.success,
          message: res.data.success
            ? `חויב ₪${res.data.amount_charged} - מנוי עד ${new Date(res.data.new_trial_end).toLocaleDateString('he-IL')}`
            : (res.data.error || 'החיוב נכשל')
        }
      }))
      if (res.data.success) {
        if (res.data.new_trial_end) patchAccount(account._id, {trial_period_end: res.data.new_trial_end})
        markUpdated(account._id)
      }
    } catch (e: any) {
      setChargeResults(prev => ({
        ...prev,
        [account._id]: {success: false, message: e.response?.data?.error || 'שגיאה בחיוב'}
      }))
    }
    setCharging(null)
  }

  const handleSaveSettings = async (account: PaymentAccount, settings: any) => {
    try {
      await updatePaymentSettings(account._id, settings)
      setSettingsModal(null)
      patchAccount(account._id, settings)
    } catch (e) {
      console.error('Failed to save settings:', e)
    }
  }

  const handleQuickSaveAmount = async (accountId: string, newAmount: number) => {
    try {
      await updatePaymentSettings(accountId, {monthly_payment_amount: newAmount})
      setEditingAmount(null)
      patchAccount(accountId, {monthly_payment_amount: newAmount})
    } catch (e) {
      console.error('Failed to save amount:', e)
    }
  }

  const openHistory = async (account: PaymentAccount) => {
    setHistoryModal({accountId: account._id, name: `${account.first_name} ${account.last_name}`})
    setHistoryLoading(true)
    try {
      const res = await getPaymentHistory(account._id)
      setHistory(res.data.payments || [])
    } catch { setHistory([]) }
    setHistoryLoading(false)
  }

  const fmt = (n: number) => `₪${n.toLocaleString('he-IL', {minimumFractionDigits: 0, maximumFractionDigits: 0})}`

  const getLastToken = (tokens: any[]) => {
    if (!tokens?.length) return null
    // Find last non-null token (don't filter by expiry - let the payment provider decide)
    for (let i = tokens.length - 1; i >= 0; i--) {
      if (tokens[i]) return tokens[i]
    }
    return null
  }

  const getTokenType = (tokens: any[]) => {
    const t = getLastToken(tokens)
    if (!t) return null
    if (t.TranzilaTK) return 'Tranzila'
    if (t.Token || t.TokenInfo?.Token || t.token) return 'Cardcom'
    return 'Unknown'
  }

  const getTokenCardInfo = (tokens: any[]) => {
    // Get info from last token (even if expired) for display
    if (!tokens?.length) return null
    for (let i = tokens.length - 1; i >= 0; i--) {
      const t = tokens[i]
      if (!t) continue
      const info = t.TranzactionInfo || t.TokenInfo || t.UIValues || t
      const last4 = info.Last4CardDigitsString || info.Last4CardDigits || ''
      const month = info.CardMonth || t.TokenInfo?.CardMonth || t.UIValues?.CardMonth
      const year = info.CardYear || t.TokenInfo?.CardYear || t.UIValues?.CardYear
      const brand = info.Brand || info.CardName || ''
      return { last4: last4 ? String(last4) : '', month, year, brand }
    }
    return null
  }

  const getCardLast4 = (tokens: any[]) => {
    const t = getLastToken(tokens)
    if (!t) return ''
    const last4 = t.Last4 || t.last4 || t.TokenInfo?.Last4CardNumber || t.CardSuffix || ''
    return last4 ? `****${last4}` : ''
  }

  const getChildrenTotal = (account: PaymentAccount) => {
    if (!account.child_accounts?.length) return 0
    return account.child_accounts.reduce((sum: number, c: any) => sum + (c.monthly_amount || 0), 0)
  }

  const getTotalMonthly = (account: PaymentAccount) => {
    return account.monthly_payment_amount + getChildrenTotal(account)
  }

  const getDaysLeft = (trialEnd?: string) => {
    if (!trialEnd) return null
    const end = new Date(trialEnd)
    const now = new Date()
    return Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  }

  return (
    <div style={{padding: isMobile ? '12px 8px' : '24px', direction: 'rtl'}}>
      {/* Inline keyframes for loading bar + pulse */}
      <style>{`
        @keyframes payments-loading-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes payments-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .payments-refreshing { animation: payments-pulse 1.4s ease-in-out infinite; }
      `}</style>
      {/* Global loading bar at the top */}
      {loading && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, height: '3px', background: '#e8f4fd', zIndex: 2000, overflow: 'hidden'}}>
          <div style={{
            width: '40%', height: '100%', background: 'linear-gradient(90deg, transparent, #3699ff, transparent)',
            animation: 'payments-loading-bar 1.2s ease-in-out infinite'
          }} />
        </div>
      )}
      {/* Header */}
      <div style={{marginBottom: isMobile ? '16px' : '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap'}}>
        <div>
          <h2 style={{fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#1e1e2d', margin: 0, display: 'flex', alignItems: 'center', gap: '10px'}}>
            <i className="bi bi-credit-card-2-front" style={{color: '#3699ff'}}></i>
            <span>תשלומות</span>
            {loading && <span className="spinner-border spinner-border-sm" style={{color: '#3699ff', width: '16px', height: '16px'}} />}
          </h2>
          {!isMobile && <p style={{color: '#a1a5b7', fontSize: '13px', marginTop: '4px'}}>ניהול תשלומים, חיוב ומעקב הכנסות</p>}
        </div>
        <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
        <button
          onClick={() => setCalendarOpen(o => !o)}
          title="לוח שנה של משימות התשלום — גרור משימה כדי להזיז אותה"
          style={{
            padding: '8px 14px', borderRadius: '8px', border: 'none',
            background: calendarOpen ? '#1e1e2d' : '#3699ff', color: '#fff',
            fontSize: '12px', fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            boxShadow: '0 2px 8px rgba(54, 153, 255, 0.35)'
          }}
        >
          <i className="bi bi-calendar3"></i>
          לוח משימות
          <i className={`bi ${calendarOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{fontSize: '10px'}}></i>
        </button>
        <button
          onClick={() => setContactListOpen(o => !o)}
          title="רשימת הלקוחות שסומנו כדורשים שיחה לפני חיוב"
          style={{
            padding: '8px 14px', borderRadius: '8px', border: 'none',
            background: contactTasks.length > 0 ? '#ffa800' : '#a1a5b7', color: '#fff',
            fontSize: '12px', fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            boxShadow: contactTasks.length > 0 ? '0 2px 8px rgba(255, 168, 0, 0.35)' : 'none'
          }}
        >
          <i className="bi bi-telephone-fill"></i>
          לטיפול טלפוני{contactTasks.length > 0 ? ` (${contactTasks.length})` : ''}
          <i className={`bi ${contactListOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{fontSize: '10px'}}></i>
        </button>
        <button
          onClick={() => setPricingModal(true)}
          title="מחירי ברירת המחדל ונוסח ההודעה לקישורי תשלום (חודשי / שנתי)"
          style={{
            padding: '8px 14px', borderRadius: '8px', border: '1px solid #8950fc',
            background: '#fff', color: '#8950fc',
            fontSize: '12px', fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '6px'
          }}
        >
          <i className="bi bi-tags"></i>
          מחירי קישורי תשלום
        </button>
        <button
          onClick={() => setTemplatesModal(true)}
          title="נהל תבניות הודעות וואטסאפ (גרסת זכר / נקבה)"
          style={{
            padding: '8px 14px', borderRadius: '8px', border: 'none',
            background: '#25a35a', color: '#fff',
            fontSize: '12px', fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            boxShadow: '0 2px 8px rgba(37, 163, 90, 0.35)'
          }}
        >
          <i className="bi bi-whatsapp"></i>
          תבניות וואטסאפ
        </button>
        <button
          onClick={() => setBankDetailsModal(true)}
          title="ערוך את פרטי ההעברה הבנקאית שנשלחים ללקוחות — נשמרים לכל האדמינים"
          style={{
            padding: '8px 14px', borderRadius: '8px', border: 'none',
            background: '#159c5b', color: '#fff',
            fontSize: '12px', fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            boxShadow: '0 2px 8px rgba(21, 156, 91, 0.35)'
          }}
        >
          <i className="bi bi-bank"></i>
          פרטי העברה בנקאית
        </button>
        <button
          onClick={() => setAutoChargeModal(true)}
          title="חייב את כל החשבונות עם חיוב אוטומטי פעיל שהמנוי שלהם פג - עם תצוגה מקדימה של מי יחויב"
          style={{
            padding: '8px 14px', borderRadius: '8px', border: 'none',
            background: '#1bc5bd', color: '#fff',
            fontSize: '12px', fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            boxShadow: '0 2px 8px rgba(27, 197, 189, 0.35)'
          }}
        >
          <i className="bi bi-lightning-charge-fill"></i>
          הרץ חיוב אוטומטי
        </button>
        <button
          onClick={handleNormalizeEod}
          disabled={normalizingEod}
          title="תקן את כל תאריכי סיום התקופה לסוף היום (שעון ישראל) כדי שמנויים לא יפוגו באמצע היום"
          style={{
            padding: '8px 14px', borderRadius: '8px', border: '1px solid #3699ff',
            background: normalizingEod ? '#e8f4fd' : '#fff', color: '#3699ff',
            fontSize: '12px', fontWeight: 600, cursor: normalizingEod ? 'default' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '6px'
          }}
        >
          <i className="bi bi-clock-history"></i>
          {normalizingEod ? 'מעדכן...' : 'תקן סיום תקופה לסוף יום'}
        </button>
        </div>
      </div>

      {calendarOpen && (
        <PaymentsCalendar
          isMobile={isMobile}
          onClose={() => setCalendarOpen(false)}
          onTasksChanged={loadContactTasks}
          refreshKey={taskRev}
          onOpenAccount={(q) => {
            // The needs-charge search finds any customer regardless of billing date.
            setNeedsChargeSearch(q)
            window.setTimeout(() => document.getElementById('needs-charge-search')?.scrollIntoView({behavior: 'smooth', block: 'center'}), 50)
          }}
        />
      )}

      {tasksModal && (
        <AccountTasksModal
          account={tasksModal}
          onClose={() => setTasksModal(null)}
          onChanged={(open) => {
            const first = open[0]
            const soonest = [...open].sort((a, b) => (a.due_date ? 0 : 1) - (b.due_date ? 0 : 1) || String(a.due_date || '').localeCompare(String(b.due_date || '')) || String(a.due_time || '99').localeCompare(String(b.due_time || '99')))
            patchAccount(tasksModal._id, {
              open_tasks_count: open.length,
              open_tasks: soonest.slice(0, 5).map(t => ({_id: t._id, reason: t.reason, due_date: t.due_date, due_time: t.due_time, progress: t.progress, notes_count: t.notes_count})),
              contact_task: first ? {_id: first._id, reason: first.reason, created_at: first.created_at, notes_count: first.notes_count} : null,
            })
            loadContactTasks()
            bumpTasks()
          }}
        />
      )}

      {/* "Need to contact" working list — clients someone must call before we
          charge them. Opened from the header button. */}
      {contactListOpen && (
        <ContactListPanel
          tasks={contactTasks}
          loading={contactLoading}
          isMobile={isMobile}
          fmt={fmt}
          vnActivity={vnActivity}
          onClose={() => setContactListOpen(false)}
          onReload={loadContactTasks}
          onResolved={(task) => {
            setContactTasks(prev => prev.filter(t => t._id !== task._id))
            if (task.account?._id) patchAccount(task.account._id, (a: PaymentAccount) => {
              const rest = (a.open_tasks || []).filter(t => t._id !== task._id)
              return {...a, contact_task: null, open_tasks: rest, open_tasks_count: Math.max(0, (a.open_tasks_count || 1) - 1)}
            })
            bumpTasks()
          }}
          onTaskUpdated={(task) => {
            setContactTasks(prev => prev.map(t => t._id === task._id ? task : t))
            if (task.account?._id) {
              patchAccount(task.account._id, {contact_task: {_id: task._id, reason: task.reason, created_at: task.createdAt, notes_count: (task.notes || []).length}})
            }
          }}
          onOpenLinks={(account) => {
            // The task carries a populated account; the modal fetches prices and
            // the message preview itself, so this is all it needs.
            const known = needsCharge.find(a => a._id === account._id) || accounts.find(a => a._id === account._id)
            setLinksModal((known || account) as PaymentAccount)
          }}
        />
      )}

      {!isMobile && <>
      {/* Stats Cards */}
      {stats && (
        <div style={{display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: isMobile ? '8px' : '16px', marginBottom: isMobile ? '16px' : '24px'}}>
          <StatCard
            gradient="linear-gradient(135deg, #3699ff 0%, #1b84e7 100%)"
            shadow="rgba(54, 153, 255, 0.3)"
            label="MRR - חיוב אוטומטי"
            value={fmt(stats.total_auto_payment_mrr)}
            sub={`סה״כ משלמים: ${fmt(stats.total_mrr)}`}
          />
          <StatCard
            gradient="linear-gradient(135deg, #1bc5bd 0%, #0bb4ac 100%)"
            shadow="rgba(27, 197, 189, 0.3)"
            label="חיוב אוטומטי"
            value={String(stats.total_auto_payment)}
            sub={`${fmt(stats.total_auto_payment_mrr)} · מתוך ${stats.total_paying} משלמים`}
          />
          <StatCard
            gradient="linear-gradient(135deg, #f64e60 0%, #d9364d 100%)"
            shadow="rgba(246, 78, 96, 0.3)"
            label="ללא אמצעי תשלום"
            value={String(stats.total_without_token)}
            sub="דורשים טיפול"
          />
          <StatCard
            gradient="linear-gradient(135deg, #8950fc 0%, #7337ee 100%)"
            shadow="rgba(137, 80, 252, 0.3)"
            label="נגבה ב-30 יום"
            value={fmt(stats.recent_collected)}
            sub={`${stats.recent_count} חיובים`}
          />
        </div>
      )}

      {/* Upcoming Auto-Charges (next 3 days) - grouped by day */}
      {stats?.upcoming_charges && stats.upcoming_charges.length > 0 && (() => {
        const byDay: Record<string, {accounts: any[], total: number, date: Date}> = {}
        for (const a of stats.upcoming_charges) {
          const date = new Date(a.trial_period_end!)
          const key = date.toDateString()
          if (!byDay[key]) byDay[key] = {accounts: [], total: 0, date}
          const childrenAmt = (a.child_accounts || []).reduce((s: number, c: any) => s + (c.monthly_amount || 0), 0)
          byDay[key].accounts.push(a)
          byDay[key].total += a.monthly_payment_amount + childrenAmt
        }
        const days = Object.values(byDay).sort((a, b) => a.date.getTime() - b.date.getTime())
        const grandTotal = days.reduce((s, d) => s + d.total, 0)

        return (
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '20px', marginBottom: '24px',
            border: '1px solid #1bc5bd', boxShadow: '0 2px 12px rgba(27, 197, 189, 0.1)'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px'}}>
              <h4 style={{fontSize: '15px', fontWeight: 700, color: '#1bc5bd', margin: 0}}>
                <i className="bi bi-calendar-check" style={{marginLeft: '8px'}}></i>
                חיובים אוטומטיים ב-3 ימים הקרובים
              </h4>
              <span style={{fontSize: '13px', color: '#a1a5b7'}}>
                סה״כ: <strong style={{color: '#1bc5bd'}}>{fmt(grandTotal)}</strong> · {stats.upcoming_charges.length} חשבונות
              </span>
            </div>
            <div style={{display: 'flex', gap: '16px', flexWrap: 'wrap'}}>
              {days.map(day => {
                const isToday = day.date.toDateString() === new Date().toDateString()
                return (
                  <div key={day.date.toISOString()} style={{
                    flex: '1 1 180px', padding: '16px', borderRadius: '12px',
                    background: isToday ? '#e8fff3' : '#f5f8fa',
                    border: isToday ? '2px solid #1bc5bd' : '1px solid #e4e6ef',
                    textAlign: 'center'
                  }}>
                    <div style={{fontSize: '13px', fontWeight: 600, color: isToday ? '#1bc5bd' : '#7e8299', marginBottom: '4px'}}>
                      {isToday ? 'היום' : day.date.toLocaleDateString('he-IL', {weekday: 'short', day: 'numeric', month: 'numeric'})}
                    </div>
                    <div style={{fontSize: '26px', fontWeight: 700, color: '#1e1e2d'}}>{fmt(day.total)}</div>
                    <div style={{fontSize: '12px', color: '#a1a5b7', marginTop: '2px'}}>{day.accounts.length} חשבונות</div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}

      </>}

      {/* Charge Modal - pick period before charging */}
      {chargeModal && (() => {
        const totalAmount = getTotalMonthly(chargeModal)
        const activeChildren = (chargeModal.child_accounts || []).filter((c: any) => c.monthly_amount > 0)
        const period = chargeModal.payment_period || 'monthly'

        return (
          <Modal onClose={() => setChargeModal(null)} title={`חיוב - ${chargeModal.first_name} ${chargeModal.last_name}`}>
            <div style={{padding: '20px'}}>
              {/* Amount breakdown */}
              <div style={{marginBottom: '16px', padding: '14px', background: '#f5f8fa', borderRadius: '10px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#5e6278', marginBottom: '6px'}}>
                  <span>חיוב עצמי</span>
                  <strong>{fmt(chargeModal.monthly_payment_amount)}</strong>
                </div>
                {activeChildren.length > 0 && (
                  <>
                    {activeChildren.map((c: any, i: number) => (
                      <div key={i} style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#7e8299', marginBottom: '4px', paddingRight: '12px'}}>
                        <span>
                          <i className="bi bi-person" style={{marginLeft: '4px', fontSize: '11px'}}></i>
                          {c.account?.first_name || ''} {c.account?.last_name || ''} {c.label ? `(${c.label})` : ''}
                        </span>
                        <span>{fmt(c.monthly_amount)}</span>
                      </div>
                    ))}
                    <div style={{borderTop: '1px solid #e4e6ef', marginTop: '8px', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 700, color: '#1e1e2d'}}>
                      <span>סה״כ</span>
                      <span>{fmt(totalAmount)}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Invoice Name */}
              <div style={{marginBottom: '16px'}}>
                <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '8px', display: 'block'}}>שם לחשבונית:</label>
                <input
                  type="text"
                  value={chargeInvoiceName}
                  onChange={e => setChargeInvoiceName(e.target.value)}
                  style={{width: '100%', padding: '8px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '14px', direction: 'rtl'}}
                />
                {chargeInvoiceName !== chargeModal.company_name && chargeModal.company_name && (
                  <div style={{fontSize: '11px', color: '#ffa800', marginTop: '4px'}}>
                    שם חברה בחשבון: {chargeModal.company_name}
                  </div>
                )}
                {!chargeModal.company_name && (
                  <div style={{fontSize: '11px', color: '#a1a5b7', marginTop: '4px'}}>
                    אין שם חברה בחשבון - יעודכן לאחר חיוב
                  </div>
                )}
              </div>

              <div style={{marginBottom: '16px', padding: '10px 14px', background: '#f5f8fa', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '13px', color: '#5e6278'}}>תקופה</span>
                <span style={{fontSize: '14px', fontWeight: 700, color: '#1e1e2d'}}>{PERIOD_LABELS[period] || period}</span>
              </div>

              {/* Installments */}
              <div style={{marginBottom: '16px'}}>
                <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '8px', display: 'block'}}>תשלומים:</label>
                <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                  {[1, 2, 3, 4, 6, 8, 10, 12].map(n => (
                    <button key={n} onClick={() => setChargeInstallments(n)} style={{
                      padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                      border: chargeInstallments === n ? '2px solid #3699ff' : '1px solid #e4e6ef',
                      background: chargeInstallments === n ? '#e8f4fd' : '#fff',
                      color: chargeInstallments === n ? '#3699ff' : '#7e8299',
                      minWidth: '42px'
                    }}>
                      {n}
                    </button>
                  ))}
                </div>
                {chargeInstallments > 1 && (
                  <div style={{fontSize: '11px', color: '#7e8299', marginTop: '6px'}}>
                    {chargeInstallments} תשלומים של {fmt(Math.ceil(totalAmount / chargeInstallments))}
                  </div>
                )}
              </div>

              {/* Payment Method Info */}
              {(() => {
                const hasCard = !!getLastToken(chargeModal.payment_tokens)
                const cardInfo = getTokenCardInfo(chargeModal.payment_tokens)
                const tokenType = getTokenType(chargeModal.payment_tokens)
                return (
                  <div style={{marginBottom: '16px', padding: '12px 14px', background: hasCard ? '#e8fff3' : '#fff5f8', borderRadius: '10px', border: `1px solid ${hasCard ? '#1bc5bd' : '#f64e60'}`}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <i className={`bi ${hasCard ? 'bi-credit-card' : 'bi-exclamation-triangle'}`} style={{color: hasCard ? '#1bc5bd' : '#f64e60', fontSize: '16px'}}></i>
                        <div>
                          {hasCard ? (
                            <>
                              <div style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d'}}>{tokenType}</div>
                              {cardInfo && (
                                <div style={{fontSize: '11px', color: '#7e8299'}}>
                                  {cardInfo.last4 && `****${cardInfo.last4}`} {cardInfo.brand && `(${cardInfo.brand})`}
                                  {cardInfo.month && cardInfo.year && <span style={{marginRight: '4px'}}>{cardInfo.month}/{String(cardInfo.year).slice(-2)}</span>}
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{fontSize: '13px', fontWeight: 600, color: '#f64e60'}}>אין אמצעי תשלום</div>
                          )}
                        </div>
                      </div>
                      <div style={{display: 'flex', gap: '6px'}}>
                        <button
                          onClick={() => setRecoverModal(chargeModal)}
                          title="שחזר כרטיס שמור מ-Cardcom (אם לקוח שילם אך הטוקן לא נשמר)"
                          style={{
                            padding: '5px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                            background: '#fff', color: '#8950fc', border: '1px solid #8950fc'
                          }}
                        >
                          <i className="bi bi-arrow-counterclockwise" style={{marginLeft: '4px'}}></i>שחזר כרטיס
                        </button>
                        <button
                          onClick={() => handleAddPaymentMethod(chargeModal)}
                          disabled={paymentMethodLoading === chargeModal._id}
                          style={{
                            padding: '5px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                            background: hasCard ? '#fff' : '#f64e60', color: hasCard ? '#7e8299' : '#fff',
                            border: hasCard ? '1px solid #e4e6ef' : 'none'
                          }}
                        >
                          {paymentMethodLoading === chargeModal._id ? <span className="spinner-border spinner-border-sm" style={{width: '12px', height: '12px'}} /> :
                            <><i className="bi bi-credit-card-2-front" style={{marginLeft: '4px'}}></i>{hasCard ? 'החלף כרטיס' : 'הוסף כרטיס'}</>}
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })()}

              {activeChildren.length > 0 && (
                <div style={{marginBottom: '14px', padding: '10px 14px', background: '#e8f4fd', borderRadius: '8px', fontSize: '12px', color: '#3699ff'}}>
                  <i className="bi bi-info-circle" style={{marginLeft: '6px'}}></i>
                  חיוב יחיד עבור חשבון הורה + {activeChildren.length} תתי-חשבונות. כולם יקבלו הארכת מנוי.
                </div>
              )}

              <button
                onClick={() => handleCharge(chargeModal, period, chargeInvoiceName, chargeInstallments)}
                disabled={!getLastToken(chargeModal.payment_tokens)}
                style={{
                  width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                  background: getLastToken(chargeModal.payment_tokens) ? '#3699ff' : '#e4e6ef',
                  color: getLastToken(chargeModal.payment_tokens) ? '#fff' : '#a1a5b7',
                  fontSize: '15px', fontWeight: 700, cursor: getLastToken(chargeModal.payment_tokens) ? 'pointer' : 'default'
                }}
              >
                <i className="bi bi-lightning-charge" style={{marginLeft: '6px'}}></i>
                חייב {fmt(totalAmount)}
              </button>
              {!getLastToken(chargeModal.payment_tokens) && (
                <div style={{textAlign: 'center', fontSize: '11px', color: '#f64e60', marginTop: '6px'}}>יש להוסיף אמצעי תשלום לפני חיוב</div>
              )}

              {/* Or: create a payment link to send to the client */}
              <div style={{textAlign: 'center', fontSize: '11px', color: '#a1a5b7', margin: '12px 0 8px'}}>— או —</div>
              <button
                onClick={() => { setLinkModal(chargeModal); setLinkResult(null); setChargeModal(null) }}
                style={{
                  width: '100%', padding: '11px', borderRadius: '10px', border: '1px solid #8950fc',
                  background: '#fff', color: '#8950fc', fontSize: '14px', fontWeight: 700, cursor: 'pointer'
                }}
              >
                <i className="bi bi-link-45deg" style={{marginLeft: '6px'}}></i>
                צור קישור תשלום לשליחה ללקוח
              </button>
            </div>
          </Modal>
        )
      })()}

      {/* Payment Link Modal — create a Cardcom link to send to the client */}
      {linkModal && (
        <PaymentLinkModal
          account={linkModal}
          defaultAmount={getTotalMonthly(linkModal)}
          defaultPeriod={linkModal.payment_period || 'monthly'}
          defaultInvoiceName={linkModal.company_name || `${linkModal.first_name} ${linkModal.last_name}`}
          fmt={fmt}
          result={linkResult}
          onCreate={async (data) => {
            const res = await createPaymentLink(linkModal._id, data)
            if (res.data.success && res.data.url) {
              setLinkResult({url: res.data.url, amount: res.data.amount || data.amount, period_label: res.data.period_label, max_installments: res.data.max_installments})
            } else {
              alert(res.data.error || 'שגיאה ביצירת קישור התשלום')
            }
          }}
          onClose={() => { setLinkModal(null); setLinkResult(null) }}
        />
      )}

      {/* Needs Charge Panel. Also rendered while a search is active even with no
          results, so the box can't vanish out from under whoever is typing. */}
      {(needsCharge.length > 0 || loading || !!needsChargeSearch.trim()) && (() => {
        const searchLower = needsChargeSearch.trim().toLowerCase()
        // Once the server is doing the lookup, don't re-filter its answer: it
        // matches on fields (and full-name splits) this haystack doesn't, so a
        // second pass here would hide legitimate hits. Locked accounts stay
        // visible during a search too — you searched for that customer by name.
        const serverSearching = needsChargeIsSearch && ncSearchQ.length >= 2
        const lockedCount = needsCharge.filter(a => a.is_locked).length
        const preFiltered = (showLocked || serverSearching)
          ? needsCharge
          : needsCharge.filter(a => !a.is_locked)
        const filteredNeedsCharge = (searchLower && !serverSearching)
          ? preFiltered.filter(a => {
              const haystack = [
                a.first_name, a.last_name, `${a.first_name} ${a.last_name}`,
                a.email, a.phone, a.company_name
              ].filter(Boolean).map(String).join(' ').toLowerCase()
              return haystack.includes(searchLower)
            })
          : preFiltered
        const filteredChanged = filteredNeedsCharge.length !== needsCharge.length
        return (
        <div style={{
          background: '#fff', borderRadius: '12px', padding: isMobile ? '12px' : '20px', marginTop: isMobile ? '16px' : '24px',
          border: '2px solid #f64e60', boxShadow: '0 2px 12px rgba(246, 78, 96, 0.1)'
        }}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '6px'}}>
            <h4 style={{fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: '#f64e60', margin: 0, display: 'flex', alignItems: 'center', gap: '8px'}}>
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>דורשים חיוב ({loading ? '...' : (filteredChanged ? `${filteredNeedsCharge.length}/${needsCharge.length}` : needsCharge.length)})</span>
              {loading && <span className="spinner-border spinner-border-sm payments-refreshing" style={{color: '#f64e60', width: '14px', height: '14px'}} title="טוען..." />}
            </h4>
            {/* AI review — writes a comment per client, never charges or flags anyone. */}
            <div style={{display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap'}}>
              {aiError && (
                <span style={{fontSize: '11px', color: '#f64e60', maxWidth: '280px'}} title={aiError}>
                  <i className="bi bi-exclamation-circle" style={{marginLeft: '4px'}}></i>{aiError}
                </span>
              )}
              <button
                onClick={() => runAiReview(filteredNeedsCharge.map(a => a._id))}
                disabled={aiRunning || loading || filteredNeedsCharge.length === 0}
                title="הרץ ניתוח AI לכל הלקוחות המוצגים — כותב הערה בלבד, לא מבצע שום פעולה. לקוחות שנתוניהם לא השתנו מוחזרים מהמטמון."
                style={{
                  padding: '6px 12px', borderRadius: '8px', border: 'none',
                  background: aiRunning ? '#c9b6ff' : '#8950fc', color: '#fff',
                  fontSize: '11px', fontWeight: 700,
                  cursor: aiRunning || loading || filteredNeedsCharge.length === 0 ? 'default' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '6px'
                }}
              >
                <i className={aiRunning ? 'bi bi-hourglass-split' : 'bi bi-stars'}></i>
                {aiRunning ? 'מנתח...' : `נתח עם AI (${filteredNeedsCharge.length})`}
              </button>
              <button
                onClick={() => runAiReview(filteredNeedsCharge.map(a => a._id), true)}
                disabled={aiRunning || loading || filteredNeedsCharge.length === 0}
                title="הרץ ניתוח מחדש לכל המוצגים, גם אם הנתונים לא השתנו"
                style={{
                  padding: '6px 12px', borderRadius: '8px', border: '1px solid #8950fc',
                  background: '#fff', color: '#8950fc', fontSize: '11px', fontWeight: 700,
                  cursor: aiRunning || loading ? 'default' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '5px'
                }}
              >
                <i className="bi bi-arrow-clockwise"></i>
                נתח הכל מחדש
              </button>
              {vnLoading && (
                <span style={{fontSize: '10px', color: '#a1a5b7', display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                  <span className="spinner-border spinner-border-sm" style={{width: '10px', height: '10px'}} />
                  טוען נתוני מספר וירטואלי
                </span>
              )}
            </div>
          </div>
          {/* Search sits above the date filters, and outside them: it overrides
              them, so it must stay usable while they're dimmed. */}
          <div id="needs-charge-search" style={{position: 'relative', marginBottom: '10px'}}>
            <i className="bi bi-search" style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#a1a5b7', fontSize: '13px'}}></i>
            <input
              type="text"
              value={needsChargeSearch}
              onChange={e => setNeedsChargeSearch(e.target.value)}
              placeholder="חפש לקוח לפי שם, אימייל, טלפון או חברה — בכל החשבונות, ללא תלות בתאריך החיוב"
              style={{
                width: '100%', padding: '9px 34px 9px 32px', borderRadius: '8px', fontSize: '13px',
                outline: 'none', direction: 'rtl',
                border: serverSearching ? '2px solid #3699ff' : '1px solid #e4e6ef',
                background: '#fff',
              }}
            />
            {needsChargeSearch && (
              <button
                onClick={() => setNeedsChargeSearch('')}
                title="נקה"
                style={{position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#a1a5b7', cursor: 'pointer', fontSize: '14px', padding: 2}}
              >
                <i className="bi bi-x-circle-fill"></i>
              </button>
            )}
          </div>

          {/* A search bypasses every date bound, so say so — otherwise the filter
              chips below look like they're still narrowing the result. */}
          {serverSearching && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px',
              padding: '8px 12px', borderRadius: '8px',
              background: '#e8f4fd', border: '1px solid #b8ddfb', color: '#1b6fb8', fontSize: '12px'
            }}>
              <i className="bi bi-search"></i>
              <span>
                <strong>חיפוש בכל החשבונות</strong> — ללא הגבלת תאריך חיוב, כולל מנויים שעדיין בתוקף,
                מנויים שפגו מזמן וחשבונות מושבתים. סינוני התאריך למטה אינם חלים כרגע.
              </span>
              <button
                onClick={() => setNeedsChargeSearch('')}
                style={{marginInlineStart: 'auto', border: 'none', background: 'transparent', color: '#1b6fb8', cursor: 'pointer', fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap'}}
              >נקה חיפוש</button>
            </div>
          )}
          {/* Trial-end cutoff filter */}
          <div style={{
            display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '14px',
            padding: '10px 12px', background: '#fff5f8', borderRadius: '8px', border: '1px dashed #f5c2c7',
            opacity: serverSearching ? 0.5 : 1
          }}>
            <span style={{fontSize: '12px', fontWeight: 600, color: '#f64e60'}}>
              <i className="bi bi-funnel" style={{marginLeft: '4px'}}></i>
              הצג חשבונות שפג תוקפם מתאריך:
            </span>
            {[
              {days: 3, label: '3 ימים (ברירת מחדל)'},
              {days: 7, label: '7 ימים'},
              {days: 14, label: '14 ימים'},
              {days: 30, label: '30 ימים'}
            ].map(opt => {
              const target = new Date()
              target.setDate(target.getDate() - opt.days)
              const targetStr = target.toISOString().split('T')[0]
              const active = needsChargeFrom === targetStr
              return (
                <button
                  key={opt.days}
                  onClick={() => setNeedsChargeFrom(targetStr)}
                  style={{
                    padding: '5px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                    border: active ? '2px solid #f64e60' : '1px solid #e4e6ef',
                    background: active ? '#fff' : '#fff',
                    color: active ? '#f64e60' : '#7e8299',
                    cursor: 'pointer'
                  }}
                >{opt.label}</button>
              )
            })}
            <label style={{fontSize: '12px', color: '#7e8299', display: 'inline-flex', alignItems: 'center', gap: '6px'}}>
              <span>מתאריך:</span>
              <input
                type="date"
                value={needsChargeFrom}
                max={new Date().toISOString().split('T')[0]}
                onChange={e => setNeedsChargeFrom(e.target.value)}
                style={{padding: '5px 8px', border: '1px solid #e4e6ef', borderRadius: '6px', fontSize: '12px'}}
              />
            </label>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
              padding: '5px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
              border: showLocked ? '1px solid #7e8299' : '1px solid #e4e6ef',
              background: showLocked ? '#f1f1f2' : '#fff',
              color: showLocked ? '#5e6278' : '#a1a5b7'
            }}>
              <input
                type="checkbox"
                checked={showLocked}
                onChange={e => setShowLocked(e.target.checked)}
                style={{width: '14px', height: '14px', cursor: 'pointer'}}
              />
              <span>הצג מושבתים{lockedCount > 0 ? ` (${lockedCount})` : ''}</span>
            </label>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
              padding: '5px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
              border: includeActive ? '1px solid #1bc5bd' : '1px solid #e4e6ef',
              background: includeActive ? '#e8fff3' : '#fff',
              color: includeActive ? '#1bc5bd' : '#a1a5b7'
            }}>
              <input
                type="checkbox"
                checked={includeActive}
                onChange={e => setIncludeActive(e.target.checked)}
                style={{width: '14px', height: '14px', cursor: 'pointer'}}
              />
              <span>הצג גם פעילים (מנוי בתוקף)</span>
            </label>
          </div>
          {/* Unified card layout (mobile + desktop) */}
          {loading ? (
            <div style={{padding: '60px 30px', textAlign: 'center', color: '#a1a5b7'}}>
              <div className="spinner-border" style={{color: '#f64e60', width: '40px', height: '40px', marginBottom: '12px'}} role="status">
                <span className="visually-hidden">טוען...</span>
              </div>
              <div style={{fontSize: '14px', fontWeight: 600, color: '#5e6278'}}>טוען חשבונות...</div>
            </div>
          ) : filteredNeedsCharge.length === 0 ? (
            <div style={{padding: '30px', textAlign: 'center', color: '#a1a5b7', fontSize: '13px'}}>
              <i className="bi bi-search" style={{fontSize: '24px', display: 'block', marginBottom: '6px'}}></i>
              {!needsChargeSearch
                ? 'אין חשבונות להצגה'
                : serverSearching
                  ? `לא נמצא אף חשבון בשם "${needsChargeSearch}" — החיפוש נעשה על כל החשבונות, ללא הגבלת תאריך`
                  : `מקליד... (לפחות 2 תווים כדי לחפש בכל החשבונות)`}
            </div>
          ) : (
          <div style={{display: 'grid', gap: '12px', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(520px, 1fr))'}}>
            {filteredNeedsCharge.map(account => {
                const hasToken = !!getLastToken(account.payment_tokens)
                // Positive = days since expiry (past); Negative = days until expiry (future)
                const daysExpired = account.trial_period_end
                  ? Math.ceil((new Date().getTime() - new Date(account.trial_period_end).getTime()) / (1000 * 60 * 60 * 24))
                  : 0
                const result = chargeResults[account._id]
                const profiles = (account as any).profiles || []
                const pstats = (account as any).profile_stats || {}
                const lastPayments = (account as any).last_payments || []
                const isJustUpdated = !!recentlyUpdated[account._id]
                return (
                  <div key={account._id} style={{
                    padding: '12px', borderRadius: '10px',
                    border: isJustUpdated ? '2px solid #1bc5bd' : '1px solid #fdd',
                    background: isJustUpdated ? '#e8fdfa' : '#fffbfb',
                    opacity: account.is_locked ? 0.4 : 1,
                    transition: 'background 0.3s ease, border-color 0.3s ease',
                    position: 'relative'
                  }}>
                    {isJustUpdated && (
                      <div style={{
                        position: 'absolute', top: '-9px', right: '12px',
                        background: '#1bc5bd', color: '#fff',
                        padding: '2px 10px', borderRadius: '999px',
                        fontSize: '10px', fontWeight: 700, letterSpacing: '0.02em',
                        boxShadow: '0 2px 6px rgba(27, 197, 189, 0.35)'
                      }}>
                        עודכן
                      </div>
                    )}
                    <NeedsChargeCard
                      account={account}
                      profiles={profiles}
                      pstats={pstats}
                      lastPayments={lastPayments}
                      daysExpired={daysExpired}
                      hasToken={hasToken}
                      result={result}
                      charging={charging === account._id}
                      paymentMethodLoading={paymentMethodLoading === account._id}
                      loginAsLoading={loginAsLoading === account._id}
                      editingAmount={editingAmount === account._id}
                      editAmountValue={editAmountValue}
                      fmt={fmt}
                      getTotalMonthly={getTotalMonthly}
                      getChildrenTotal={getChildrenTotal}
                      getTokenType={getTokenType}
                      getTokenCardInfo={getTokenCardInfo}
                      onStartEditAmount={() => { setEditingAmount(account._id); setEditAmountValue(account.monthly_payment_amount) }}
                      onChangeEditAmount={setEditAmountValue}
                      onSaveEditAmount={() => handleQuickSaveAmount(account._id, editAmountValue)}
                      onCancelEditAmount={() => setEditingAmount(null)}
                      onOpenCharge={() => { setChargeModal(account); setChargePeriod(account.payment_period || 'monthly'); setChargeInvoiceName(account.company_name || (account as any).last_invoices?.[0]?.Cust_Name || `${account.first_name} ${account.last_name}`); setChargeInstallments(1) }}
                      onPaymentLink={() => { setLinkModal(account); setLinkResult(null) }}
                      onAddPaymentMethod={() => handleAddPaymentMethod(account)}
                      onRecover={() => setRecoverModal(account)}
                      onOpenCards={() => setCardsModal(account)}
                      onOpenSettings={() => setSettingsModal(account)}
                      onOpenHistory={() => openHistory(account)}
                      onSetGender={(gender) => handleSetGender(account._id, gender)}
                      onOpenWhatsapp={() => setWaModal(account)}
                      onOpenIssue={() => setIssueModal(account)}
                      onOpenMeeting={() => setMeetingModal(account)}
                      onOpenManualPayment={(method) => setManualPayModal({account, method})}
                      bankDetails={bankDetails}
                      onOpenNote={() => { setNoteModal(account); setNoteText(''); setNoteTicketDept('') }}
                      vn={vnActivity[account._id] || null}
                      aiBusy={aiSingle === account._id || aiRunning}
                      onRunAi={() => runAiReview([account._id], true)}
                      contactFlagBusy={flagging === account._id}
                      onToggleContactFlag={() => handleToggleContactFlag(account)}
                      onOpenTasks={() => setTasksModal(account)}
                      onOpenPaymentLinks={() => setLinksModal(account)}
                      onOpenTrialEdit={() => setTrialEditModal(account)}
                      onLoginAs={() => handleLoginAs(account)}
                      onLock={() => { setLockModal(account); setLockReason('') }}
                      onUnlock={() => handleUnlockAccount(account)}
                      editingNoteId={editingNoteId}
                      editingNoteText={editingNoteText}
                      onStartEditNote={(noteId: string, current: string) => { setEditingNoteId(noteId); setEditingNoteText(current) }}
                      onChangeEditNote={setEditingNoteText}
                      onSaveEditNote={handleSaveNoteEdit}
                      onCancelEditNote={() => { setEditingNoteId(null); setEditingNoteText('') }}
                      onDeleteNote={handleDeleteNote}
                      onToggleAutoPayment={async () => {
                        const next = !account.disable_auto_payment
                        patchAccount(account._id, {disable_auto_payment: next})
                        try {
                          await updatePaymentSettings(account._id, {disable_auto_payment: next})
                        } catch (e) {
                          console.error(e)
                          patchAccount(account._id, {disable_auto_payment: !next})
                          alert('שגיאה בעדכון חיוב אוטומטי')
                        }
                      }}
                    />
                  </div>
                )
              })}
          </div>
          )}
        </div>
        )
      })()}

      {/* Auto-Charge Modal — bill everyone with auto payment on */}
      {autoChargeModal && (
        <AutoChargeModal
          onClose={(didCharge) => {
            setAutoChargeModal(false)
            if (didCharge) silentRefresh()
          }}
          onCharged={(accountId, newTrialEnd) => {
            if (newTrialEnd) patchAccount(accountId, {trial_period_end: newTrialEnd})
            markUpdated(accountId)
          }}
        />
      )}

      {/* Settings Modal */}
      {settingsModal && (
        <SettingsModalContent
          account={settingsModal}
          onClose={() => setSettingsModal(null)}
          onSave={(settings) => handleSaveSettings(settingsModal, settings)}
        />
      )}

      {/* Trial End Edit Modal */}
      {/* Note Modal */}
      {noteModal && (
        <Modal onClose={() => setNoteModal(null)} title={`הערות תשלום - ${noteModal.first_name} ${noteModal.last_name}`}>
          <div style={{padding: '20px'}}>
            {/* Existing notes */}
            {(noteModal as any).payment_notes?.length > 0 && (
              <div style={{marginBottom: '16px', maxHeight: '200px', overflowY: 'auto'}}>
                {(noteModal as any).payment_notes.map((note: any) => (
                  <div key={note._id} style={{padding: '8px 12px', background: '#f9fafb', borderRadius: '8px', marginBottom: '6px', fontSize: '12px', direction: 'rtl'}}>
                    <div style={{color: '#181c32'}}>{note.description}</div>
                    <div style={{color: '#a1a5b7', fontSize: '10px', marginTop: '4px'}}>
                      {new Date(note.created_at).toLocaleDateString('he-IL')} {new Date(note.created_at).toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit'})}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* New note input */}
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="כתוב הערה חדשה..."
              rows={3}
              style={{width: '100%', padding: '10px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '13px', outline: 'none', resize: 'vertical', direction: 'rtl'}}
            />
            <div style={{marginTop: '12px'}}>
              <div style={{fontSize: '12px', fontWeight: 600, color: '#5e6278', marginBottom: '6px'}}>שלח כמשימה:</div>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px'}}>
                {[
                  {key: '', label: 'ללא משימה', icon: 'bi-sticky', color: '#7e8299', bg: '#f1f1f2', border: '#e4e6ef'},
                  {key: 'customer_service', label: 'שירות לקוחות', icon: 'bi-headset', color: '#3699ff', bg: '#e8f4fd', border: '#3699ff'},
                  {key: 'development', label: 'פיתוח', icon: 'bi-code-slash', color: '#8950fc', bg: '#f1e7fe', border: '#8950fc'}
                ].map(opt => {
                  const active = noteTicketDept === opt.key
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setNoteTicketDept(opt.key as any)}
                      style={{
                        padding: '10px 8px', borderRadius: '8px', cursor: 'pointer',
                        border: active ? `2px solid ${opt.border}` : '1px solid #e4e6ef',
                        background: active ? opt.bg : '#fff',
                        color: active ? opt.color : '#7e8299',
                        fontWeight: 600, fontSize: '12px',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'
                      }}
                    >
                      <i className={`bi ${opt.icon}`} style={{fontSize: '16px'}}></i>
                      <span>{opt.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>
            <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '14px'}}>
              <button
                onClick={async () => {
                  if (!noteText.trim()) return
                  setNoteSaving(true)
                  try {
                    const res = await addPaymentNote(noteModal._id, noteText.trim(), noteTicketDept ? {create_ticket: true, ticket_department: noteTicketDept} : undefined)
                    const created = res?.data?.note
                    if (created) {
                      patchAccount(noteModal._id, (a) => ({
                        ...a,
                        payment_notes: [created, ...(((a as any).payment_notes as any[]) || [])],
                      } as PaymentAccount))
                    }
                    setNoteModal(null)
                    setNoteText('')
                    setNoteTicketDept('')
                  } catch (e) {
                    console.error('Failed to save note:', e)
                  }
                  setNoteSaving(false)
                }}
                disabled={noteSaving || !noteText.trim()}
                style={{
                  padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, border: 'none',
                  background: noteText.trim() ? (noteTicketDept === 'development' ? '#8950fc' : '#3699ff') : '#e4e6ef',
                  color: noteText.trim() ? '#fff' : '#a1a5b7', cursor: noteText.trim() ? 'pointer' : 'default'
                }}
              >
                {noteSaving ? <span className="spinner-border spinner-border-sm" /> :
                  noteTicketDept === 'customer_service' ? <><i className="bi bi-send" style={{marginLeft: '6px'}}></i>שמור ושלח לשירות לקוחות</> :
                  noteTicketDept === 'development' ? <><i className="bi bi-send" style={{marginLeft: '6px'}}></i>שמור ושלח לפיתוח</> :
                  'שמור הערה'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {trialEditModal && (
        <TrialEditModalContent
          account={trialEditModal}
          onClose={() => setTrialEditModal(null)}
          onSaved={(newDateIso) => {
            patchAccount(trialEditModal._id, {trial_period_end: newDateIso})
            markUpdated(trialEditModal._id)
            setTrialEditModal(null)
          }}
        />
      )}

      {/* Lock Account Modal — warning + required reason */}
      {lockModal && (
        <Modal onClose={() => { if (!lockSaving) { setLockModal(null); setLockReason('') } }} title={`השבתת חשבון - ${lockModal.first_name} ${lockModal.last_name}`}>
          <div style={{padding: '20px'}}>
            <div style={{
              background: '#fff5f8', border: '1px solid #f64e60', borderRadius: '8px', padding: '12px 14px',
              marginBottom: '16px', color: '#f64e60', fontSize: '13px', lineHeight: 1.5
            }}>
              <div style={{fontWeight: 700, fontSize: '14px', marginBottom: '6px'}}>
                <i className="bi bi-exclamation-triangle-fill" style={{marginLeft: '6px'}}></i>
                אזהרה: פעולה זו תשבית את החשבון
              </div>
              <div>
                הלקוח לא יוכל להיכנס, לקמפיינים לא יתווספו פוסטים חדשים,
                והחשבון יסומן כ״חסום״. הפעולה תתועד ב-ClientLog עם הסיבה שתציין.
              </div>
            </div>
            <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '8px', display: 'block'}}>
              סיבה להשבתה <span style={{color: '#f64e60'}}>*</span>
            </label>
            <textarea
              value={lockReason}
              onChange={e => setLockReason(e.target.value)}
              placeholder="לדוגמה: אי-תשלום, בקשת הלקוח, שימוש לרעה..."
              rows={3}
              autoFocus
              style={{width: '100%', padding: '10px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '13px', resize: 'vertical', direction: 'rtl'}}
            />
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px'}}>
              <button
                onClick={() => { if (!lockSaving) { setLockModal(null); setLockReason('') } }}
                disabled={lockSaving}
                style={{padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, border: '1px solid #e4e6ef', background: '#fff', color: '#5e6278', cursor: 'pointer'}}
              >
                ביטול
              </button>
              <button
                onClick={handleLockAccount}
                disabled={lockSaving || !lockReason.trim()}
                style={{
                  padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, border: 'none',
                  background: lockReason.trim() ? '#f64e60' : '#e4e6ef',
                  color: lockReason.trim() ? '#fff' : '#a1a5b7',
                  cursor: lockReason.trim() ? 'pointer' : 'default'
                }}
              >
                {lockSaving ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-lock-fill" style={{marginLeft: '6px'}}></i>השבת חשבון</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* History Modal */}
      {historyModal && (
        <Modal onClose={() => setHistoryModal(null)} title={`היסטוריית תשלומים - ${historyModal.name}`}>
          <div style={{padding: '20px', maxHeight: '400px', overflowY: 'auto'}}>
            {historyLoading ? (
              <div style={{textAlign: 'center', padding: '40px'}}><div className="spinner-border text-primary" /></div>
            ) : history.length === 0 ? (
              <div style={{textAlign: 'center', padding: '40px', color: '#a1a5b7'}}>
                <i className="bi bi-inbox" style={{fontSize: '32px', display: 'block', marginBottom: '8px'}}></i>
                אין היסטוריית תשלומים
              </div>
            ) : (
              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                <thead>
                  <tr style={{borderBottom: '2px solid #e4e6ef'}}>
                    {['תאריך', 'סוג', 'סכום', 'סטטוס', 'אמצעי'].map(h => (
                      <th key={h} style={{padding: '10px', textAlign: 'right', color: '#5e6278'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map((p: any) => (
                    <tr key={p._id} style={{borderBottom: '1px solid #f1f1f2'}}>
                      <td style={{padding: '10px'}}>{new Date(p.created_at).toLocaleDateString('he-IL')}</td>
                      <td style={{padding: '10px'}}>
                        {({initial: 'ראשוני', recurring: 'חוזר', prorated: 'יחסי', refund: 'החזר'} as any)[p.payment_type] || p.payment_type}
                      </td>
                      <td style={{padding: '10px', fontWeight: 600}}>{fmt(p.amount)}</td>
                      <td style={{padding: '10px'}}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                          background: p.status === 'completed' ? '#e8fff3' : p.status === 'failed' ? '#fff5f8' : '#fff8dd',
                          color: p.status === 'completed' ? '#50cd89' : p.status === 'failed' ? '#f64e60' : '#ffc107'
                        }}>
                          {({completed: 'הושלם', failed: 'נכשל', pending: 'ממתין', refunded: 'הוחזר'} as any)[p.status] || p.status}
                        </span>
                      </td>
                      <td style={{padding: '10px', fontSize: '11px', color: '#a1a5b7'}}>{p.payment_method}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Modal>
      )}

      {/* Add Payment Method Modal (Cardcom iframe) */}
      {paymentMethodModal && (
        <Modal onClose={() => {
                  const id = paymentMethodModal?.account._id
                  setPaymentMethodModal(null); setChargeModal(null)
                  silentRefresh()
                  if (id) markUpdated(id)
                }} title={`הוספת אמצעי תשלום - ${paymentMethodModal.account.first_name} ${paymentMethodModal.account.last_name}`}>
          <div style={{padding: '0'}}>
            <div style={{padding: '12px 20px', background: '#e8f4fd', fontSize: '12px', color: '#3699ff', display: 'flex', alignItems: 'center', gap: '8px'}}>
              <i className="bi bi-info-circle"></i>
              הלקוח צריך להזין את פרטי הכרטיס בטופס. לאחר סיום לחץ על ״סגור ורענן״.
            </div>
            <iframe
              src={paymentMethodModal.url}
              style={{width: '100%', height: '400px', border: 'none'}}
              title="הוספת אמצעי תשלום"
            />
            <div style={{padding: '12px 20px', borderTop: '1px solid #e4e6ef', display: 'flex', justifyContent: 'flex-end'}}>
              <button
                onClick={() => {
                  const id = paymentMethodModal?.account._id
                  setPaymentMethodModal(null); setChargeModal(null)
                  silentRefresh()
                  if (id) markUpdated(id)
                }}
                style={{
                  padding: '8px 24px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, border: 'none',
                  background: '#3699ff', color: '#fff', cursor: 'pointer'
                }}
              >
                <i className="bi bi-arrow-clockwise" style={{marginLeft: '6px'}}></i>
                סגור ורענן
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Recover Card Modal — pull a saved token from Cardcom and attach it */}
      {recoverModal && (
        <RecoverCardModal
          account={recoverModal}
          fmt={fmt}
          onClose={() => setRecoverModal(null)}
          onAdded={(payment_tokens) => {
            const id = recoverModal._id
            patchAccount(id, a => ({...a, payment_tokens}))
            markUpdated(id)
            silentRefresh()
            setRecoverModal(null)
          }}
        />
      )}

      {/* Saved cards popup — history of every card, put an old one back as active */}
      {cardsModal && (
        <SavedCardsModal
          account={cardsModal}
          onClose={() => setCardsModal(null)}
          onChanged={(payment_tokens) => {
            const id = cardsModal._id
            patchAccount(id, a => ({...a, payment_tokens}))
            setCardsModal(prev => (prev && prev._id === id ? {...prev, payment_tokens} as PaymentAccount : prev))
            markUpdated(id)
            silentRefresh()
          }}
        />
      )}

      {/* WhatsApp send modal (per client) */}
      {waModal && (
        <WhatsappSendModal
          account={waModal}
          onClose={() => setWaModal(null)}
          onManageTemplates={() => { setWaModal(null); setTemplatesModal(true) }}
        />
      )}

      {/* Open a real issue (תקלה) for this customer */}
      {issueModal && (
        <OpenIssueModal
          account={issueModal}
          onClose={() => setIssueModal(null)}
        />
      )}

      {/* Book an onboarding/follow-up meeting straight from the payments row */}
      {meetingModal && (
        <ScheduleMeetingModal
          account={meetingModal}
          onClose={() => setMeetingModal(null)}
        />
      )}

      {/* Manual (bank transfer) payment */}
      {manualPayModal && (
        <ManualPaymentModal
          account={manualPayModal.account}
          method={manualPayModal.method}
          fmt={fmt}
          onClose={() => setManualPayModal(null)}
          onSaved={(newTrialEnd) => {
            patchAccount(manualPayModal.account._id, {trial_period_end: newTrialEnd})
            markUpdated(manualPayModal.account._id)
            setManualPayModal(null)
            silentRefresh()
          }}
        />
      )}

      {/* Bank transfer details — one shared block of text, editable here */}
      {bankDetailsModal && (
        <BankDetailsModal
          details={bankDetails}
          onSaved={(text) => setBankDetails(text)}
          onClose={() => setBankDetailsModal(false)}
        />
      )}

      {/* WhatsApp templates manager */}
      {templatesModal && (
        <WhatsappTemplatesModal onClose={() => setTemplatesModal(false)} />
      )}

      {/* Never-expiring payment links (monthly + yearly) over WhatsApp */}
      {linksModal && (
        <PaymentLinksModal
          account={linksModal}
          fmt={fmt}
          onClose={() => setLinksModal(null)}
          onSent={(waMessage) => {
            patchAccount(linksModal._id, (a) => ({...a, wa_messages: [waMessage, ...(a.wa_messages || [])]}))
            markUpdated(linksModal._id)
          }}
        />
      )}

      {/* Global price defaults + message text for those links */}
      {pricingModal && (
        <PricingSettingsModal onClose={() => setPricingModal(false)} />
      )}
    </div>
  )
}

// ============================================
// Sub-components
// ============================================

const StatCard: FC<{gradient: string; shadow: string; label: string; value: string; sub: string}> = ({gradient, shadow, label, value, sub}) => (
  <div style={{background: gradient, borderRadius: '12px', padding: '20px', color: '#fff', boxShadow: `0 4px 20px ${shadow}`}}>
    <div style={{fontSize: '12px', opacity: 0.8, marginBottom: '4px'}}>{label}</div>
    <div style={{fontSize: '28px', fontWeight: 700}}>{value}</div>
    <div style={{fontSize: '11px', opacity: 0.7, marginTop: '4px'}}>{sub}</div>
  </div>
)

const Modal: FC<{onClose: () => void; title: string; children: React.ReactNode}> = ({onClose, title, children}) => {
  const isSmall = window.innerWidth <= 768
  return (
  <>
    <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1040}} onClick={onClose} />
    <div style={{
      position: 'fixed',
      ...(isSmall
        ? {bottom: 0, left: 0, right: 0, top: 'auto', transform: 'none', borderRadius: '16px 16px 0 0', maxHeight: '90vh'}
        : {top: '50%', left: '50%', transform: 'translate(-50%, -50%)', borderRadius: '16px', width: '560px', maxWidth: '90vw', maxHeight: '80vh'}),
      background: '#fff', overflow: 'auto', zIndex: 1050,
      boxShadow: '0 20px 60px rgba(0,0,0,0.2)', direction: 'rtl'
    }}>
      <div style={{padding: '16px 24px', borderBottom: '1px solid #e4e6ef', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <h4 style={{margin: 0, fontSize: '16px', fontWeight: 700}}>{title}</h4>
        <button onClick={onClose} style={{border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#a1a5b7'}}>
          <i className="bi bi-x-lg"></i>
        </button>
      </div>
      {children}
    </div>
  </>
  )
}

// Hebrew labels + styling for a detected invoice payment method.
const PAYMENT_METHOD_META: Record<PaymentMethod, {label: string; icon: string; color: string}> = {
  credit_card: {label: 'כרטיס אשראי', icon: 'bi-credit-card', color: '#1bc5bd'},
  cheque: {label: "צ'ק", icon: 'bi-card-text', color: '#f1bc00'},
  cash: {label: 'מזומן', icon: 'bi-cash', color: '#f1bc00'},
  bank_transfer: {label: 'העברה בנקאית / אחר', icon: 'bi-bank', color: '#f1bc00'},
  unknown: {label: 'לא ידוע', icon: 'bi-question-circle', color: '#a1a5b7'},
}
const methodMeta = (m: PaymentMethod) => PAYMENT_METHOD_META[m] || PAYMENT_METHOD_META.unknown

// Recover a saved card token from Cardcom's reporting API and attach it to the
// account. For customers who paid but whose token was never stored (e.g. a
// webhook mismatch). The reporting API is terminal-wide, so the admin picks the
// matching transaction by amount / last-4 / date.
const RecoverCardModal: FC<{
  account: PaymentAccount
  fmt: (n: number) => string
  onClose: () => void
  onAdded: (payment_tokens: any[]) => void
}> = ({account, fmt, onClose, onAdded}) => {
  const isoDay = (d: Date) => d.toISOString().slice(0, 10)
  const defaultInvoiceName = account.company_name || (account as any).last_invoices?.[0]?.Cust_Name || `${account.first_name} ${account.last_name}`.trim()
  const [name, setName] = useState(defaultInvoiceName)
  const [fromDate, setFromDate] = useState(isoDay(new Date(Date.now() - 120 * 86400000)))
  const [toDate, setToDate] = useState(isoDay(new Date()))
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [candidates, setCandidates] = useState<CardcomTokenCandidate[]>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsInfo | null>(null)
  const [addingId, setAddingId] = useState<number | null>(null)

  const search = async () => {
    setLoading(true); setError(null); setSearched(false)
    try {
      const res = await getCardcomTokenCandidates(account._id, {fromDate, toDate, name: name.trim() || undefined})
      if (res.data.success) {
        setCandidates(res.data.candidates || [])
        setPaymentMethods(res.data.payment_methods || null)
        setSearched(true)
      } else {
        setError(res.data.error || 'שגיאה בשליפת עסקאות')
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'שגיאה בשליפת עסקאות')
    }
    setLoading(false)
  }

  const add = async (c: CardcomTokenCandidate) => {
    setAddingId(c.TranzactionId); setError(null)
    try {
      const res = await addCardcomToken(account._id, c)
      if (res.data.success) {
        onAdded(res.data.payment_tokens || [])
      } else {
        setError(res.data.error || 'שגיאה בהוספת הכרטיס')
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'שגיאה בהוספת הכרטיס')
    }
    setAddingId(null)
  }

  return (
    <Modal onClose={onClose} title={`שחזור כרטיס מ-Cardcom - ${account.first_name} ${account.last_name}`}>
      <div style={{padding: '16px 20px'}}>
        <div style={{padding: '10px 12px', background: '#f5f8fa', borderRadius: '8px', fontSize: '12px', color: '#7e8299', marginBottom: '14px'}}>
          <i className="bi bi-info-circle" style={{marginLeft: '6px'}}></i>
          חיפוש עסקאות מהמסוף לפי השם בחשבונית וטווח התאריכים. בחר/י את העסקה של הלקוח והוסף/י את הכרטיס השמור.
          {account.monthly_payment_amount ? <span> סכום חודשי: <b>{fmt(account.monthly_payment_amount)}</b></span> : null}
        </div>

        <div style={{marginBottom: '10px'}}>
          <label style={{fontSize: '11px', color: '#7e8299', display: 'block', marginBottom: '4px'}}>שם בחשבונית</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="השם כפי שמופיע בחשבונית"
            onKeyDown={e => { if (e.key === 'Enter' && !loading) search() }}
            style={{width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #e4e6ef', fontSize: '13px'}} />
        </div>

        <div style={{display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '14px'}}>
          <div style={{flex: 1}}>
            <label style={{fontSize: '11px', color: '#7e8299', display: 'block', marginBottom: '4px'}}>מתאריך</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              style={{width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #e4e6ef', fontSize: '13px'}} />
          </div>
          <div style={{flex: 1}}>
            <label style={{fontSize: '11px', color: '#7e8299', display: 'block', marginBottom: '4px'}}>עד תאריך</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #e4e6ef', fontSize: '13px'}} />
          </div>
          <button onClick={search} disabled={loading}
            style={{padding: '9px 18px', borderRadius: '8px', border: 'none', background: '#3699ff', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: loading ? 'default' : 'pointer'}}>
            {loading ? <span className="spinner-border spinner-border-sm" style={{width: '13px', height: '13px'}} /> : <><i className="bi bi-search" style={{marginLeft: '6px'}}></i>חפש</>}
          </button>
        </div>

        {error && (
          <div style={{padding: '10px 12px', background: '#fff5f8', border: '1px solid #f64e60', borderRadius: '8px', fontSize: '12px', color: '#f64e60', marginBottom: '12px'}}>{error}</div>
        )}

        {searched && paymentMethods?.non_cc_only && (
          <div style={{padding: '11px 13px', background: '#fff8dd', border: '1px solid #f1bc00', borderRadius: '8px', fontSize: '12.5px', color: '#7e6a00', marginBottom: '12px', lineHeight: 1.5}}>
            <i className="bi bi-exclamation-triangle-fill" style={{marginLeft: '6px', color: '#f1bc00'}}></i>
            <b>לקוח זה אינו משלם בכרטיס אשראי.</b> החשבוניות האחרונות שולמו ב{methodMeta(paymentMethods.recent_invoices.find(r => Number(r.Amount) > 0)?.method || 'unknown').label} — לכן אין כרטיס שמור לשחזור.
          </div>
        )}

        {searched && candidates.length === 0 && !error && !(paymentMethods && paymentMethods.recent_invoices.length > 0) && (
          <div style={{textAlign: 'center', padding: '20px', color: '#a1a5b7', fontSize: '13px'}}>לא נמצאו עסקאות עם כרטיס שמור בטווח הזה.</div>
        )}

        {searched && candidates.length === 0 && paymentMethods && paymentMethods.recent_invoices.length > 0 && (
          <div style={{marginBottom: '4px'}}>
            <div style={{fontSize: '11px', color: '#7e8299', marginBottom: '6px'}}>
              {paymentMethods.non_cc_only ? 'חשבוניות אחרונות (אמצעי תשלום):' : 'לא נמצאו עסקאות אשראי בטווח. חשבוניות אחרונות של הלקוח:'}
            </div>
            <div style={{maxHeight: '40vh', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '6px'}}>
              {paymentMethods.recent_invoices.map((inv: AccountInvoiceMethod) => {
                const m = methodMeta(inv.method)
                return (
                  <div key={inv.Invoice_Number} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '8px 11px', borderRadius: '9px', border: '1px solid #e4e6ef', background: '#fff'}}>
                    <div style={{fontSize: '12px'}}>
                      <div style={{fontWeight: 700, color: '#1e1e2d'}}>{fmt(inv.Amount)}</div>
                      <div style={{color: '#a1a5b7', fontSize: '11px', marginTop: '2px'}}>
                        <i className="bi bi-receipt" style={{marginLeft: '4px'}}></i>#{inv.Invoice_Number} · {String(inv.InvoiceDate).slice(0, 10)}
                        {inv.Cust_Name ? ` · ${inv.Cust_Name}` : ''}
                      </div>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap'}}>
                      {inv.DocUrl && (
                        <a href={inv.DocUrl} target="_blank" rel="noreferrer" title="חשבונית" style={{color: '#3699ff', fontSize: '13px'}}>
                          <i className="bi bi-box-arrow-up-left"></i>
                        </a>
                      )}
                      <span style={{fontSize: '11px', fontWeight: 700, color: m.color, background: `${m.color}1a`, padding: '3px 9px', borderRadius: '6px'}}>
                        <i className={`bi ${m.icon}`} style={{marginLeft: '4px'}}></i>{m.label}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {candidates.length > 0 && (
          <div style={{maxHeight: '46vh', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '8px'}}>
            {candidates.map(c => (
              <div key={c.TranzactionId}
                style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '10px 12px',
                  borderRadius: '10px', border: `1px solid ${c.amount_matches ? '#1bc5bd' : '#e4e6ef'}`, background: c.amount_matches ? '#e8fff3' : '#fff'}}>
                <div style={{fontSize: '12px'}}>
                  <div style={{fontWeight: 700, color: '#1e1e2d'}}>
                    {fmt(c.Amount)} {c.amount_matches && <span style={{fontSize: '10px', color: '#1bc5bd', fontWeight: 600}}>(תואם חודשי)</span>}
                  </div>
                  <div style={{color: '#7e8299', fontSize: '11px', marginTop: '2px'}}>
                    <i className="bi bi-credit-card" style={{marginLeft: '4px'}}></i>****{c.Last4} {c.Brand ? `(${c.Brand})` : ''} · {c.CardMonth}/{String(c.CardYear).slice(-2)}
                  </div>
                  {c.InvoiceName && (
                    <div style={{color: '#3699ff', fontSize: '11px', fontWeight: 600, marginTop: '2px'}}>
                      <i className="bi bi-receipt" style={{marginLeft: '4px'}}></i>{c.InvoiceName}
                    </div>
                  )}
                  <div style={{color: '#a1a5b7', fontSize: '11px', marginTop: '2px'}}>
                    {String(c.CreateDate).replace('T', ' ').slice(0, 16)} · #{c.TranzactionId}
                    {c.CardOwnerName ? ` · ${c.CardOwnerName}` : ''}
                  </div>
                </div>
                {c.already_added ? (
                  <span style={{fontSize: '11px', color: '#1bc5bd', fontWeight: 700, whiteSpace: 'nowrap'}}><i className="bi bi-check-circle" style={{marginLeft: '4px'}}></i>נוסף</span>
                ) : (
                  <button onClick={() => add(c)} disabled={addingId === c.TranzactionId}
                    style={{padding: '6px 14px', borderRadius: '8px', border: 'none', background: '#8950fc', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap'}}>
                    {addingId === c.TranzactionId ? <span className="spinner-border spinner-border-sm" style={{width: '12px', height: '12px'}} /> : 'הוסף כרטיס'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

// Every card ever saved on the account, newest first, with the option to put an
// older one back as the active card. Charging always uses the LAST saved token,
// so "putting back" just moves that card to the end of the list — nothing is
// deleted, and the admin can switch back at any time.
const SavedCardsModal: FC<{
  account: PaymentAccount
  onClose: () => void
  onChanged: (payment_tokens: any[]) => void
}> = ({account, onClose, onChanged}) => {
  const [busyIndex, setBusyIndex] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const tokens: any[] = account.payment_tokens || []
  // Index of the card actually used for charges: the last non-null entry.
  let activeIndex = -1
  for (let i = tokens.length - 1; i >= 0; i--) { if (tokens[i]) { activeIndex = i; break; } }

  const cardOf = (t: any) => {
    const info = t.TranzactionInfo || t.TokenInfo || t.UIValues || t
    const last4 = info.Last4CardDigitsString || info.Last4CardDigits || t.Last4 || t.last4 || t.CardSuffix || ''
    const month = Number(info.CardMonth || t.TokenInfo?.CardMonth || t.UIValues?.CardMonth || 0)
    const yearRaw = Number(info.CardYear || t.TokenInfo?.CardYear || t.UIValues?.CardYear || 0)
    const year = yearRaw > 0 && yearRaw < 100 ? 2000 + yearRaw : yearRaw
    const brand = info.Brand || info.CardName || ''
    const owner = info.CardOwnerName || t.CardOwnerName || ''
    const added = t.reactivated_at || t.recovered_at || t.created_at || t.createdAt || t.CreateDate || info.CreateDate || null
    const provider = t.TranzilaTK ? 'Tranzila' : (t.Token || t.TokenInfo?.Token || t.token) ? 'Cardcom' : 'לא מזוהה'
    // Expiry is end-of-month; a card past it will simply be declined.
    const expired = !!(month && year) && new Date(year, month, 1).getTime() < Date.now()
    return {last4: last4 ? String(last4) : '', month, year, brand, owner, added, provider, expired}
  }

  const tokenOf = (t: any) => t?.TokenInfo?.Token || t?.Token || t?.token || t?.TranzilaTK || ''

  const activate = async (index: number) => {
    setBusyIndex(index); setError(null)
    try {
      const res = await setActivePaymentToken(account._id, {index, token: tokenOf(tokens[index]) || undefined})
      if (res.data.success) {
        onChanged(res.data.payment_tokens || [])
      } else {
        setError(res.data.error || 'שגיאה בהחלפת הכרטיס')
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'שגיאה בהחלפת הכרטיס')
    }
    setBusyIndex(null)
  }

  // Newest first, keeping each card's real index for the server call.
  const rows = tokens
    .map((t, index) => ({t, index}))
    .filter(r => !!r.t)
    .reverse()

  return (
    <Modal onClose={onClose} title={`כרטיסים שמורים - ${account.first_name} ${account.last_name}`}>
      <div style={{padding: '16px 20px'}}>
        <div style={{padding: '10px 12px', background: '#f5f8fa', borderRadius: '8px', fontSize: '12px', color: '#7e8299', marginBottom: '14px', lineHeight: 1.6}}>
          <i className="bi bi-info-circle" style={{marginLeft: '6px'}}></i>
          החיוב תמיד מתבצע בכרטיס <b>הפעיל</b> (האחרון שנשמר). אם הכרטיס החדש לא עובד, אפשר להחזיר כאן כרטיס ישן להיות הפעיל — שום כרטיס לא נמחק, וניתן להחליף שוב בכל רגע.
        </div>

        {error && (
          <div style={{padding: '10px 12px', background: '#fff5f8', border: '1px solid #f64e60', borderRadius: '8px', fontSize: '12px', color: '#f64e60', marginBottom: '12px'}}>{error}</div>
        )}

        {rows.length === 0 ? (
          <div style={{textAlign: 'center', padding: '24px', color: '#a1a5b7', fontSize: '13px'}}>אין כרטיסים שמורים על החשבון.</div>
        ) : (
          <div style={{maxHeight: '52vh', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '8px'}}>
            {rows.map(({t, index}) => {
              const c = cardOf(t)
              const isActive = index === activeIndex
              return (
                <div key={index} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
                  padding: '10px 12px', borderRadius: '10px',
                  border: `1px solid ${isActive ? '#1bc5bd' : '#e4e6ef'}`,
                  background: isActive ? '#e8fff3' : '#fff'
                }}>
                  <div style={{fontSize: '12px', minWidth: 0}}>
                    <div style={{fontWeight: 700, color: '#1e1e2d', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
                      <i className="bi bi-credit-card" style={{color: isActive ? '#1bc5bd' : '#a1a5b7'}}></i>
                      {c.last4 ? `****${c.last4}` : 'כרטיס ללא 4 ספרות'}
                      {c.brand ? <span style={{fontWeight: 600, color: '#7e8299'}}>({c.brand})</span> : null}
                      {c.month && c.year ? (
                        <span style={{fontWeight: 600, color: c.expired ? '#f64e60' : '#7e8299'}}>
                          {c.month}/{String(c.year).slice(-2)}{c.expired ? ' · פג תוקף' : ''}
                        </span>
                      ) : null}
                      {isActive && (
                        <span style={{fontSize: '10px', fontWeight: 700, color: '#fff', background: '#1bc5bd', padding: '2px 8px', borderRadius: '999px'}}>פעיל</span>
                      )}
                    </div>
                    <div style={{color: '#a1a5b7', fontSize: '11px', marginTop: '3px'}}>
                      {c.provider}
                      {c.owner ? ` · ${c.owner}` : ''}
                      {c.added ? ` · נשמר ${String(new Date(c.added).toLocaleDateString('he-IL'))}` : ''}
                      {t.TranzactionId ? ` · עסקה ${t.TranzactionId}` : ''}
                    </div>
                  </div>
                  {isActive ? (
                    <span style={{fontSize: '11px', color: '#1bc5bd', fontWeight: 700, whiteSpace: 'nowrap'}}>
                      <i className="bi bi-check-circle" style={{marginLeft: '4px'}}></i>כרטיס החיוב
                    </span>
                  ) : (
                    <button
                      onClick={() => activate(index)}
                      disabled={busyIndex !== null}
                      style={{
                        padding: '7px 12px', borderRadius: '8px', border: 'none', background: '#3699ff', color: '#fff',
                        fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap',
                        cursor: busyIndex !== null ? 'default' : 'pointer', opacity: busyIndex !== null && busyIndex !== index ? 0.5 : 1
                      }}
                    >
                      {busyIndex === index
                        ? <span className="spinner-border spinner-border-sm" style={{width: '12px', height: '12px'}} />
                        : <><i className="bi bi-arrow-counterclockwise" style={{marginLeft: '5px'}}></i>החזר ככרטיס פעיל</>}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Modal>
  )
}

// Create a Cardcom payment link to send to the client. Works even with no card
// on file — the client enters their card on the Cardcom page; on payment it
// charges (with installments), saves the token, creates the invoice, and
// extends the account by the chosen period.
const PaymentLinkModal: FC<{
  account: PaymentAccount
  defaultAmount: number
  defaultPeriod: string
  defaultInvoiceName: string
  fmt: (n: number) => string
  result: {url: string; amount: number; period_label?: string; max_installments?: number} | null
  onCreate: (data: {amount: number; period?: string; days?: number; max_installments?: number; invoice_name?: string; description?: string}) => Promise<void>
  onClose: () => void
}> = ({account, defaultAmount, defaultPeriod, defaultInvoiceName, fmt, result, onCreate, onClose}) => {
  const [amount, setAmount] = useState(defaultAmount || 0)
  const [periodMode, setPeriodMode] = useState<'monthly' | '3months' | 'yearly' | 'custom'>((defaultPeriod as any) || 'monthly')
  const [customDays, setCustomDays] = useState(30)
  const [installments, setInstallments] = useState(1)
  const [invoiceName, setInvoiceName] = useState(defaultInvoiceName || '')
  const [creating, setCreating] = useState(false)
  const [copied, setCopied] = useState(false)

  const create = async () => {
    if (!amount || amount <= 0) { alert('יש להזין סכום'); return }
    setCreating(true)
    try {
      await onCreate({
        amount,
        period: periodMode === 'custom' ? undefined : periodMode,
        days: periodMode === 'custom' ? customDays : undefined,
        max_installments: installments,
        invoice_name: invoiceName.trim() || undefined,
      })
    } finally { setCreating(false) }
  }
  const copy = () => { if (result) { navigator.clipboard?.writeText(result.url); setCopied(true); setTimeout(() => setCopied(false), 1800) } }
  const waLink = result && account.phone
    ? `https://wa.me/${String(account.phone).replace(/[^\d]/g, '').replace(/^0/, '972')}?text=${encodeURIComponent(`קישור לתשלום:\n${result.url}`)}`
    : null

  return (
    <Modal onClose={onClose} title={`קישור תשלום - ${account.first_name} ${account.last_name}`}>
      <div style={{padding: '20px'}}>
        {!result ? (
          <>
            <div style={{marginBottom: '16px'}}>
              <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '8px', display: 'block'}}>סכום לחיוב (₪, כולל מע״מ):</label>
              <input type="number" min={0} value={amount} onChange={e => setAmount(Math.max(0, parseInt(e.target.value) || 0))}
                style={{width: '160px', padding: '8px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '16px', fontWeight: 700, textAlign: 'center'}} />
            </div>

            <div style={{marginBottom: '16px'}}>
              <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '8px', display: 'block'}}>הארכת מנוי:</label>
              <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                {([['monthly', 'חודש'], ['3months', '3 חודשים'], ['yearly', 'שנה'], ['custom', 'מותאם']] as const).map(([k, label]) => (
                  <button key={k} onClick={() => setPeriodMode(k as any)} style={{
                    padding: '7px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                    border: periodMode === k ? '2px solid #8950fc' : '1px solid #e4e6ef',
                    background: periodMode === k ? '#f1e7fe' : '#fff', color: periodMode === k ? '#8950fc' : '#7e8299'
                  }}>{label}</button>
                ))}
              </div>
              {periodMode === 'custom' && (
                <div style={{marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <input type="number" min={1} value={customDays} onChange={e => setCustomDays(Math.max(1, parseInt(e.target.value) || 1))}
                    style={{width: '90px', padding: '6px 10px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '14px', textAlign: 'center'}} />
                  <span style={{fontSize: '13px', color: '#5e6278'}}>ימים</span>
                </div>
              )}
            </div>

            <div style={{marginBottom: '16px'}}>
              <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '8px', display: 'block'}}>מספר תשלומים (קבוע):</label>
              <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                {[1, 2, 3, 4, 6, 8, 10, 12].map(n => (
                  <button key={n} onClick={() => setInstallments(n)} style={{
                    padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', minWidth: '42px',
                    border: installments === n ? '2px solid #8950fc' : '1px solid #e4e6ef',
                    background: installments === n ? '#f1e7fe' : '#fff', color: installments === n ? '#8950fc' : '#7e8299'
                  }}>{n}</button>
                ))}
              </div>
              {installments > 1 && <div style={{fontSize: '11px', color: '#7e8299', marginTop: '6px'}}>{installments} תשלומים של {fmt(Math.ceil(amount / installments))}</div>}
            </div>

            <div style={{marginBottom: '16px'}}>
              <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '8px', display: 'block'}}>שם לחשבונית:</label>
              <input type="text" value={invoiceName} onChange={e => setInvoiceName(e.target.value)}
                style={{width: '100%', padding: '8px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '14px', direction: 'rtl'}} />
            </div>

            <div style={{marginBottom: '16px', padding: '10px 14px', background: '#f1e7fe', borderRadius: '8px', fontSize: '12px', color: '#8950fc'}}>
              <i className="bi bi-info-circle" style={{marginLeft: '6px'}}></i>
              הלקוח יזין כרטיס בעמוד מאובטח של Cardcom. בתשלום מוצלח: הכרטיס יישמר, תופק חשבונית, והמנוי יוארך ב{periodMode === 'custom' ? `-${customDays} ימים` : periodMode === 'monthly' ? 'חודש' : periodMode === '3months' ? '-3 חודשים' : 'שנה'}.
            </div>

            <button onClick={create} disabled={creating} style={{
              width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
              background: '#8950fc', color: '#fff', fontSize: '15px', fontWeight: 700, cursor: creating ? 'wait' : 'pointer'
            }}>
              {creating ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-link-45deg" style={{marginLeft: '6px'}}></i>צור קישור תשלום</>}
            </button>
          </>
        ) : (
          <>
            <div style={{textAlign: 'center', marginBottom: '16px'}}>
              <div style={{width: '54px', height: '54px', borderRadius: '50%', background: '#f1e7fe', color: '#8950fc', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px'}}>
                <i className="bi bi-link-45deg"></i>
              </div>
              <div style={{fontSize: '15px', fontWeight: 700, color: '#1e1e2d', marginTop: '10px'}}>הקישור מוכן לשליחה</div>
              <div style={{fontSize: '12px', color: '#7e8299', marginTop: '2px'}}>
                {fmt(result.amount)}{result.max_installments && result.max_installments > 1 ? ` · ${result.max_installments} תשלומים` : ''}{result.period_label ? ` · ${result.period_label}` : ''}
              </div>
            </div>
            <div style={{display: 'flex', gap: '8px', marginBottom: '12px'}}>
              <input readOnly value={result.url} dir="ltr"
                style={{flex: 1, padding: '8px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '12px', background: '#f5f8fa'}} />
              <button onClick={copy} style={{padding: '8px 14px', borderRadius: '8px', border: 'none', background: '#8950fc', color: '#fff', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer'}}>
                <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'}`} style={{marginLeft: '4px'}}></i>{copied ? 'הועתק' : 'העתק'}
              </button>
            </div>
            {waLink && (
              <a href={waLink} target="_blank" rel="noreferrer" style={{
                display: 'block', textAlign: 'center', padding: '11px', borderRadius: '10px',
                background: '#25d366', color: '#fff', fontSize: '14px', fontWeight: 700, textDecoration: 'none', marginBottom: '8px'
              }}>
                <i className="bi bi-whatsapp" style={{marginLeft: '6px'}}></i>שלח בוואטסאפ ל-{account.phone}
              </a>
            )}
            <button onClick={onClose} style={{width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #e4e6ef', background: '#fff', color: '#5e6278', fontSize: '13px', fontWeight: 600, cursor: 'pointer'}}>סגור</button>
          </>
        )}
      </div>
    </Modal>
  )
}

const SettingsModalContent: FC<{account: PaymentAccount; onClose: () => void; onSave: (s: any) => void}> = ({account, onClose, onSave}) => {
  const [autoCharge, setAutoCharge] = useState(!account.disable_auto_payment)
  const [period, setPeriod] = useState(account.payment_period || 'monthly')
  const [monthlyAmount, setMonthlyAmount] = useState(account.monthly_payment_amount || 0)

  return (
    <Modal onClose={onClose} title={`הגדרות תשלום - ${account.first_name} ${account.last_name}`}>
      <div style={{padding: '20px'}}>
        {/* Monthly Amount */}
        <div style={{marginBottom: '20px'}}>
          <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '8px', display: 'block'}}>חיוב חודשי (₪):</label>
          <input
            type="number" min={0} value={monthlyAmount}
            onChange={e => setMonthlyAmount(Math.max(0, parseInt(e.target.value) || 0))}
            style={{width: '150px', padding: '8px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '16px', fontWeight: 700, textAlign: 'center'}}
          />
          {monthlyAmount !== account.monthly_payment_amount && (
            <span style={{fontSize: '11px', color: '#ffa800', marginRight: '10px'}}>
              שונה מ-{account.monthly_payment_amount}₪
            </span>
          )}
        </div>

        {/* Auto Charge */}
        <div style={{marginBottom: '20px'}}>
          <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
            <div onClick={() => setAutoCharge(!autoCharge)} style={{
              width: '44px', height: '24px', borderRadius: '12px', position: 'relative',
              background: autoCharge ? '#1bc5bd' : '#e4e6ef', transition: 'all 0.2s', cursor: 'pointer'
            }}>
              <div style={{
                width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                position: 'absolute', top: '2px', transition: 'all 0.2s',
                right: autoCharge ? '2px' : '22px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }} />
            </div>
            <span style={{fontWeight: 600, fontSize: '14px'}}>חיוב אוטומטי</span>
          </label>
          <div style={{fontSize: '11px', color: '#a1a5b7', marginTop: '4px', marginRight: '54px'}}>
            כאשר פעיל, החשבון יחויב אוטומטית כשהמנוי פג
          </div>
        </div>

        {/* Payment Period */}
        <div style={{marginBottom: '24px'}}>
          <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '8px', display: 'block'}}>תקופת תשלום:</label>
          <div style={{display: 'flex', gap: '8px'}}>
            {(['monthly', '3months', 'yearly'] as const).map(p => (
              <button key={p} onClick={() => setPeriod(p)} style={{
                flex: 1, padding: '10px', borderRadius: '8px', cursor: 'pointer',
                border: period === p ? '2px solid #3699ff' : '1px solid #e4e6ef',
                background: period === p ? '#e8f4fd' : '#fff', fontWeight: 600, fontSize: '13px',
                color: period === p ? '#3699ff' : '#7e8299'
              }}>
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
        </div>

        <button onClick={() => onSave({disable_auto_payment: !autoCharge, payment_period: period, monthly_payment_amount: monthlyAmount})} style={{
          width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
          background: '#3699ff', color: '#fff', fontSize: '15px', fontWeight: 700, cursor: 'pointer'
        }}>
          <i className="bi bi-check-lg" style={{marginLeft: '6px'}}></i>שמור הגדרות
        </button>

        {/* Image watermark: enable + per-account position/size override with live preview.
            Saves immediately on its own (independent of the "save settings" button above). */}
        <div style={{marginTop: '20px', borderTop: '1px solid #e4e6ef', paddingTop: '16px'}}>
          <WatermarkOverridePanel accountId={account._id} />
        </div>

        {/* Past Invoices */}
        {(account as any).last_invoices?.length > 0 && (
          <div style={{marginTop: '20px', borderTop: '1px solid #e4e6ef', paddingTop: '16px'}}>
            <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '10px', display: 'block'}}>
              <i className="bi bi-receipt" style={{marginLeft: '6px', color: '#3699ff'}}></i>
              חשבוניות אחרונות
            </label>
            {(account as any).last_invoices.map((inv: any) => (
              <div key={inv._id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: '#f5f8fa', borderRadius: '8px', marginBottom: '6px', fontSize: '12px'
              }}>
                <div>
                  <span style={{color: '#a1a5b7'}}>{new Date(inv.InvoiceDate).toLocaleDateString('he-IL')}</span>
                  {' '}<strong style={{color: '#1e1e2d'}}>₪{inv.TotalIncludeVATNIS?.toLocaleString()}</strong>
                  {inv.Cust_Name && <span style={{color: '#7e8299'}}> · {inv.Cust_Name}</span>}
                </div>
                {inv.DocUrl && (
                  <a href={inv.DocUrl} target="_blank" rel="noreferrer" style={{color: '#3699ff', textDecoration: 'none', fontSize: '13px'}}>
                    <i className="bi bi-file-earmark-text"></i>
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

const TrialEditModalContent: FC<{account: PaymentAccount; onClose: () => void; onSaved: (newDateIso: string) => void}> = ({account, onClose, onSaved}) => {
  const toDateStr = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  // Default: today + 3 days when trial is expired/missing, otherwise current + 3 days
  const defaultDate = () => {
    const base = account.trial_period_end ? new Date(account.trial_period_end) : new Date()
    const now = new Date()
    const anchor = base > now ? base : now
    anchor.setDate(anchor.getDate() + 3)
    return toDateStr(anchor)
  }
  const [newDate, setNewDate] = useState(defaultDate())
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const addDays = (days: number) => {
    const base = account.trial_period_end ? new Date(account.trial_period_end) : new Date()
    const now = new Date()
    const anchor = base > now ? base : now
    anchor.setDate(anchor.getDate() + days)
    setNewDate(toDateStr(anchor))
  }

  const handleSave = async () => {
    if (!newDate) { setError('יש לבחור תאריך'); return }
    if (!reason.trim()) { setError('יש לציין סיבה'); return }
    setSaving(true)
    setError('')
    try {
      const res: any = await updateTrialEnd(account._id, {new_date: newDate, reason})
      // Prefer the server's stored value (end-of-day, with TZ), fall back to local pick.
      const serverDate = res?.data?.trial_period_end || res?.data?.new_trial_end
      onSaved(serverDate || new Date(newDate + 'T23:59:59').toISOString())
    } catch (e: any) {
      setError(e.response?.data?.error || 'שגיאה')
    }
    setSaving(false)
  }

  const QUICK = [
    {days: 3, label: '+3 ימים (ברירת מחדל)', highlight: true},
    {days: 7, label: '+7 ימים'},
    {days: 14, label: '+14 ימים'},
    {days: 30, label: '+30 ימים'}
  ]

  return (
    <Modal onClose={onClose} title={`עריכת מנוי - ${account.first_name} ${account.last_name}`}>
      <div style={{padding: '20px'}}>
        {account.trial_period_end && (
          <div style={{fontSize: '12px', color: '#a1a5b7', marginBottom: '10px'}}>
            תאריך נוכחי: {new Date(account.trial_period_end).toLocaleDateString('he-IL')}
          </div>
        )}
        <div style={{marginBottom: '12px'}}>
          <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '8px', display: 'block'}}>הארכה מהירה:</label>
          <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
            {QUICK.map(q => (
              <button key={q.days} onClick={() => addDays(q.days)} style={{
                padding: '8px 14px', borderRadius: '8px',
                border: q.highlight ? '2px solid #3699ff' : '1px solid #e4e6ef',
                background: q.highlight ? '#e8f4fd' : '#fff',
                color: q.highlight ? '#3699ff' : '#5e6278',
                fontWeight: 600, fontSize: '12px', cursor: 'pointer'
              }}>{q.label}</button>
            ))}
          </div>
        </div>
        <div style={{marginBottom: '16px'}}>
          <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '8px', display: 'block'}}>תאריך מנוי עד:</label>
          <input
            type="date" value={newDate}
            onChange={e => setNewDate(e.target.value)}
            style={{width: '100%', padding: '10px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '14px'}}
          />
        </div>
        <div style={{marginBottom: '20px'}}>
          <label style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', marginBottom: '8px', display: 'block'}}>סיבה:</label>
          <textarea
            value={reason} onChange={e => setReason(e.target.value)}
            placeholder="ציין סיבה לשינוי..."
            rows={3}
            style={{width: '100%', padding: '10px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '13px', resize: 'vertical'}}
          />
        </div>
        {error && <div style={{color: '#f64e60', fontSize: '12px', marginBottom: '12px'}}>{error}</div>}
        <button onClick={handleSave} disabled={saving} style={{
          width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
          background: '#3699ff', color: '#fff', fontSize: '15px', fontWeight: 700,
          cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1
        }}>
          {saving ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check-lg" style={{marginLeft: '6px'}}></i>שמור</>}
        </button>
      </div>
    </Modal>
  )
}

// ============================================
// AutoChargeModal — preview + run billing for all accounts with auto payment on
// ============================================
type AutoChargeResult = {status: 'charging' | 'success' | 'failed'; message?: string; amount?: number}

const AutoChargeModal: FC<{
  onClose: (didCharge: boolean) => void
  onCharged: (accountId: string, newTrialEnd?: string) => void
}> = ({onClose, onCharged}) => {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [accounts, setAccounts] = useState<any[]>([])
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [running, setRunning] = useState(false)
  const [done, setDone] = useState(false)
  const [results, setResults] = useState<Record<string, AutoChargeResult>>({})

  const fmt = (n: number) => `₪${n.toLocaleString('he-IL', {minimumFractionDigits: 0, maximumFractionDigits: 0})}`

  useEffect(() => {
    getAutoChargeEligible()
      .then(res => {
        if (res.data.success) {
          setAccounts(res.data.accounts || [])
          const sel: Record<string, boolean> = {}
          for (const a of res.data.accounts || []) sel[a._id] = true
          setSelected(sel)
        } else {
          setLoadError(res.data.error || 'שגיאה בטעינת רשימת החשבונות')
        }
      })
      .catch((e: any) => setLoadError(e.response?.data?.error || 'שגיאה בטעינת רשימת החשבונות'))
      .finally(() => setLoading(false))
  }, [])

  const selectedAccounts = accounts.filter(a => selected[a._id])
  const selectedTotal = selectedAccounts.reduce((s, a) => s + (a.total_amount || 0), 0)
  const successCount = Object.values(results).filter(r => r.status === 'success').length
  const failedCount = Object.values(results).filter(r => r.status === 'failed').length
  const chargedTotal = Object.values(results).reduce((s, r) => s + (r.status === 'success' ? (r.amount || 0) : 0), 0)
  const didCharge = successCount > 0

  const handleClose = () => {
    if (running) return // don't allow closing mid-run
    onClose(didCharge)
  }

  const run = async () => {
    if (!selectedAccounts.length) return
    if (!window.confirm(`לחייב ${selectedAccounts.length} חשבונות בסך ${fmt(selectedTotal)}?\nכל חשבון יחויב בנפרד והמנוי שלו יוארך.`)) return
    setRunning(true)
    for (const a of selectedAccounts) {
      setResults(prev => ({...prev, [a._id]: {status: 'charging'}}))
      try {
        const res = await chargeAccount(a._id, {
          period: a.payment_period || 'monthly',
          installments: 1,
          // Only pass invoice_name when the account has no company name (the charge
          // route falls back to company_name and we don't want to overwrite it).
          ...(a.has_company_name ? {} : {invoice_name: a.invoice_name})
        })
        if (res.data.success) {
          setResults(prev => ({...prev, [a._id]: {
            status: 'success',
            amount: res.data.amount_charged,
            message: `חויב ${fmt(res.data.amount_charged)} · מנוי עד ${new Date(res.data.new_trial_end).toLocaleDateString('he-IL')}`
          }}))
          onCharged(a._id, res.data.new_trial_end)
        } else {
          setResults(prev => ({...prev, [a._id]: {status: 'failed', message: res.data.error || 'החיוב נכשל'}}))
        }
      } catch (e: any) {
        setResults(prev => ({...prev, [a._id]: {status: 'failed', message: e.response?.data?.error || 'שגיאה בחיוב'}}))
      }
    }
    setRunning(false)
    setDone(true)
  }

  return (
    <Modal onClose={handleClose} title="חיוב אוטומטי - כל החשבונות עם חיוב אוטומטי פעיל">
      <div style={{padding: '20px'}}>
        {loading ? (
          <div style={{textAlign: 'center', padding: '40px'}}>
            <div className="spinner-border" style={{color: '#1bc5bd'}} />
            <div style={{fontSize: '13px', color: '#7e8299', marginTop: '10px'}}>טוען חשבונות לחיוב...</div>
          </div>
        ) : loadError ? (
          <div style={{textAlign: 'center', padding: '30px', color: '#f64e60', fontSize: '13px'}}>
            <i className="bi bi-exclamation-triangle" style={{fontSize: '24px', display: 'block', marginBottom: '8px'}}></i>
            {loadError}
          </div>
        ) : accounts.length === 0 ? (
          <div style={{textAlign: 'center', padding: '30px', color: '#a1a5b7', fontSize: '13px'}}>
            <i className="bi bi-check-circle" style={{fontSize: '24px', display: 'block', marginBottom: '8px', color: '#1bc5bd'}}></i>
            אין חשבונות שדורשים חיוב אוטומטי כרגע
          </div>
        ) : (
          <>
            {/* Summary header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px',
              padding: '12px 14px', borderRadius: '10px', marginBottom: '14px',
              background: done ? (failedCount > 0 ? '#fff8dd' : '#e8fff3') : '#f5f8fa',
              border: `1px solid ${done ? (failedCount > 0 ? '#ffa800' : '#1bc5bd') : '#e4e6ef'}`
            }}>
              {done ? (
                <div style={{fontSize: '13px', fontWeight: 700, color: '#1e1e2d'}}>
                  <i className="bi bi-flag-fill" style={{marginLeft: '6px', color: failedCount > 0 ? '#ffa800' : '#1bc5bd'}}></i>
                  הסתיים: {successCount} חויבו בהצלחה ({fmt(chargedTotal)}){failedCount > 0 ? ` · ${failedCount} נכשלו` : ''}
                </div>
              ) : (
                <>
                  <div style={{fontSize: '13px', fontWeight: 700, color: '#1e1e2d'}}>
                    יחויבו: {selectedAccounts.length} / {accounts.length} חשבונות
                  </div>
                  <div style={{fontSize: '15px', fontWeight: 700, color: '#1bc5bd'}}>
                    סה״כ {fmt(selectedTotal)}
                  </div>
                </>
              )}
            </div>

            {/* Account list */}
            <div style={{maxHeight: '340px', overflowY: 'auto', marginBottom: '14px'}}>
              {accounts.map(a => {
                const r = results[a._id]
                const isSelected = !!selected[a._id]
                return (
                  <div key={a._id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 10px', borderRadius: '8px', marginBottom: '6px',
                    background: r?.status === 'success' ? '#e8fff3' : r?.status === 'failed' ? '#fff5f8' : r?.status === 'charging' ? '#e8f4fd' : isSelected ? '#fff' : '#f9fafb',
                    border: `1px solid ${r?.status === 'success' ? '#c8efd4' : r?.status === 'failed' ? '#fdd' : '#e4e6ef'}`,
                    opacity: !isSelected && !r ? 0.5 : 1
                  }}>
                    {!running && !done && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={e => setSelected(prev => ({...prev, [a._id]: e.target.checked}))}
                        style={{width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0}}
                      />
                    )}
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={{fontSize: '13px', fontWeight: 600, color: '#1e1e2d', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
                        <span>{a.first_name} {a.last_name}</span>
                        {a.children_count > 0 && (
                          <span style={{fontSize: '10px', color: '#3699ff', fontWeight: 600}}>+{a.children_count} תתי-חשבונות</span>
                        )}
                        <span style={{fontSize: '10px', color: '#a1a5b7', fontWeight: 500}}>
                          {PERIOD_LABELS[a.payment_period] || a.payment_period} · {a.token_type === 'tranzila' ? 'Tranzila' : 'Cardcom'}
                        </span>
                      </div>
                      <div style={{fontSize: '11px', color: '#a1a5b7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {a.email}
                        {a.trial_period_end && <span> · פג {new Date(a.trial_period_end).toLocaleDateString('he-IL')}</span>}
                      </div>
                      {r?.message && (
                        <div style={{fontSize: '11px', fontWeight: 600, marginTop: '2px', color: r.status === 'success' ? '#50cd89' : '#f64e60'}}>
                          {r.message}
                        </div>
                      )}
                    </div>
                    <div style={{textAlign: 'left', flexShrink: 0}}>
                      <div style={{fontSize: '14px', fontWeight: 700, color: '#1e1e2d'}}>{fmt(a.total_amount)}</div>
                      {r?.status === 'charging' && <span className="spinner-border spinner-border-sm" style={{color: '#3699ff', width: '14px', height: '14px'}} />}
                      {r?.status === 'success' && <i className="bi bi-check-circle-fill" style={{color: '#50cd89'}}></i>}
                      {r?.status === 'failed' && <i className="bi bi-x-circle-fill" style={{color: '#f64e60'}}></i>}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Footer */}
            {done ? (
              <button
                onClick={handleClose}
                style={{
                  width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                  background: '#3699ff', color: '#fff', fontSize: '15px', fontWeight: 700, cursor: 'pointer'
                }}
              >
                <i className="bi bi-arrow-clockwise" style={{marginLeft: '6px'}}></i>
                סגור ורענן
              </button>
            ) : (
              <button
                onClick={run}
                disabled={running || selectedAccounts.length === 0}
                style={{
                  width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                  background: running ? '#e8f4fd' : selectedAccounts.length ? '#1bc5bd' : '#e4e6ef',
                  color: running ? '#3699ff' : selectedAccounts.length ? '#fff' : '#a1a5b7',
                  fontSize: '15px', fontWeight: 700,
                  cursor: running ? 'wait' : selectedAccounts.length ? 'pointer' : 'default'
                }}
              >
                {running ? (
                  <>
                    <span className="spinner-border spinner-border-sm" style={{marginLeft: '8px'}} />
                    מחייב... ({successCount + failedCount} / {selectedAccounts.length})
                  </>
                ) : (
                  <>
                    <i className="bi bi-lightning-charge-fill" style={{marginLeft: '6px'}}></i>
                    חייב {selectedAccounts.length} חשבונות - {fmt(selectedTotal)}
                  </>
                )}
              </button>
            )}
            {running && (
              <div style={{textAlign: 'center', fontSize: '11px', color: '#ffa800', marginTop: '8px'}}>
                <i className="bi bi-exclamation-triangle" style={{marginLeft: '4px'}}></i>
                אל תסגור את החלון - החיובים מתבצעים אחד אחרי השני
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}

// ============================================
// NeedsChargeCard — comprehensive card for accounts needing charge
// ============================================
interface NeedsChargeCardProps {
  account: PaymentAccount
  profiles: any[]
  pstats: any
  lastPayments: any[]
  daysExpired: number
  hasToken: boolean
  result?: {success: boolean; message: string}
  charging: boolean
  paymentMethodLoading: boolean
  loginAsLoading: boolean
  editingAmount: boolean
  editAmountValue: number
  fmt: (n: number) => string
  getTotalMonthly: (a: PaymentAccount) => number
  getChildrenTotal: (a: PaymentAccount) => number
  getTokenType: (t: any[]) => string | null
  getTokenCardInfo: (t: any[]) => any
  onStartEditAmount: () => void
  onChangeEditAmount: (v: number) => void
  onSaveEditAmount: () => void
  onCancelEditAmount: () => void
  onOpenCharge: () => void
  onPaymentLink: () => void
  onAddPaymentMethod: () => void
  onRecover: () => void
  onOpenCards: () => void
  onOpenSettings: () => void
  onOpenHistory: () => void
  onOpenNote: () => void
  onOpenTrialEdit: () => void
  onLoginAs: () => void
  onLock: () => void
  onUnlock: () => void
  onToggleAutoPayment: () => void
  editingNoteId: string | null
  editingNoteText: string
  onStartEditNote: (noteId: string, current: string) => void
  onChangeEditNote: (value: string) => void
  onSaveEditNote: () => void
  onCancelEditNote: () => void
  onDeleteNote: (noteId: string) => void
  onSetGender: (gender: 'male' | 'female' | null) => void
  onOpenWhatsapp: () => void
  onOpenIssue: () => void
  onOpenMeeting: () => void
  onOpenManualPayment: (method: 'bank_transfer' | 'bit') => void
  // Shared bank-transfer text, so the row can copy it without a fetch.
  bankDetails: string
  // Lead activity on this client's virtual number (null = they don't have one).
  vn: VnActivity | null
  aiBusy: boolean
  onRunAi: () => void
  contactFlagBusy: boolean
  onToggleContactFlag: () => void
  onOpenTasks: () => void
  onOpenPaymentLinks: () => void
}

const STAT_ITEMS: Array<{key: string; label: string}> = [
  {key: 'profile_count', label: 'פרופ׳'},
  {key: 'campaign_count', label: 'קמפ׳'},
  {key: 'post_count', label: 'פוסט'},
  {key: 'group_count', label: 'קבוצ׳'},
  {key: 'scheduled_today', label: 'היום'},
  {key: 'posted_today', label: 'פורסם'},
  {key: 'posted_14d', label: 'שבועיים'},
  {key: 'missed_today', label: 'פספוס'},
  {key: 'errors_today', label: 'שגיאות'}
]

const NeedsChargeCard: FC<NeedsChargeCardProps> = ({
  account, profiles, pstats, lastPayments, daysExpired, hasToken, result,
  charging, paymentMethodLoading, loginAsLoading, editingAmount, editAmountValue,
  fmt, getTotalMonthly, getChildrenTotal, getTokenType, getTokenCardInfo,
  onStartEditAmount, onChangeEditAmount, onSaveEditAmount, onCancelEditAmount,
  onOpenCharge, onPaymentLink, onAddPaymentMethod, onRecover, onOpenCards, onOpenSettings, onOpenHistory, onOpenNote,
  onOpenTrialEdit, onLoginAs, onLock, onUnlock, onToggleAutoPayment,
  editingNoteId, editingNoteText, onStartEditNote, onChangeEditNote, onSaveEditNote, onCancelEditNote, onDeleteNote,
  onSetGender, onOpenWhatsapp, onOpenIssue, onOpenMeeting, onOpenManualPayment, bankDetails,
  vn, aiBusy, onRunAi, contactFlagBusy, onToggleContactFlag, onOpenTasks, onOpenPaymentLinks
}) => {
  // "הועתק" tick after copying the bank-transfer details for this client.
  const [bankCopied, setBankCopied] = useState(false)
  // Cards ever saved on the account (null slots are failed/blank saves).
  const savedCardsCount = (account.payment_tokens || []).filter(Boolean).length
  // Post-footer position (top/bottom/off) — local optimistic state.
  const [fp, setFp] = useState<'start' | 'end' | null>(footerPosOf(account))
  const [fpSaving, setFpSaving] = useState(false)
  // Shared account comment (important_notes) — the SAME store shown in the
  // profile-switcher dropdown and on the account detail page.
  const [impNotes, setImpNotes] = useState<any[]>((account as any).important_notes || [])
  const [impSaving, setImpSaving] = useState(false)
  const latestImpNote = impNotes.length ? impNotes[impNotes.length - 1] : null
  const addImpNote = async () => {
    const text = window.prompt('הוסף הערה משותפת (תופיע גם בבורר הפרופילים ובעמוד הלקוח):')
    if (!text || !text.trim()) return
    setImpSaving(true)
    try {
      const res = await addAccountImportantNote(account._id, text.trim())
      setImpNotes(res.data.admin_notes || [])
    } catch (e: any) {
      alert('שגיאה בשמירת הערה: ' + (e?.response?.data?.error || e?.message || ''))
    } finally { setImpSaving(false) }
  }
  const toggleFp = async (target: 'start' | 'end') => {
    const next = fp === target ? null : target
    setFpSaving(true)
    try { await setPostFooterPosition(account._id, next); setFp(next) }
    catch (e: any) { alert('שגיאה בעדכון פוטר פוסט: ' + (e?.response?.data?.error || e?.message || '')) }
    finally { setFpSaving(false) }
  }
  const aiReview = account.ai_review || null
  const verdictMeta = VERDICT_META[aiReview?.verdict || 'review']
  // daysExpired: >0 = past, <0 = future, 0 = today
  const isFuture = daysExpired < 0
  const absDays = Math.abs(daysExpired)
  const expiryLabel = daysExpired === 0
    ? 'פג היום'
    : isFuture
      ? `פג בעוד ${absDays} ${absDays === 1 ? 'יום' : 'ימים'}`
      : `פג לפני ${absDays} ${absDays === 1 ? 'יום' : 'ימים'}`
  const trialColor = isFuture ? '#1bc5bd' : daysExpired > 14 ? '#f64e60' : '#ffa800'
  const trialBg = isFuture ? '#e8fff3' : daysExpired > 14 ? '#f64e60' : daysExpired > 7 ? '#ffa800' : '#fff8dd'
  const trialTextColor = isFuture ? '#1bc5bd' : daysExpired > 7 ? '#fff' : '#ffa800'
  const createdAt = (account as any).createdAt || (account as any).created_at
  const startDateStr = createdAt ? new Date(createdAt).toLocaleDateString('he-IL') : ''
  const startFromNow = createdAt ? hebrewFromNow(createdAt) : ''

  return (
    <>
      {/* Customer asked to cancel. Loud and first: this was previously recorded on the
          account and as a ticket, but never surfaced here, so requests went unnoticed. */}
      {account.cancellation_requested && (
        <div style={{
          background: '#fff5f8', border: '1px solid #f64e60', borderRadius: '8px',
          padding: '8px 10px', marginBottom: '10px', display: 'flex', gap: '8px', alignItems: 'flex-start',
        }}>
          <i className='bi bi-exclamation-octagon-fill' style={{color: '#f64e60', fontSize: '16px', lineHeight: 1.2}} />
          <div style={{minWidth: 0}}>
            <div style={{fontWeight: 700, fontSize: '13px', color: '#f64e60'}}>
              בקשת ביטול מנוי
              {account.cancellation_requested_at && (
                <span style={{fontWeight: 400, color: '#b5546b', marginInlineStart: 6}}>
                  · {new Date(account.cancellation_requested_at).toLocaleDateString('he-IL')}
                </span>
              )}
            </div>
            {account.cancellation_reason && (
              <div style={{fontSize: '12px', color: '#7a1f2f', whiteSpace: 'pre-wrap'}}>
                סיבה: {account.cancellation_reason}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Row 1: Name + amount */}
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '8px'}}>
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{fontWeight: 700, fontSize: '15px', color: '#1e1e2d', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
            <span>{account.first_name} {account.last_name}</span>
            {!account.disable_auto_payment && hasToken && (
              <span style={{padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, background: '#e8fff3', color: '#1bc5bd'}}>חיוב אוטומטי</span>
            )}
            {/* Post-footer banner + top/bottom toggle */}
            <span style={{display: 'inline-flex', alignItems: 'center', gap: '3px'}}>
              <span style={{padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, background: fp ? '#e8f0ff' : '#f1f3f5', color: fp ? '#2b70fa' : '#868e96'}} title={fp ? `פוטר פעיל (${fp === 'start' ? 'למעלה' : 'למטה'})` : 'פוטר כבוי'}>
                {fp ? `פוטר ${fp === 'start' ? '↑' : '↓'}` : 'פוטר ✕'}
              </span>
              {(['start', 'end'] as const).map((t) => (
                <button
                  key={t}
                  disabled={fpSaving}
                  onClick={() => toggleFp(t)}
                  title={t === 'start' ? 'טקסט בתחילת הפוסט' : 'טקסט בסוף הפוסט'}
                  style={{width: 20, height: 18, fontSize: '10px', fontWeight: 700, borderRadius: '4px', cursor: fpSaving ? 'not-allowed' : 'pointer', background: fp === t ? '#2b70fa' : '#eef2f7', color: fp === t ? '#fff' : '#5a6a85', border: `1px solid ${fp === t ? '#2b70fa' : '#d6deea'}`, opacity: fpSaving ? 0.5 : 1, padding: 0, lineHeight: 1}}
                >{t === 'start' ? '↑' : '↓'}</button>
              ))}
            </span>
          </div>
          <div style={{fontSize: '11px', color: '#a1a5b7', display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '3px'}}>
            <span>{account.email}</span>
            {account.phone && <a href={`tel:${String(account.phone).replace(/[^\d+]/g, '')}`} onClick={e => e.stopPropagation()} title="חייג" style={{color: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center'}}><i className="bi bi-telephone" style={{marginLeft: '3px'}}></i>{account.phone}</a>}
            {startDateStr && <span title="תאריך הצטרפות"><i className="bi bi-calendar-plus" style={{marginLeft: '3px'}}></i>{startDateStr}{startFromNow && <span style={{marginRight: '4px', color: '#7e8299'}}>({startFromNow})</span>}</span>}
            <span style={{display: 'inline-flex', alignItems: 'center', gap: '3px'}} title="מגדר הלקוח - קובע איזו גרסת הודעה תישלח">
              <i className="bi bi-person" style={{color: account.gender ? '#8950fc' : '#c4c8d0'}}></i>
              <select
                value={account.gender || ''}
                onChange={e => onSetGender((e.target.value || null) as 'male' | 'female' | null)}
                style={{padding: '1px 4px', border: `1px solid ${account.gender ? '#e9d5ff' : '#e4e6ef'}`, borderRadius: '4px', fontSize: '10px', fontWeight: 600, color: account.gender ? '#8950fc' : '#a1a5b7', background: account.gender ? '#faf5ff' : '#fff', cursor: 'pointer', outline: 'none'}}
              >
                <option value="">מגדר —</option>
                <option value="male">זכר</option>
                <option value="female">נקבה</option>
              </select>
            </span>
          </div>
          {/* Shared comment — same store as the profile-switcher dropdown / account page */}
          <div style={{marginTop: '5px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
            {latestImpNote?.text ? (
              <span
                style={{fontSize: '10px', color: '#b26a00', background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: '5px', padding: '2px 6px', maxWidth: '320px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
                title={impNotes.map((n: any) => `${n.created_at ? new Date(n.created_at).toLocaleDateString('he-IL') + ' — ' : ''}${n.text}`).join('\n')}
              >📝 {latestImpNote.text}{latestImpNote.created_at ? <span style={{color: '#b98a2e', marginInlineStart: 4}}>· {new Date(latestImpNote.created_at).toLocaleDateString('he-IL')}</span> : null}</span>
            ) : (
              <span style={{fontSize: '10px', color: '#c4c8d0'}}>אין הערה משותפת</span>
            )}
            <button
              onClick={addImpNote}
              disabled={impSaving}
              title="הוסף הערה משותפת"
              style={{fontSize: '10px', fontWeight: 600, color: '#b26a00', background: '#fff', border: '1px solid #ffe0a3', borderRadius: '5px', padding: '1px 6px', cursor: impSaving ? 'not-allowed' : 'pointer', opacity: impSaving ? 0.5 : 1}}
            >＋ הערה</button>
          </div>
        </div>
        <div style={{textAlign: 'left', whiteSpace: 'nowrap'}}>
          {editingAmount ? (
            <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
              <input
                type="number" min={0} autoFocus
                value={editAmountValue}
                onChange={e => onChangeEditAmount(Math.max(0, parseInt(e.target.value) || 0))}
                onKeyDown={e => { if (e.key === 'Enter') onSaveEditAmount(); if (e.key === 'Escape') onCancelEditAmount() }}
                style={{width: '80px', padding: '4px 6px', border: '2px solid #3699ff', borderRadius: '6px', fontSize: '16px', fontWeight: 700, textAlign: 'center'}}
              />
              <button onClick={onSaveEditAmount} style={{padding: '4px 8px', borderRadius: '6px', border: 'none', background: '#3699ff', color: '#fff', fontSize: '14px', cursor: 'pointer'}}>
                <i className="bi bi-check-lg"></i>
              </button>
              <button onClick={onCancelEditAmount} style={{padding: '4px 8px', borderRadius: '6px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', fontSize: '14px', cursor: 'pointer'}}>
                <i className="bi bi-x"></i>
              </button>
            </div>
          ) : (
            <div onClick={onStartEditAmount} style={{fontWeight: 700, fontSize: '18px', color: '#1e1e2d', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end'}}>
              {fmt(getTotalMonthly(account))}
              <span style={{
                padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                background: account.payment_period === 'yearly' ? '#fff8dd' : account.payment_period === '3months' ? '#e8f4fd' : '#f5f8fa',
                color: account.payment_period === 'yearly' ? '#ffa800' : account.payment_period === '3months' ? '#3699ff' : '#7e8299'
              }}>
                {PERIOD_LABELS[account.payment_period || 'monthly']}
              </span>
              <i className="bi bi-pencil" style={{fontSize: '10px', opacity: 0.4}}></i>
            </div>
          )}
          {getChildrenTotal(account) > 0 && (
            <div style={{fontSize: '10px', color: '#3699ff'}}>+{account.child_accounts?.filter((c: any) => c.monthly_amount > 0).length} ילדים</div>
          )}
        </div>
      </div>

      {/* Row 2: Trial button + card info */}
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap'}}>
        <button
          onClick={onOpenTrialEdit}
          title="לחץ לעריכת תאריך המנוי"
          style={{
            padding: '6px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer',
            fontSize: '12px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: trialBg, color: trialTextColor,
            boxShadow: daysExpired > 7 ? `0 2px 6px ${trialColor}33` : undefined
          }}
        >
          <i className="bi bi-calendar-event"></i>
          {account.trial_period_end ? new Date(account.trial_period_end).toLocaleDateString('he-IL') : 'ללא תאריך'}
          <span style={{fontWeight: 500, opacity: 0.85}}>· {expiryLabel}</span>
          <i className="bi bi-pencil-square" style={{fontSize: '11px'}}></i>
        </button>
        <div style={{fontSize: '11px', textAlign: 'left'}}>
          {hasToken ? (() => {
            const cardInfo = getTokenCardInfo(account.payment_tokens)
            return (
              <span style={{color: '#1bc5bd'}}>
                <i className="bi bi-credit-card" style={{marginLeft: '3px'}}></i>
                {getTokenType(account.payment_tokens)}
                {cardInfo?.last4 && <span style={{marginRight: '4px'}}>****{cardInfo.last4}</span>}
                {cardInfo?.brand && <span>({cardInfo.brand})</span>}
                {cardInfo?.month && cardInfo?.year && <span style={{marginRight: '3px'}}>{cardInfo.month}/{String(cardInfo.year).slice(-2)}</span>}
              </span>
            )
          })() : (
            <span style={{color: '#f64e60'}}><i className="bi bi-x-circle" style={{marginLeft: '3px'}}></i>אין כרטיס</span>
          )}
          {/* Card saved through a ₪0 token-only page — nothing was collected, so it
              is sitting here waiting for someone to press חיוב. */}
          {(account as any).card_added_uncharged_at && (
            <div
              title={`הכרטיס נשמר בעמוד אשראי על סכום 0 (${new Date((account as any).card_added_uncharged_at).toLocaleDateString('he-IL')}${(account as any).card_added_uncharged_source === 'trial_popup' ? ', פופאפ סיום ניסיון' : (account as any).card_added_uncharged_source === 'admin_link' ? ', קישור מהמערכת' : ', עמוד החיוב של הלקוח'}) — לא בוצע חיוב. יש ללחוץ "חייב עכשיו".`}
              style={{marginTop: '3px', color: '#b45309', fontWeight: 700, whiteSpace: 'nowrap'}}
            >
              <i className="bi bi-exclamation-triangle-fill" style={{marginLeft: '3px'}}></i>
              כרטיס נשמר, טרם חויב
            </div>
          )}
        </div>
      </div>

      {/* Profile avatars + status dots */}
      {profiles.length > 0 && (
        <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px', padding: '6px 0', borderTop: '1px solid #f1f1f2', borderBottom: '1px solid #f1f1f2'}}>
          {profiles.slice(0, 10).map((p: any) => {
            const name = `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.facebook_username || 'פרופיל'
            const dotColor = STATUS_DOT_COLOR[p.status] || STATUS_DOT_COLOR.disconnected
            const isNewProfile = typeof p.facebook_id === 'string' && p.facebook_id.startsWith('615')
            // When it dropped, not just that it is down — the difference between a
            // session that died an hour ago and one dead since last year.
            const since = p.status !== 'connected'
              ? (p.disconnected_since
                ? ` · מנותק מ-${new Date(p.disconnected_since).toLocaleDateString('he-IL')} (${hebrewFromNow(p.disconnected_since)})`
                : ' · מנותק — אין תאריך (הפרופיל מעולם לא דיווח חיבור)')
              : ''
            const tooltip = `${name} (${p.status})${since}${isNewProfile ? ' · פרופיל חדש' : ''}`
            return (
              <div key={p._id} title={tooltip} style={{position: 'relative', width: '30px', height: '30px'}}>
                {p.facebook_id ? (
                  <img
                    src={fbPicUrl(p.facebook_id)}
                    alt={name}
                    style={{
                      width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover', background: '#f1f1f2',
                      border: isNewProfile ? '2px solid #ffa800' : undefined
                    }}
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden' }}
                  />
                ) : (
                  <div style={{width: '30px', height: '30px', borderRadius: '50%', background: '#f1f1f2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a1a5b7', fontSize: '12px'}}>
                    <i className="bi bi-person"></i>
                  </div>
                )}
                <span style={{
                  position: 'absolute', bottom: '-2px', right: '-2px',
                  width: '10px', height: '10px', borderRadius: '50%',
                  border: '2px solid #fff', background: dotColor
                }} />
                {isNewProfile && (
                  <span
                    title="פרופיל חדש"
                    style={{
                      position: 'absolute', top: '-6px', left: '-6px',
                      width: '16px', height: '16px', borderRadius: '50%',
                      background: '#ffa800', color: '#fff', fontSize: '10px',
                      fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }}
                  >!</span>
                )}
              </div>
            )
          })}
          {profiles.length > 10 && (
            <div style={{width: '30px', height: '30px', borderRadius: '50%', background: '#f1f1f2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7e8299', fontSize: '10px', fontWeight: 600}}>
              +{profiles.length - 10}
            </div>
          )}
        </div>
      )}

      {/* Nothing is connected: say since WHEN, because that is the charge/don't-charge
          call. The date comes from last_health_check, which the bot freezes at the
          moment the FB session dropped. Profiles old enough to predate the heartbeat
          have no date at all — for those the last post is the only evidence, so it is
          shown either way. */}
      {profiles.length > 0 && !pstats.active_profiles && (
        <div style={{
          marginBottom: '8px', padding: '6px 8px', borderRadius: '6px',
          background: '#fff4f4', border: '1px solid #ffd9d9',
          fontSize: '11px', color: '#b00020', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center'
        }}>
          <span style={{fontWeight: 700}}>
            <i className="bi bi-plug" style={{marginLeft: '4px'}}></i>
            {profiles.length === 1 ? 'הפרופיל מנותק' : `כל ${profiles.length} הפרופילים מנותקים`}
          </span>
          {pstats.disconnected_since ? (
            <span>
              מאז {new Date(pstats.disconnected_since).toLocaleDateString('he-IL')}
              {' '}({hebrewFromNow(pstats.disconnected_since)}
              {pstats.disconnected_days != null ? ` · ${pstats.disconnected_days} ימים` : ''})
            </span>
          ) : (
            <span style={{color: '#7e8299'}}>אין תאריך ניתוק (פרופילים ישנים מלפני מדידת החיבור)</span>
          )}
          <span style={{color: '#7e8299'}}>
            ·{' '}
            {pstats.last_posted_at
              ? `פורסם לאחרונה ${new Date(pstats.last_posted_at).toLocaleDateString('he-IL')} (${hebrewFromNow(pstats.last_posted_at)})`
              : 'לא פורסם כלום ב-90 הימים האחרונים'}
          </span>
        </div>
      )}

      {/* AI billing comment — an opinion only. Nothing here charges, flags or
          messages the client; the admin acts on it with the buttons below. */}
      <div style={{
        marginBottom: '8px', borderRadius: '8px', padding: '8px 10px',
        background: aiReview ? verdictMeta.bg : '#fbfbfd',
        border: `1px solid ${aiReview ? verdictMeta.border : '#eef0f5'}`
      }}>
        <div style={{display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: aiReview ? '4px' : 0}}>
          <i className="bi bi-stars" style={{color: '#8950fc', fontSize: '11px'}}></i>
          <span style={{fontSize: '10px', fontWeight: 700, color: '#8950fc'}}>המלצת AI</span>
          {aiReview && (
            <span style={{
              padding: '1px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 700,
              background: '#fff', color: verdictMeta.color, border: `1px solid ${verdictMeta.border}`
            }}>
              <i className={`bi ${verdictMeta.icon}`} style={{marginLeft: '4px'}}></i>
              {verdictMeta.label}
            </span>
          )}
          <span style={{flex: 1}} />
          {aiReview?.generated_at && (
            <span style={{fontSize: '9px', color: '#a1a5b7'}}>{hebrewFromNow(aiReview.generated_at)}</span>
          )}
          <button
            onClick={onRunAi}
            disabled={aiBusy}
            title={aiReview
              ? 'הרץ ניתוח מחדש ללקוח זה — מתעלם מהמטמון ושואל את המודל שוב'
              : 'הרץ ניתוח AI ללקוח זה'}
            style={{
              padding: '3px 10px', borderRadius: '6px', border: '1px solid #8950fc',
              background: aiBusy ? '#f4f1ff' : '#fff', color: '#8950fc',
              fontSize: '10px', fontWeight: 700,
              cursor: aiBusy ? 'default' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: '4px'
            }}
          >
            <i className={aiBusy ? 'bi bi-hourglass-split' : (aiReview ? 'bi bi-arrow-clockwise' : 'bi bi-play-circle')}></i>
            {aiBusy ? 'מנתח...' : (aiReview ? 'נתח מחדש' : 'נתח')}
          </button>
        </div>
        {aiReview ? (
          <>
            <div style={{fontSize: '11px', color: '#3f4254', lineHeight: 1.45, whiteSpace: 'pre-wrap'}}>
              {aiReview.comment}
            </div>
            {/* "Set a price" is only useful if you can act on it here. */}
            {aiReview.verdict === 'set_price' && !editingAmount && (
              <button
                onClick={onStartEditAmount}
                style={{
                  marginTop: '6px', padding: '4px 12px', borderRadius: '6px', border: 'none',
                  background: '#5c3ec6', color: '#fff', fontSize: '11px', fontWeight: 700, cursor: 'pointer'
                }}
              >
                <i className="bi bi-tag" style={{marginLeft: '5px'}}></i>קבע חיוב חודשי
              </button>
            )}
          </>
        ) : (
          <div style={{fontSize: '10px', color: '#a1a5b7'}}>
            {aiBusy ? 'מנתח...' : 'טרם נותח — לחץ ▶ או "נתח עם AI" למעלה'}
          </div>
        )}
      </div>

      {/* Virtual number — what the client actually got for it this month. */}
      {vn && (
        <div style={{
          marginBottom: '8px', borderRadius: '8px', padding: '8px 10px',
          background: vn.total_leads === 0 ? '#fff5f8' : '#f4f1ff',
          border: `1px solid ${vn.total_leads === 0 ? '#f9c4cf' : '#e0d8ff'}`
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px'}}>
            <i className="bi bi-telephone-inbound-fill" style={{color: vn.total_leads === 0 ? '#c2334a' : '#8950fc', fontSize: '11px'}}></i>
            <span style={{fontSize: '10px', fontWeight: 700, color: vn.total_leads === 0 ? '#c2334a' : '#8950fc'}}>
              מספר וירטואלי
            </span>
            {vn.numbers.map(n => (
              <span key={n.ddi} style={{
                padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                background: '#fff', color: '#5e6278', border: '1px solid #e4e6ef', direction: 'ltr'
              }}>{n.ddi}</span>
            ))}
            <span style={{fontSize: '9px', color: '#a1a5b7'}}>· 30 יום</span>
          </div>
          <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '11px'}}>
            {[
              {label: 'שיחות', value: vn.phone_calls, icon: 'bi-telephone'},
              {label: 'וואטסאפ', value: vn.whatsapp_leads, icon: 'bi-whatsapp'},
              {label: 'תגובות', value: vn.post_comments, icon: 'bi-chat-left-text'},
              {label: 'מרקטפלייס', value: vn.marketplace_inquiries, icon: 'bi-shop'},
            ].map(m => (
              <span key={m.label} style={{display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap'}}>
                <i className={`bi ${m.icon}`} style={{fontSize: '10px', color: '#a1a5b7'}}></i>
                <strong style={{color: m.value > 0 ? '#50cd89' : '#b5b5c3'}}>{m.value}</strong>
                <span style={{fontSize: '9px', color: '#999'}}>{m.label}</span>
              </span>
            ))}
          </div>
          {vn.total_leads === 0 && (
            <div style={{fontSize: '10px', color: '#c2334a', marginTop: '4px', fontWeight: 600}}>
              <i className="bi bi-exclamation-triangle-fill" style={{marginLeft: '4px'}}></i>
              לא התקבלה אף פנייה למספר ב-30 הימים האחרונים
            </div>
          )}
        </div>
      )}

      {/* Profile stats row */}
      {(pstats.profile_count || pstats.campaign_count || pstats.post_count || pstats.group_count) ? (
        <div style={{display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', flexShrink: 0, flexWrap: 'wrap', marginBottom: '8px'}}>
          {STAT_ITEMS.map(s => {
            const value = pstats[s.key] || 0
            const color = s.key === 'errors_today' ? (value > 0 ? '#f44336' : '#b5b5c3')
              : s.key === 'missed_today' ? (value > 0 ? '#ffa800' : '#b5b5c3')
              : (value > 0 ? '#50cd89' : '#b5b5c3')
            return (
              <span key={s.key} style={{display: 'flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap'}}>
                <span style={{fontWeight: 700, color}}>{value}</span>
                <span style={{fontSize: '9px', color: '#999'}}>{s.label}</span>
              </span>
            )
          })}
        </div>
      ) : null}

      {/* Last invoices */}
      {(account as any).last_invoices?.length > 0 && (
        <div style={{marginBottom: '8px', paddingTop: '6px', borderTop: '1px solid #f1f1f2'}}>
          <div style={{fontSize: '10px', color: '#a1a5b7', marginBottom: '3px', fontWeight: 600}}>חשבוניות:</div>
          {(account as any).last_invoices.map((inv: any) => (
            <div key={inv._id} style={{fontSize: '10px', color: '#7e8299', marginBottom: '3px', lineHeight: 1.3, display: 'flex', justifyContent: 'space-between', gap: '6px'}}>
              <span>
                <span style={{color: '#a1a5b7'}}>{new Date(inv.InvoiceDate).toLocaleDateString('he-IL')}</span>
                {' '}<strong style={{color: '#1e1e2d'}}>{fmt(inv.TotalIncludeVATNIS)}</strong>
                {inv.Cust_Name && <span style={{color: '#7e8299'}}> · {inv.Cust_Name}</span>}
              </span>
              {inv.DocUrl && (
                <a href={inv.DocUrl} target="_blank" rel="noreferrer" style={{color: '#3699ff', fontSize: '12px', textDecoration: 'none'}}>
                  <i className="bi bi-file-earmark-text"></i>
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* WhatsApp messages sent — always visible inline */}
      {(account.wa_messages?.length ?? 0) > 0 && (
        <div style={{marginBottom: '8px', background: '#f2fbf6', borderRadius: '6px', padding: '6px 8px', border: '1px solid #e0f3e8'}}>
          <div style={{fontSize: '10px', color: '#25a35a', marginBottom: '4px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px'}}>
            <i className="bi bi-whatsapp"></i>
            <span>הודעות וואטסאפ ({account.wa_messages!.length})</span>
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: '3px', maxHeight: '132px', overflowY: 'auto'}}>
            {account.wa_messages!.map(m => (
              <div key={m._id} style={{fontSize: '10px', color: '#5e6278', lineHeight: 1.35}}>
                <span style={{color: '#a1a5b7'}}>{new Date(m.createdAt).toLocaleDateString('he-IL')}</span>
                {m.template_name && <strong style={{color: '#25a35a'}}> · {m.template_name}</strong>}
                <span style={{color: '#7e8299'}}> — {m.message.length > 70 ? m.message.slice(0, 70) + '…' : m.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Last payments (transactions) — always visible */}
      {lastPayments.length > 0 && (
        <div style={{marginBottom: '8px', background: '#f9fafb', borderRadius: '6px', padding: '6px 8px'}}>
          <div style={{fontSize: '10px', color: '#a1a5b7', marginBottom: '4px', fontWeight: 600, display: 'flex', justifyContent: 'space-between'}}>
            <span>היסטוריית תשלומים ({lastPayments.length})</span>
          </div>
          {lastPayments.map((p: any) => (
            <div key={p._id} style={{fontSize: '10px', marginBottom: '2px', display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: '6px', alignItems: 'center'}}>
              <span style={{color: '#a1a5b7'}}>{new Date(p.created_at).toLocaleDateString('he-IL')}</span>
              <span style={{color: '#7e8299', fontSize: '9px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={p.description || p.payment_method}>
                {({initial: 'ראשוני', recurring: 'חוזר', prorated: 'יחסי', refund: 'החזר'} as any)[p.payment_type] || p.payment_method || ''}
                {p.status === 'failed' && p.failure_reason && <span style={{color: '#f64e60'}}> · {p.failure_reason}</span>}
              </span>
              <span style={{fontWeight: 600, color: '#1e1e2d'}}>{fmt(p.amount)}</span>
              <span style={{
                padding: '1px 6px', borderRadius: '4px', fontWeight: 600, fontSize: '9px',
                background: p.status === 'completed' ? '#e8fff3' : p.status === 'failed' ? '#fff5f8' : '#fff8dd',
                color: p.status === 'completed' ? '#50cd89' : p.status === 'failed' ? '#f64e60' : '#ffa800'
              }}>
                {({completed: 'הושלם', failed: 'נכשל', pending: 'ממתין', refunded: 'הוחזר'} as any)[p.status] || p.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Payment notes */}
      {(account as any).payment_notes?.length > 0 && (
        <div style={{marginBottom: '8px'}}>
          {(account as any).payment_notes.map((note: any) => {
            const isEditing = editingNoteId === note._id
            return (
              <div key={note._id} style={{
                padding: '6px 8px', background: '#fff8dd', borderRadius: '6px', marginBottom: '4px',
                borderRight: '3px solid #ffa800', fontSize: '11px', color: '#5e6278', lineHeight: 1.4
              }}>
                {isEditing ? (
                  <>
                    <textarea
                      value={editingNoteText}
                      onChange={e => onChangeEditNote(e.target.value)}
                      autoFocus
                      rows={2}
                      style={{width: '100%', padding: '6px 8px', border: '1px solid #e4e6ef', borderRadius: '4px', fontSize: '11px', resize: 'vertical', direction: 'rtl', outline: 'none'}}
                    />
                    <div style={{display: 'flex', gap: '4px', justifyContent: 'flex-end', marginTop: '4px'}}>
                      <button
                        onClick={onCancelEditNote}
                        style={{padding: '3px 10px', borderRadius: '4px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', fontSize: '10px', cursor: 'pointer'}}
                      >ביטול</button>
                      <button
                        onClick={onSaveEditNote}
                        disabled={!editingNoteText.trim()}
                        style={{padding: '3px 12px', borderRadius: '4px', border: 'none', background: editingNoteText.trim() ? '#3699ff' : '#e4e6ef', color: editingNoteText.trim() ? '#fff' : '#a1a5b7', fontSize: '10px', fontWeight: 600, cursor: editingNoteText.trim() ? 'pointer' : 'default'}}
                      ><i className="bi bi-check-lg" style={{marginLeft: '3px'}}></i>שמור</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px'}}>
                      <div style={{flex: 1, whiteSpace: 'pre-wrap'}}>{note.description}</div>
                      <div style={{display: 'flex', gap: '2px', flexShrink: 0}}>
                        <button
                          onClick={() => onStartEditNote(note._id, note.description)}
                          title="ערוך"
                          style={{border: 'none', background: 'transparent', color: '#7e8299', cursor: 'pointer', padding: '2px 4px', fontSize: '11px'}}
                        ><i className="bi bi-pencil"></i></button>
                        <button
                          onClick={() => onDeleteNote(note._id)}
                          title="מחק"
                          style={{border: 'none', background: 'transparent', color: '#f64e60', cursor: 'pointer', padding: '2px 4px', fontSize: '11px'}}
                        ><i className="bi bi-trash"></i></button>
                      </div>
                    </div>
                    <div style={{fontSize: '9px', color: '#a1a5b7', marginTop: '2px'}}>
                      {new Date(note.created_at).toLocaleDateString('he-IL')} {new Date(note.created_at).toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit'})}
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Flag for a phone call + send both never-expiring payment links */}
      <div style={{display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap'}}>
        <button
          onClick={onToggleContactFlag}
          disabled={contactFlagBusy}
          title={account.contact_task
            ? 'הסר מרשימת הטיפול הטלפוני'
            : 'סמן שצריך ליצור קשר עם הלקוח לפני חיוב — הלקוח ייכנס לרשימת הטיפול הטלפוני'}
          style={{
            flex: '1 1 160px', padding: '7px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
            border: account.contact_task ? 'none' : '1px solid #ffa800',
            background: account.contact_task ? '#ffa800' : '#fff',
            color: account.contact_task ? '#fff' : '#b76e00',
            cursor: contactFlagBusy ? 'default' : 'pointer', opacity: contactFlagBusy ? 0.6 : 1
          }}
        >
          <i className={`bi ${account.contact_task ? 'bi-check2-circle' : 'bi-telephone-plus'}`} style={{marginLeft: '6px'}}></i>
          {account.contact_task
            ? `ברשימת יצירת קשר${account.contact_task.notes_count ? ` (${account.contact_task.notes_count})` : ''}`
            : 'צריך ליצור קשר'}
        </button>
        <button
          onClick={onOpenTasks}
          title="כל משימות התשלום של הלקוח — הוספה, תאריך, סטטוס והתקדמות"
          style={{
            flex: '0 1 auto', padding: '7px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
            border: '1px solid #3699ff', background: account.open_tasks_count ? '#3699ff' : '#fff',
            color: account.open_tasks_count ? '#fff' : '#3699ff', cursor: 'pointer'
          }}
        >
          <i className="bi bi-list-check" style={{marginLeft: '6px'}}></i>
          משימות{account.open_tasks_count ? ` (${account.open_tasks_count})` : ''}
        </button>
        <button
          onClick={onOpenPaymentLinks}
          title="שלח בוואטסאפ שני קישורי תשלום שלא פגי תוקף — חודשי ושנתי, במחיר של הלקוח הזה"
          style={{
            flex: '1 1 160px', padding: '7px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
            border: 'none', background: '#25a35a', color: '#fff', cursor: 'pointer'
          }}
        >
          <i className="bi bi-link-45deg" style={{marginLeft: '6px'}}></i>
          שלח קישורי תשלום
        </button>
      </div>

      {/* The client's next open payment tasks, visible without opening anything */}
      <AccountTaskPreview tasks={account.open_tasks || []} total={account.open_tasks_count || 0} onOpen={onOpenTasks} />

      {/* Add note button — moved under notes */}
      <div style={{marginBottom: '10px'}}>
        <button onClick={onOpenNote} style={{
          width: '100%', padding: '7px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
          border: '1px dashed #e4e6ef',
          background: (account as any).payment_notes?.length > 0 ? '#fffbf0' : '#fff',
          color: '#7e8299', cursor: 'pointer'
        }}>
          <i className="bi bi-sticky" style={{marginLeft: '6px'}}></i>
          הוסף הערה{(account as any).payment_notes?.length > 0 ? ` (${(account as any).payment_notes.length})` : ''}
        </button>
      </div>

      {/* Actions row */}
      <div style={{display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap'}}>
        {hasToken && account.monthly_payment_amount > 0 && !account.is_locked ? (
          <button
            onClick={onOpenCharge}
            disabled={charging}
            style={{
              flex: '1 1 140px', padding: '10px', borderRadius: '10px', fontSize: '14px', fontWeight: 700, border: 'none',
              background: result?.success ? '#e8fff3' : result?.success === false ? '#fff5f8' : '#f64e60',
              color: result?.success ? '#50cd89' : result?.success === false ? '#f64e60' : '#fff',
              cursor: charging ? 'wait' : 'pointer'
            }}
          >
            {charging ? <span className="spinner-border spinner-border-sm" /> :
              result?.success ? <><i className="bi bi-check-circle" style={{marginLeft: '4px'}}></i>בוצע</> :
              result?.success === false ? <><i className="bi bi-x-circle" style={{marginLeft: '4px'}}></i>נכשל</> :
              <><i className="bi bi-lightning-charge" style={{marginLeft: '4px'}}></i>חייב {fmt(getTotalMonthly(account))}</>}
          </button>
        ) : (
          <button
            onClick={onAddPaymentMethod}
            disabled={paymentMethodLoading}
            style={{flex: '1 1 140px', padding: '10px', borderRadius: '10px', fontSize: '13px', fontWeight: 600, border: 'none', background: '#fff5f8', color: '#f64e60', cursor: 'pointer'}}
          >
            {paymentMethodLoading ? <span className="spinner-border spinner-border-sm" style={{width: '12px', height: '12px'}} /> :
              <><i className="bi bi-credit-card-2-front" style={{marginLeft: '4px'}}></i>הוסף כרטיס</>}
          </button>
        )}
        {!hasToken && (
          <button
            onClick={onRecover}
            title="שחזר כרטיס שמור מ-Cardcom (אם לקוח שילם אך הטוקן לא נשמר)"
            style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #8950fc', background: '#fff', color: '#8950fc', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}
          >
            <i className="bi bi-arrow-counterclockwise"></i>שחזר כרטיס
          </button>
        )}
        {savedCardsCount > 0 && (
          <button
            onClick={onOpenCards}
            title="כל הכרטיסים ששמורים על החשבון — אפשר להחזיר כרטיס ישן להיות הכרטיס הפעיל"
            style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #c9d6e5', background: '#fff', color: '#5e6278', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}
          >
            <i className="bi bi-credit-card-2-back"></i>כרטיסים ({savedCardsCount})
          </button>
        )}
        <button
          onClick={onPaymentLink}
          title="צור קישור תשלום לשליחה ללקוח (עובד גם ללא כרטיס שמור)"
          style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #e9d5ff', background: '#f1e7fe', color: '#8950fc', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}
        >
          <i className="bi bi-link-45deg"></i>קישור תשלום
        </button>
        <button
          onClick={() => {
            // {first_name} / {last_name} work here exactly like in the WhatsApp templates.
            const text = (bankDetails || '')
              .replace(/\{first_name\}/g, account.first_name || '')
              .replace(/\{last_name\}/g, account.last_name || '')
            if (!text.trim()) return
            navigator.clipboard?.writeText(text)
            setBankCopied(true)
            setTimeout(() => setBankCopied(false), 1800)
          }}
          disabled={!bankDetails.trim()}
          title={bankDetails.trim() ? 'העתק את פרטי ההעברה הבנקאית לשליחה ללקוח' : 'עדיין לא הוגדרו פרטי העברה בנקאית — הגדר אותם בכפתור שבראש הדף'}
          style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #b8e6cf', background: bankCopied ? '#159c5b' : '#f2fff8', color: bankCopied ? '#fff' : '#159c5b', cursor: bankDetails.trim() ? 'pointer' : 'not-allowed', opacity: bankDetails.trim() ? 1 : 0.5, fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}
        >
          <i className={`bi ${bankCopied ? 'bi-check2' : 'bi-clipboard'}`}></i>{bankCopied ? 'הועתק' : 'העתק פרטי העברה'}
        </button>
        <button
          onClick={() => onOpenManualPayment('bank_transfer')}
          title="רשום תשלום שהתקבל בהעברה בנקאית (ללא חיוב כרטיס) — מאריך את תקופת המנוי"
          style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #b8e6cf', background: '#e8fff3', color: '#159c5b', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}
        >
          <i className="bi bi-bank"></i>העברה בנקאית
        </button>
        <button
          onClick={() => onOpenManualPayment('bit')}
          title="רשום תשלום שהתקבל בביט (ללא חיוב כרטיס) — מאריך את תקופת המנוי ויוצר חשבונית"
          style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #c7b8f5', background: '#f3efff', color: '#6c47d6', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}
        >
          <i className="bi bi-phone"></i>ביט
        </button>
        <button
          onClick={onLoginAs}
          disabled={loginAsLoading}
          title="התחבר כמשתמש"
          style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff8dd', color: '#ffa800', cursor: 'pointer', fontSize: '13px', fontWeight: 600}}
        >
          {loginAsLoading ? <span className="spinner-border spinner-border-sm" style={{width: '12px', height: '12px'}}/> :
            <><i className="bi bi-box-arrow-in-right" style={{marginLeft: '4px'}}></i>כניסה</>}
        </button>
        <a
          href={`/admin/accounts/${account._id}`}
          target="_blank"
          rel="noreferrer"
          title="CRM חדש"
          style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#e8f4fd', color: '#3699ff', textDecoration: 'none', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center'}}
        >
          <i className="bi bi-kanban" style={{marginLeft: '4px'}}></i>CRM
        </a>
        <a
          href={`https://s1.fbzipper.com/admin/customer/${account._id}`}
          target="_blank"
          rel="noreferrer"
          title="CRM ישן"
          style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#f1f1f2', color: '#7e8299', textDecoration: 'none', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center'}}
        >
          <i className="bi bi-box-arrow-up-right" style={{marginLeft: '4px'}}></i>ישן
        </a>
        <button onClick={onOpenSettings} title="הגדרות תשלום" style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
          <i className="bi bi-gear"></i>הגדרות
        </button>
        <button onClick={onOpenHistory} title="היסטוריית תשלומים מלאה" style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
          <i className="bi bi-clock-history"></i>היסטוריה
        </button>
        <button
          onClick={onOpenWhatsapp}
          title="שלח הודעת וואטסאפ ללקוח"
          style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #c8efd4', background: '#e8fff3', color: '#25a35a', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}
        >
          <i className="bi bi-whatsapp"></i>וואטסאפ
        </button>
        <button
          onClick={onOpenIssue}
          title="פתח תקלה ללקוח (שירות לקוחות / תמיכה טכנית / חשבונות)"
          style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #ffd7c2', background: '#fff1ea', color: '#f1416c', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}
        >
          <i className="bi bi-exclamation-triangle"></i>פתח תקלה
        </button>
        <button
          onClick={onOpenMeeting}
          title="קבע פגישה ללקוח (תאריך, שעה והערה)"
          style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #c9e3ff', background: '#eef6ff', color: '#3699ff', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}
        >
          <i className="bi bi-calendar-plus"></i>קבע פגישה
        </button>
        <button
          onClick={onToggleAutoPayment}
          title={account.disable_auto_payment ? 'הפעל חיוב אוטומטי' : 'בטל חיוב אוטומטי'}
          style={{
            padding: '8px 10px', borderRadius: '8px', border: '1px solid #e4e6ef', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
            background: account.disable_auto_payment ? '#f1f1f2' : '#e8fff3',
            color: account.disable_auto_payment ? '#a1a5b7' : '#1bc5bd',
            display: 'inline-flex', alignItems: 'center', gap: '4px'
          }}
        >
          <i className={`bi ${account.disable_auto_payment ? 'bi-toggle-off' : 'bi-toggle-on'}`}></i>
          {account.disable_auto_payment ? 'אוטו כבוי' : 'אוטו פעיל'}
        </button>
        {account.is_locked ? (
          <button
            onClick={onUnlock}
            title="הפעל חשבון מחדש"
            style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #c8efd4', background: '#e8fff3', color: '#1bc5bd', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}
          >
            <i className="bi bi-unlock-fill"></i>הפעל מחדש
          </button>
        ) : (
          <button
            onClick={onLock}
            title="השבת חשבון"
            style={{padding: '8px 10px', borderRadius: '8px', border: '1px solid #fdd', background: '#fff5f8', color: '#f64e60', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px'}}
          >
            <i className="bi bi-lock-fill"></i>השבת
          </button>
        )}
      </div>
      {result && !result.success && <div style={{fontSize: '11px', color: '#f64e60', marginTop: '6px'}}>{result.message}</div>}
      {result?.success && <div style={{fontSize: '11px', color: '#50cd89', marginTop: '6px'}}>{result.message}</div>}
    </>
  )
}

// ============================================
// Manual (bank transfer) payment modal — record a payment already received (no card
// charge): amount + date + period; extends the subscription and creates a bank-transfer invoice.
// ============================================
const ManualPaymentModal: FC<{
  account: PaymentAccount
  method: 'bank_transfer' | 'bit'
  fmt: (n: number) => string
  onClose: () => void
  onSaved: (newTrialEnd: string) => void
}> = ({account, method, fmt, onClose, onSaved}) => {
  const methodLabel = method === 'bit' ? 'ביט' : 'העברה בנקאית'
  const defaultPeriod = account.payment_period || 'monthly'
  const [period, setPeriod] = useState(defaultPeriod)
  const [amount, setAmount] = useState<number>(
    Math.round((account.monthly_payment_amount || 0) * (PERIOD_MULTIPLIER[defaultPeriod] || 1))
  )
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10))
  const [invoiceName, setInvoiceName] = useState<string>(
    account.company_name || (account as any).last_invoices?.[0]?.Cust_Name || `${account.first_name} ${account.last_name}`.trim()
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const onPeriodChange = (p: string) => {
    setPeriod(p)
    setAmount(Math.round((account.monthly_payment_amount || 0) * (PERIOD_MULTIPLIER[p] || 1)))
  }

  const submit = async () => {
    if (!amount || amount <= 0) { setError('יש להזין סכום תקין'); return }
    if (!invoiceName.trim()) { setError('יש להזין שם לחשבונית'); return }
    if (!payDate) { setError('יש לבחור תאריך תשלום'); return }
    setSaving(true); setError('')
    try {
      const res = await recordManualPayment(account._id, {amount, payment_date: payDate, period, invoice_name: invoiceName.trim(), method})
      if (res.data.success) onSaved(res.data.new_trial_end || '')
      else setError(res.data.error || 'שגיאה ברישום התשלום')
    } catch (e: any) {
      setError(e.response?.data?.error || 'שגיאה ברישום התשלום')
    }
    setSaving(false)
  }

  const currentEnd = account.trial_period_end ? new Date(account.trial_period_end) : null
  const days = PERIOD_DAYS[period] || 30

  return (
    <Modal onClose={onClose} title={`רישום ${methodLabel} - ${account.first_name} ${account.last_name}`}>
      <div style={{padding: '20px 24px 24px'}}>
        <div style={method === 'bit'
          ? {marginBottom: '14px', padding: '10px 12px', background: '#f3efff', borderRadius: '8px', border: '1px solid #c7b8f5', fontSize: '12px', color: '#6c47d6'}
          : {marginBottom: '14px', padding: '10px 12px', background: '#e8fff3', borderRadius: '8px', border: '1px solid #b8e6cf', fontSize: '12px', color: '#159c5b'}}>
          <i className={method === 'bit' ? 'bi bi-phone' : 'bi bi-bank'} style={{marginLeft: '6px'}}></i>
          רישום תשלום שהתקבל ב{methodLabel}. תיווצר חשבונית ותקופת המנוי תוארך בהתאם.
        </div>

        <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#5e6278', marginBottom: '5px'}}>סכום ששולם (כולל מע"מ)</label>
        <input
          type="number" min={0} autoFocus
          value={amount}
          onChange={e => setAmount(Math.max(0, parseInt(e.target.value) || 0))}
          style={{width: '100%', padding: '9px 10px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '15px', fontWeight: 700, marginBottom: '14px', outline: 'none'}}
        />

        <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#5e6278', marginBottom: '5px'}}>שם לחשבונית</label>
        <input
          type="text"
          value={invoiceName}
          onChange={e => setInvoiceName(e.target.value)}
          placeholder="שם החברה / הלקוח לחשבונית"
          style={{width: '100%', padding: '9px 10px', border: `1px solid ${invoiceName.trim() ? '#e4e6ef' : '#f5c2c7'}`, borderRadius: '8px', fontSize: '13px', marginBottom: '14px', outline: 'none', direction: 'rtl'}}
        />

        <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#5e6278', marginBottom: '5px'}}>תאריך התשלום</label>
        <input
          type="date"
          value={payDate}
          max={new Date().toISOString().slice(0, 10)}
          onChange={e => setPayDate(e.target.value)}
          style={{width: '100%', padding: '9px 10px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '13px', marginBottom: '14px', outline: 'none'}}
        />

        <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#5e6278', marginBottom: '5px'}}>תקופת התשלום</label>
        <div style={{display: 'flex', gap: '8px', marginBottom: '14px'}}>
          {Object.keys(PERIOD_LABELS).map(p => (
            <button
              key={p}
              onClick={() => onPeriodChange(p)}
              style={{
                flex: 1, padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                border: period === p ? '2px solid #159c5b' : '1px solid #e4e6ef',
                background: period === p ? '#e8fff3' : '#fff', color: period === p ? '#159c5b' : '#7e8299'
              }}
            >{PERIOD_LABELS[p]}</button>
          ))}
        </div>

        <div style={{fontSize: '12px', color: '#7e8299', marginBottom: '16px', padding: '10px 12px', background: '#f5f8fa', borderRadius: '8px'}}>
          <div>סיום מנוי נוכחי: <strong>{currentEnd ? currentEnd.toLocaleDateString('he-IL') : '—'}</strong></div>
          <div style={{marginTop: '4px'}}>יוארך ב-<strong>{days}</strong> ימים ({PERIOD_LABELS[period]})</div>
        </div>

        {error && <div style={{fontSize: '12px', color: '#f64e60', marginBottom: '10px', fontWeight: 600}}>{error}</div>}

        <button
          onClick={submit}
          disabled={saving || !amount || !invoiceName.trim()}
          style={{
            width: '100%', padding: '11px', borderRadius: '8px', border: 'none',
            background: (!saving && amount && invoiceName.trim()) ? '#159c5b' : '#e4e6ef', color: (!saving && amount && invoiceName.trim()) ? '#fff' : '#a1a5b7',
            fontSize: '14px', fontWeight: 700, cursor: (!saving && amount && invoiceName.trim()) ? 'pointer' : 'default',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
          }}
        >
          {saving ? <span className="spinner-border spinner-border-sm" style={{width: '14px', height: '14px'}} /> : <i className="bi bi-check-lg"></i>}
          {saving ? 'רושם...' : `רשום תשלום ${fmt(amount)}`}
        </button>
      </div>
    </Modal>
  )
}

// ============================================
// WhatsApp send modal — pick a template, preview the gender variant, send, see history
// ============================================
const fillWaTemplate = (text: string, account: {first_name?: string; last_name?: string}) =>
  String(text || '')
    .replace(/\{first_name\}/g, account.first_name || '')
    .replace(/\{last_name\}/g, account.last_name || '')

// Departments a support/billing ticket can be routed to from the payments page.
// Values must match AdminUser.departments so the assignee list filters correctly.
const ISSUE_DEPT_VALUES = ['customer_service', 'development', 'accounting']

// Open a real issue (תקלה) for a customer, routed to a department (customer
// support / technical support / billing) and optionally assigned to a specific
// staff member. Posts to /admin/issues — the same system as the Issues page —
// with the customer attached to the issue's affected-accounts checklist.
const OpenIssueModal: FC<{
  account: PaymentAccount
  onClose: () => void
}> = ({account, onClose}) => {
  const depts = ISSUE_DEPARTMENTS.filter(d => ISSUE_DEPT_VALUES.includes(d.value))
  const [admins, setAdmins] = useState<{_id: string; username: string; departments?: string[]}[]>([])
  const [department, setDepartment] = useState('customer_service')
  const [assignedTo, setAssignedTo] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState<{ok: boolean; text: string} | null>(null)

  useEffect(() => {
    let alive = true
    getIssueAdmins().then(r => { if (alive) setAdmins(r.data.admins || []) }).catch(() => {})
    return () => { alive = false }
  }, [])

  const filteredAdmins = department ? admins.filter(a => a.departments?.includes(department)) : admins

  const handleCreate = async () => {
    const t = title.trim() || `תקלה - ${account.first_name || ''} ${account.last_name || ''}`.trim()
    setSaving(true)
    setFeedback(null)
    try {
      await createIssue({
        title: t,
        description: description.trim() || undefined,
        account_ids: [account._id],
        department: department || undefined,
        assigned_to: assignedTo || undefined,
      })
      setFeedback({ok: true, text: 'התקלה נפתחה בהצלחה'})
    } catch (e: any) {
      setFeedback({ok: false, text: e.response?.data?.error || 'שגיאה בפתיחת התקלה'})
    }
    setSaving(false)
  }

  const fieldStyle: React.CSSProperties = {width: '100%', padding: '9px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '13px', outline: 'none', direction: 'rtl'}
  const labelStyle: React.CSSProperties = {fontSize: '12px', fontWeight: 600, color: '#5e6278', marginBottom: '6px', display: 'block'}

  return (
    <Modal onClose={onClose} title={`פתיחת תקלה - ${account.first_name} ${account.last_name}`}>
      <div style={{padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px'}}>
        {feedback?.ok ? (
          <div style={{textAlign: 'center', padding: '10px 0'}}>
            <div style={{color: '#50cd89', fontSize: '15px', fontWeight: 700, marginBottom: '10px'}}>
              <i className="bi bi-check-circle" style={{marginLeft: '6px'}}></i>{feedback.text}
            </div>
            <a href="/admin/issues" target="_blank" rel="noreferrer"
              style={{display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '8px 16px', borderRadius: '8px', background: '#fff1ea', color: '#f1416c', textDecoration: 'none', fontWeight: 600, fontSize: '13px'}}>
              <i className="bi bi-box-arrow-up-left"></i>עבור לתקלות
            </a>
            <div style={{marginTop: '14px'}}>
              <button onClick={onClose} style={{padding: '8px 20px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', cursor: 'pointer', fontWeight: 600, fontSize: '13px'}}>סגור</button>
            </div>
          </div>
        ) : (
          <>
            {/* Department chips */}
            <div>
              <span style={labelStyle}>מחלקה</span>
              <div style={{display: 'grid', gridTemplateColumns: `repeat(${depts.length}, 1fr)`, gap: '6px'}}>
                {depts.map(d => {
                  const active = department === d.value
                  return (
                    <button key={d.value}
                      onClick={() => { setDepartment(d.value); setAssignedTo('') }}
                      style={{
                        padding: '10px 8px', borderRadius: '8px', cursor: 'pointer',
                        border: active ? '2px solid #f1416c' : '1px solid #e4e6ef',
                        background: active ? '#fff1ea' : '#fff',
                        color: active ? '#f1416c' : '#7e8299',
                        fontWeight: 600, fontSize: '12px'
                      }}
                    >{d.label}</button>
                  )
                })}
              </div>
            </div>

            {/* Assignee */}
            <div>
              <span style={labelStyle}>שיוך לנציג{department ? ` (${depts.find(d => d.value === department)?.label})` : ''}</span>
              <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)} style={fieldStyle}>
                <option value="">ללא שיוך (למחלקה)</option>
                {filteredAdmins.map(a => (
                  <option key={a._id} value={a._id}>{a.username}</option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div>
              <span style={labelStyle}>כותרת התקלה</span>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                placeholder={`תקלה - ${account.first_name || ''} ${account.last_name || ''}`.trim()}
                style={fieldStyle} />
            </div>

            {/* Description */}
            <div>
              <span style={labelStyle}>תיאור מפורט</span>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder="פרט את התקלה..." rows={4}
                style={{...fieldStyle, resize: 'vertical'}} />
            </div>

            {feedback && !feedback.ok && (
              <div style={{fontSize: '12px', color: '#f64e60'}}>{feedback.text}</div>
            )}

            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px'}}>
              <button onClick={onClose} style={{padding: '8px 18px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', cursor: 'pointer', fontWeight: 600, fontSize: '13px'}}>ביטול</button>
              <button onClick={handleCreate} disabled={saving}
                style={{padding: '8px 20px', borderRadius: '8px', border: 'none', background: '#f1416c', color: '#fff', cursor: saving ? 'default' : 'pointer', fontWeight: 600, fontSize: '13px', opacity: saving ? 0.7 : 1}}>
                {saving ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-exclamation-triangle" style={{marginLeft: '6px'}}></i>פתח תקלה</>}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

// ============================================
// Book a meeting for this customer without leaving the payments screen.
// Date + time + note is the whole point — the assignee defaults to the admin
// who is logged in, and the title is filled in for them. Creating it here goes
// through the same POST /admin/meetings as the meetings page, so the customer
// gets the usual WhatsApp coordination message.
// ============================================
const MEETING_TIME_SLOTS = Array.from({length: 25}, (_, i) => {
  const h = Math.floor((i * 20) / 60) + 9
  const m = (i * 20) % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
})

const ScheduleMeetingModal: FC<{
  account: PaymentAccount
  onClose: () => void
}> = ({account, onClose}) => {
  const customerName = `${account.first_name || ''} ${account.last_name || ''}`.trim()
  const [admins, setAdmins] = useState<{_id: string; username: string; email: string}[]>([])
  const [assignedTo, setAssignedTo] = useState('')
  const [date, setDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
  const [time, setTime] = useState('10:00')
  const [meetingType, setMeetingType] = useState<MeetingType>('followup')
  const [title, setTitle] = useState('')
  const [note, setNote] = useState('')
  const [takenSlots, setTakenSlots] = useState<{time: string; minutes: number; account: string}[]>([])
  const [existing, setExisting] = useState<{_id: string; meeting_date: string; title: string}[]>([])
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState<{ok: boolean; text: string} | null>(null)

  const todayStr = (() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })()

  // Admin list + default the assignee to whoever is logged in.
  useEffect(() => {
    let alive = true
    getAdminUsers()
      .then(r => {
        if (!alive) return
        const list = r.data.admins || []
        setAdmins(list)
        checkAdminAuth()
          .then(auth => {
            if (!alive) return
            const me = auth.data.admin?._id
            setAssignedTo(me && list.some(a => a._id === me) ? me : (list[0]?._id || ''))
          })
          .catch(() => { if (alive) setAssignedTo(list[0]?._id || '') })
      })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  // Meetings this customer already has on the books, so we don't double-book.
  useEffect(() => {
    let alive = true
    getMeetings({account: account._id, status: 'scheduled', limit: 5, sort: 'meeting_date'})
      .then(r => { if (alive) setExisting((r.data.meetings || []) as any) })
      .catch(() => {})
    return () => { alive = false }
  }, [account._id])

  // What the picked admin already has that day.
  useEffect(() => {
    if (!assignedTo || !date) { setTakenSlots([]); return }
    let alive = true
    getTakenSlots(assignedTo, date)
      .then(r => { if (alive) setTakenSlots(r.data.takenSlots || []) })
      .catch(() => { if (alive) setTakenSlots([]) })
    return () => { alive = false }
  }, [assignedTo, date])

  const slotTaken = (slot: string) => {
    const [h, m] = slot.split(':').map(Number)
    const mins = h * 60 + m
    return takenSlots.find(t => Math.abs(t.minutes - mins) < 20)
  }
  const slotPast = (slot: string) => new Date(`${date}T${slot}`).getTime() < Date.now()

  const handleCreate = async () => {
    if (!assignedTo || !date || !time) {
      setFeedback({ok: false, text: 'נא לבחור נציג, תאריך ושעה'})
      return
    }
    setSaving(true)
    setFeedback(null)
    try {
      await createMeeting({
        account: account._id,
        assigned_to: assignedTo,
        meeting_date: `${date}T${time}`,
        meeting_type: meetingType,
        title: title.trim() || `${MEETING_TYPE_LABELS[meetingType]} - ${customerName}`,
        notes: note.trim() || undefined,
      })
      setFeedback({ok: true, text: `הפגישה נקבעה ל-${date.split('-').reverse().join('/')} בשעה ${time}`})
    } catch (e: any) {
      setFeedback({ok: false, text: e.response?.data?.error || 'שגיאה בקביעת הפגישה'})
    }
    setSaving(false)
  }

  const fieldStyle: React.CSSProperties = {width: '100%', padding: '9px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '13px', outline: 'none', direction: 'rtl'}
  const labelStyle: React.CSSProperties = {fontSize: '12px', fontWeight: 600, color: '#5e6278', marginBottom: '6px', display: 'block'}

  return (
    <Modal onClose={onClose} title={`קביעת פגישה - ${customerName}`}>
      <div style={{padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px'}}>
        {feedback?.ok ? (
          <div style={{textAlign: 'center', padding: '10px 0'}}>
            <div style={{color: '#50cd89', fontSize: '15px', fontWeight: 700, marginBottom: '10px'}}>
              <i className="bi bi-check-circle" style={{marginLeft: '6px'}}></i>{feedback.text}
            </div>
            <div style={{fontSize: '12px', color: '#7e8299', marginBottom: '12px'}}>הודעת תיאום בוואטסאפ נשלחת ללקוח אוטומטית</div>
            <a href="/admin/meetings" target="_blank" rel="noreferrer"
              style={{display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '8px 16px', borderRadius: '8px', background: '#eef6ff', color: '#3699ff', textDecoration: 'none', fontWeight: 600, fontSize: '13px'}}>
              <i className="bi bi-box-arrow-up-left"></i>עבור לפגישות
            </a>
            <div style={{marginTop: '14px'}}>
              <button onClick={onClose} style={{padding: '8px 20px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', cursor: 'pointer', fontWeight: 600, fontSize: '13px'}}>סגור</button>
            </div>
          </div>
        ) : (
          <>
            {existing.length > 0 && (
              <div style={{padding: '9px 12px', borderRadius: '8px', background: '#fff8dd', border: '1px solid #ffe2a8', fontSize: '12px', color: '#7e6a1f'}}>
                <i className="bi bi-info-circle" style={{marginLeft: '6px'}}></i>
                ללקוח זה כבר יש {existing.length === 1 ? 'פגישה מתוזמנת' : `${existing.length} פגישות מתוזמנות`}:{' '}
                {existing.map(m => new Date(m.meeting_date).toLocaleString('he-IL', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'})).join(' · ')}
              </div>
            )}

            {/* Date + time */}
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
              <div>
                <span style={labelStyle}>תאריך</span>
                <input type="date" value={date} min={todayStr} onChange={e => setDate(e.target.value)} style={fieldStyle} />
              </div>
              <div>
                <span style={labelStyle}>שעה</span>
                <select value={time} onChange={e => setTime(e.target.value)} style={fieldStyle}>
                  {MEETING_TIME_SLOTS.map(slot => {
                    const taken = slotTaken(slot)
                    const past = slotPast(slot)
                    return (
                      <option key={slot} value={slot} disabled={!!taken || past}>
                        {slot}{taken ? ` — תפוס${taken.account ? ` (${taken.account})` : ''}` : past ? ' — עבר' : ''}
                      </option>
                    )
                  })}
                </select>
              </div>
            </div>

            {/* Note — the reason for the meeting */}
            <div>
              <span style={labelStyle}>הערה לפגישה</span>
              <textarea value={note} onChange={e => setNote(e.target.value)}
                placeholder="על מה הפגישה? (יופיע לנציג לפני הפגישה)" rows={3}
                style={{...fieldStyle, resize: 'vertical'}} />
            </div>

            {/* Assignee */}
            <div>
              <span style={labelStyle}>נציג משויך</span>
              <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)} style={fieldStyle}>
                {admins.length === 0 && <option value="">טוען נציגים...</option>}
                {admins.map(a => (
                  <option key={a._id} value={a._id}>{a.username}</option>
                ))}
              </select>
            </div>

            {/* Type + title */}
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
              <div>
                <span style={labelStyle}>סוג פגישה</span>
                <select value={meetingType} onChange={e => setMeetingType(e.target.value as MeetingType)} style={fieldStyle}>
                  {(Object.keys(MEETING_TYPE_LABELS) as MeetingType[]).map(t => (
                    <option key={t} value={t}>{MEETING_TYPE_LABELS[t]}</option>
                  ))}
                </select>
              </div>
              <div>
                <span style={labelStyle}>כותרת</span>
                <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                  placeholder={`${MEETING_TYPE_LABELS[meetingType]} - ${customerName}`}
                  style={fieldStyle} />
              </div>
            </div>

            {feedback && !feedback.ok && (
              <div style={{fontSize: '12px', color: '#f64e60'}}>{feedback.text}</div>
            )}

            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'center'}}>
              <span style={{fontSize: '11px', color: '#a1a5b7', marginLeft: 'auto'}}>הלקוח יקבל הודעת תיאום בוואטסאפ</span>
              <button onClick={onClose} style={{padding: '8px 18px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', cursor: 'pointer', fontWeight: 600, fontSize: '13px'}}>ביטול</button>
              <button onClick={handleCreate} disabled={saving || !assignedTo}
                style={{padding: '8px 20px', borderRadius: '8px', border: 'none', background: '#3699ff', color: '#fff', cursor: saving ? 'default' : 'pointer', fontWeight: 600, fontSize: '13px', opacity: saving || !assignedTo ? 0.7 : 1}}>
                {saving ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-calendar-check" style={{marginLeft: '6px'}}></i>קבע פגישה</>}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

const WhatsappSendModal: FC<{
  account: PaymentAccount
  onClose: () => void
  onManageTemplates: () => void
}> = ({account, onClose, onManageTemplates}) => {
  const [templates, setTemplates] = useState<WaTemplate[]>([])
  const [selectedId, setSelectedId] = useState('')
  const [gender, setGender] = useState<'male' | 'female' | null>(account.gender ?? null)
  const [genderSaving, setGenderSaving] = useState(false)
  const [history, setHistory] = useState<WaMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [feedback, setFeedback] = useState<{ok: boolean; text: string} | null>(null)

  useEffect(() => {
    let alive = true
    Promise.all([getWaTemplates(), getClientWaHistory(account._id)])
      .then(([t, h]) => {
        if (!alive) return
        setTemplates(t.data.templates || [])
        setHistory(h.data.messages || [])
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [account._id])

  const selected = templates.find(t => t._id === selectedId)
  const rawText = selected ? (gender === 'female' ? selected.female_text : selected.male_text) : ''
  const preview = fillWaTemplate(rawText, account)

  const changeGender = async (g: 'male' | 'female' | null) => {
    const prev = gender
    setGender(g)
    setGenderSaving(true)
    try { await setClientGender(account._id, g) }
    catch (e: any) { setGender(prev); alert(e.response?.data?.message || 'שגיאה בעדכון מגדר') }
    finally { setGenderSaving(false) }
  }

  const handleSend = async () => {
    if (!selectedId || !gender) return
    setSending(true)
    setFeedback(null)
    try {
      const res = await sendClientWhatsapp(account._id, selectedId)
      if (res.data.success) {
        setFeedback({ok: true, text: 'ההודעה נשלחה בהצלחה'})
        if (res.data.message && typeof res.data.message === 'object') {
          setHistory(prev => [res.data.message as WaMessage, ...prev])
        }
      } else {
        setFeedback({ok: false, text: (res.data as any).message || 'שגיאה בשליחה'})
      }
    } catch (e: any) {
      setFeedback({ok: false, text: e.response?.data?.message || 'שגיאה בשליחה'})
    }
    setSending(false)
  }

  const canSend = !!selectedId && !!gender && !!preview.trim() && !sending

  return (
    <Modal onClose={onClose} title={`שליחת וואטסאפ - ${account.first_name} ${account.last_name}`}>
      <div style={{padding: '20px 24px 24px'}}>
      <div style={{marginBottom: '14px', fontSize: '12px', color: '#7e8299', display: 'flex', alignItems: 'center', gap: '6px'}}>
        <i className="bi bi-telephone"></i>{account.phone ? <a href={`tel:${String(account.phone).replace(/[^\d+]/g, '')}`} title="חייג" style={{color: 'inherit', textDecoration: 'none'}}>{account.phone}</a> : 'אין טלפון'}
      </div>

      {/* Gender selector */}
      <div style={{marginBottom: '14px'}}>
        <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#5e6278', marginBottom: '6px'}}>מגדר הלקוח</label>
        <div style={{display: 'flex', gap: '8px'}}>
          {([['male', 'זכר'], ['female', 'נקבה']] as const).map(([val, label]) => (
            <button
              key={val}
              disabled={genderSaving}
              onClick={() => changeGender(val)}
              style={{
                flex: 1, padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                border: gender === val ? '2px solid #8950fc' : '1px solid #e4e6ef',
                background: gender === val ? '#faf5ff' : '#fff', color: gender === val ? '#8950fc' : '#7e8299'
              }}
            >{label}</button>
          ))}
        </div>
        {!gender && <div style={{fontSize: '11px', color: '#f64e60', marginTop: '5px'}}>יש לבחור מגדר לפני שליחה</div>}
      </div>

      {/* Template selector */}
      <div style={{marginBottom: '14px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px'}}>
          <label style={{fontSize: '12px', fontWeight: 600, color: '#5e6278'}}>תבנית הודעה</label>
          <button onClick={onManageTemplates} style={{border: 'none', background: 'transparent', color: '#3699ff', fontSize: '11px', fontWeight: 600, cursor: 'pointer'}}>
            <i className="bi bi-gear" style={{marginLeft: '3px'}}></i>נהל תבניות
          </button>
        </div>
        <select
          value={selectedId}
          onChange={e => { setSelectedId(e.target.value); setFeedback(null) }}
          style={{width: '100%', padding: '9px 10px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '13px', outline: 'none', direction: 'rtl', background: '#fff'}}
        >
          <option value="">{loading ? 'טוען...' : templates.length ? 'בחר תבנית' : 'אין תבניות - צור תבנית'}</option>
          {templates.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
        </select>
      </div>

      {/* Preview */}
      {selected && (
        <div style={{marginBottom: '14px'}}>
          <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#5e6278', marginBottom: '6px'}}>תצוגה מקדימה ({gender === 'female' ? 'נקבה' : gender === 'male' ? 'זכר' : '—'})</label>
          <div style={{padding: '12px', borderRadius: '10px', background: '#eafff1', border: '1px solid #c8efd4', fontSize: '13px', color: '#1e1e2d', whiteSpace: 'pre-wrap', minHeight: '48px', direction: 'rtl'}}>
            {gender ? (preview.trim() || <span style={{color: '#f64e60'}}>לתבנית אין טקסט עבור מגדר זה</span>) : <span style={{color: '#a1a5b7'}}>בחר מגדר לתצוגה מקדימה</span>}
          </div>
        </div>
      )}

      {feedback && (
        <div style={{fontSize: '12px', fontWeight: 600, marginBottom: '10px', color: feedback.ok ? '#25a35a' : '#f64e60'}}>
          <i className={`bi ${feedback.ok ? 'bi-check-circle' : 'bi-exclamation-circle'}`} style={{marginLeft: '4px'}}></i>
          {feedback.text}
        </div>
      )}

      <button
        onClick={handleSend}
        disabled={!canSend}
        style={{
          width: '100%', padding: '11px', borderRadius: '8px', border: 'none',
          background: canSend ? '#25a35a' : '#e4e6ef', color: canSend ? '#fff' : '#a1a5b7',
          fontSize: '14px', fontWeight: 700, cursor: canSend ? 'pointer' : 'default',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
        }}
      >
        {sending ? <span className="spinner-border spinner-border-sm" style={{width: '14px', height: '14px'}} /> : <i className="bi bi-whatsapp"></i>}
        {sending ? 'שולח...' : 'שלח הודעה'}
      </button>

      {/* History */}
      <div style={{marginTop: '18px', borderTop: '1px solid #eef0f4', paddingTop: '14px'}}>
        <div style={{fontSize: '12px', fontWeight: 700, color: '#5e6278', marginBottom: '8px'}}>
          <i className="bi bi-clock-history" style={{marginLeft: '5px'}}></i>היסטוריית הודעות
        </div>
        <WaHistoryList messages={history} loading={loading} />
      </div>
      </div>
    </Modal>
  )
}

// Shared list of sent WhatsApp messages, used by the send modal and the standalone history modal.
const WaHistoryList: FC<{messages: WaMessage[]; loading: boolean}> = ({messages, loading}) => {
  if (loading) return <div style={{fontSize: '12px', color: '#a1a5b7'}}>טוען...</div>
  if (messages.length === 0) return <div style={{fontSize: '12px', color: '#a1a5b7'}}>לא נשלחו הודעות עדיין</div>
  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '460px', overflowY: 'auto'}}>
      {messages.map(m => (
        <div key={m._id} style={{padding: '9px 11px', borderRadius: '8px', background: '#f9fafb', border: '1px solid #eef0f4'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
            <span style={{fontSize: '11px', fontWeight: 700, color: '#25a35a'}}>{m.template_name || 'הודעה'}</span>
            <span style={{fontSize: '10px', color: '#a1a5b7'}}>{new Date(m.createdAt).toLocaleString('he-IL')}</span>
          </div>
          <div style={{fontSize: '12px', color: '#5e6278', whiteSpace: 'pre-wrap', direction: 'rtl'}}>{m.message}</div>
          {m.sent_by && (m.sent_by.first_name || m.sent_by.last_name) && (
            <div style={{fontSize: '10px', color: '#a1a5b7', marginTop: '3px'}}>נשלח ע"י {m.sent_by.first_name || ''} {m.sent_by.last_name || ''}</div>
          )}
        </div>
      ))}
    </div>
  )
}

// ============================================
// WhatsApp templates manager — CRUD templates with male/female variants
// ============================================
const emptyForm = {name: '', male_text: '', female_text: ''}

const WhatsappTemplatesModal: FC<{onClose: () => void}> = ({onClose}) => {
  const [templates, setTemplates] = useState<WaTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null) // null = not editing, '' = new
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    getWaTemplates()
      .then(r => setTemplates(r.data.templates || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const startNew = () => { setEditingId(''); setForm(emptyForm) }
  const startEdit = (t: WaTemplate) => { setEditingId(t._id); setForm({name: t.name, male_text: t.male_text || '', female_text: t.female_text || ''}) }
  const cancel = () => { setEditingId(null); setForm(emptyForm) }

  const save = async () => {
    if (!form.name.trim()) { alert('יש להזין שם תבנית'); return }
    setSaving(true)
    try {
      if (editingId) await updateWaTemplate(editingId, form)
      else await createWaTemplate(form)
      cancel()
      load()
    } catch (e: any) {
      alert(e.response?.data?.message || 'שגיאה בשמירת תבנית')
    }
    setSaving(false)
  }

  const remove = async (t: WaTemplate) => {
    if (!window.confirm(`למחוק את התבנית "${t.name}"?`)) return
    try { await deleteWaTemplate(t._id); load() }
    catch (e: any) { alert(e.response?.data?.message || 'שגיאה במחיקה') }
  }

  const isEditing = editingId !== null

  return (
    <Modal onClose={onClose} title="תבניות הודעות וואטסאפ">
      <div style={{padding: '20px 24px 24px'}}>
      {!isEditing && (
        <button
          onClick={startNew}
          style={{marginBottom: '14px', padding: '9px 14px', borderRadius: '8px', border: 'none', background: '#25a35a', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px'}}
        >
          <i className="bi bi-plus-lg"></i>תבנית חדשה
        </button>
      )}

      {isEditing ? (
        <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
          <div>
            <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#5e6278', marginBottom: '5px'}}>שם התבנית</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({...f, name: e.target.value}))}
              placeholder="לדוגמה: תזכורת תשלום"
              style={{width: '100%', padding: '9px 10px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '13px', outline: 'none', direction: 'rtl'}}
            />
          </div>
          <div style={{fontSize: '11px', color: '#a1a5b7'}}>
            ניתן להשתמש ב-<code>{'{first_name}'}</code> ו-<code>{'{last_name}'}</code> שיוחלפו בפרטי הלקוח.
          </div>
          <div>
            <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#5e6278', marginBottom: '5px'}}>טקסט לזכר</label>
            <textarea
              value={form.male_text}
              onChange={e => setForm(f => ({...f, male_text: e.target.value}))}
              rows={4}
              placeholder="שלום {first_name}, ..."
              style={{width: '100%', padding: '9px 10px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '13px', outline: 'none', direction: 'rtl', resize: 'vertical'}}
            />
          </div>
          <div>
            <label style={{display: 'block', fontSize: '12px', fontWeight: 600, color: '#5e6278', marginBottom: '5px'}}>טקסט לנקבה</label>
            <textarea
              value={form.female_text}
              onChange={e => setForm(f => ({...f, female_text: e.target.value}))}
              rows={4}
              placeholder="שלום {first_name}, ..."
              style={{width: '100%', padding: '9px 10px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '13px', outline: 'none', direction: 'rtl', resize: 'vertical'}}
            />
          </div>
          <div style={{display: 'flex', gap: '8px'}}>
            <button
              onClick={save}
              disabled={saving}
              style={{flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: '#3699ff', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: saving ? 'default' : 'pointer'}}
            >
              {saving ? 'שומר...' : 'שמור'}
            </button>
            <button
              onClick={cancel}
              style={{padding: '10px 16px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', fontSize: '13px', fontWeight: 600, cursor: 'pointer'}}
            >
              ביטול
            </button>
          </div>
        </div>
      ) : loading ? (
        <div style={{fontSize: '13px', color: '#a1a5b7', padding: '20px', textAlign: 'center'}}>טוען...</div>
      ) : templates.length === 0 ? (
        <div style={{fontSize: '13px', color: '#a1a5b7', padding: '20px', textAlign: 'center'}}>אין תבניות עדיין</div>
      ) : (
        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          {templates.map(t => (
            <div key={t._id} style={{padding: '11px 12px', borderRadius: '9px', border: '1px solid #eef0f4', background: '#fbfcfd'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '5px'}}>
                <span style={{fontSize: '13px', fontWeight: 700, color: '#1e1e2d'}}>{t.name}</span>
                <div style={{display: 'flex', gap: '6px'}}>
                  <button onClick={() => startEdit(t)} title="ערוך" style={{border: '1px solid #e4e6ef', background: '#fff', color: '#3699ff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer'}}>
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button onClick={() => remove(t)} title="מחק" style={{border: '1px solid #ffd6dc', background: '#fff', color: '#f64e60', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer'}}>
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
              <div style={{fontSize: '11px', color: '#7e8299', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px'}}>
                <div><span style={{fontWeight: 600, color: '#8950fc'}}>זכר:</span> {t.male_text ? t.male_text.slice(0, 60) : '—'}</div>
                <div><span style={{fontWeight: 600, color: '#8950fc'}}>נקבה:</span> {t.female_text ? t.female_text.slice(0, 60) : '—'}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </Modal>
  )
}

// Bank transfer details — the block of text admins send a customer who wants to
// pay by transfer (bank, branch, account number, beneficiary, Bit…). Stored
// once for everyone, so nobody pastes last year's account number.
const BankDetailsModal: FC<{details: string; onSaved: (text: string) => void; onClose: () => void}> = ({details, onSaved, onClose}) => {
  const [text, setText] = useState(details)
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)

  const save = async () => {
    setSaving(true)
    try {
      const r = await saveBankDetails(text)
      onSaved(r.data?.details ?? text)
      onClose()
    } catch (e: any) {
      alert(e.response?.data?.message || 'שגיאה בשמירת הפרטים')
    }
    setSaving(false)
  }

  const copy = () => {
    if (!text.trim()) return
    navigator.clipboard?.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  return (
    <Modal onClose={onClose} title="פרטי העברה בנקאית">
      <div style={{padding: '20px 24px 24px'}}>
        <div style={{fontSize: '12px', color: '#7e8299', marginBottom: '10px'}}>
          הטקסט הזה משותף לכל האדמינים — כפתור "העתק פרטי העברה" אצל כל לקוח מעתיק בדיוק אותו.
        </div>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={9}
          placeholder={'בנק: לאומי (10)\nסניף: 800\nמספר חשבון: 123456\nעל שם: פיבי זיפר בע"מ\nביט: 050-0000000'}
          style={{width: '100%', padding: '10px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '13px', outline: 'none', direction: 'rtl', lineHeight: 1.6, resize: 'vertical'}}
        />
        <div style={{fontSize: '11px', color: '#a1a5b7', marginTop: '6px'}}>
          ניתן להשתמש ב-<code>{'{first_name}'}</code> ו-<code>{'{last_name}'}</code> — יוחלפו בפרטי הלקוח בעת ההעתקה מהשורה שלו.
        </div>

        <div style={{display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap'}}>
          <button
            onClick={save}
            disabled={saving}
            style={{padding: '9px 16px', borderRadius: '8px', border: 'none', background: '#159c5b', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: saving ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px'}}
          >
            {saving ? <span className="spinner-border spinner-border-sm" style={{width: '14px', height: '14px'}} /> : <i className="bi bi-check2"></i>}
            שמור
          </button>
          <button
            onClick={copy}
            disabled={!text.trim()}
            style={{padding: '9px 16px', borderRadius: '8px', border: '1px solid #b8e6cf', background: copied ? '#159c5b' : '#f2fff8', color: copied ? '#fff' : '#159c5b', fontSize: '13px', fontWeight: 600, cursor: text.trim() ? 'pointer' : 'not-allowed', opacity: text.trim() ? 1 : 0.5, display: 'inline-flex', alignItems: 'center', gap: '6px'}}
          >
            <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'}`}></i>{copied ? 'הועתק' : 'העתק'}
          </button>
          <button
            onClick={onClose}
            style={{padding: '9px 16px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', fontSize: '13px', fontWeight: 600, cursor: 'pointer'}}
          >
            ביטול
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ============================================
// ContactListPanel — the clients we owe a phone call
// ============================================
// Everything an admin needs to work the list without leaving the page: why the
// client is on it, what the AI thought, what their number is earning them, a
// running call log, and one button to take them off.
const ContactListPanel: FC<{
  tasks: ContactTask[]
  loading: boolean
  isMobile: boolean
  fmt: (n: number) => string
  vnActivity: Record<string, VnActivity>
  onClose: () => void
  onReload: () => void
  onResolved: (task: ContactTask) => void
  onTaskUpdated: (task: ContactTask) => void
  onOpenLinks: (account: any) => void
}> = ({tasks, loading, isMobile, fmt, vnActivity, onClose, onReload, onResolved, onTaskUpdated, onOpenLinks}) => {
  const [search, setSearch] = useState('')
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<string | null>(null)

  const searchLower = search.trim().toLowerCase()
  const filtered = searchLower
    ? tasks.filter(t => {
        const a = t.account || {}
        return [a.first_name, a.last_name, `${a.first_name} ${a.last_name}`, a.email, a.phone, a.company_name, t.reason]
          .filter(Boolean).map(String).join(' ').toLowerCase().includes(searchLower)
      })
    : tasks

  const addNote = async (task: ContactTask) => {
    const text = (noteDrafts[task._id] || '').trim()
    if (!text) return
    setBusy(task._id)
    try {
      const res = await addContactTaskNote(task._id, text)
      onTaskUpdated(res.data.task)
      setNoteDrafts(prev => ({...prev, [task._id]: ''}))
    } catch (e: any) {
      alert('שגיאה בשמירת ההערה: ' + (e?.response?.data?.error || e?.message || ''))
    } finally { setBusy(null) }
  }

  const removeNote = async (task: ContactTask, noteId: string) => {
    if (!window.confirm('למחוק את ההערה?')) return
    setBusy(task._id)
    try {
      await deleteContactTaskNote(task._id, noteId)
      onTaskUpdated({...task, notes: task.notes.filter(n => n._id !== noteId)})
    } catch (e: any) {
      alert('שגיאה במחיקת ההערה: ' + (e?.response?.data?.error || e?.message || ''))
    } finally { setBusy(null) }
  }

  const resolve = async (task: ContactTask) => {
    const a = task.account || {}
    if (!window.confirm(`להסיר את ${a.first_name || ''} ${a.last_name || ''} מרשימת הטיפול הטלפוני?`)) return
    setBusy(task._id)
    try {
      // Whatever is still in the note box is the outcome of the call — keep it.
      await resolveContactTask(task._id, (noteDrafts[task._id] || '').trim() || undefined)
      onResolved(task)
    } catch (e: any) {
      alert('שגיאה: ' + (e?.response?.data?.error || e?.message || ''))
    } finally { setBusy(null) }
  }

  return (
    <div style={{
      background: '#fff', borderRadius: '12px', padding: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '16px' : '24px',
      border: '2px solid #ffa800', boxShadow: '0 2px 12px rgba(255, 168, 0, 0.12)'
    }}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px'}}>
        <h4 style={{fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: '#b76e00', margin: 0, display: 'flex', alignItems: 'center', gap: '8px'}}>
          <i className="bi bi-telephone-fill"></i>
          <span>לטיפול טלפוני ({loading ? '...' : filtered.length}{filtered.length !== tasks.length ? `/${tasks.length}` : ''})</span>
          {loading && <span className="spinner-border spinner-border-sm payments-refreshing" style={{color: '#ffa800', width: '14px', height: '14px'}} />}
        </h4>
        <div style={{display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap'}}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="חפש לקוח..."
            style={{padding: '6px 10px', border: '1px solid #e4e6ef', borderRadius: '6px', fontSize: '12px', outline: 'none', direction: 'rtl', minWidth: '160px'}}
          />
          <button
            onClick={onReload}
            title="רענן"
            style={{padding: '6px 10px', borderRadius: '6px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', fontSize: '12px', cursor: 'pointer'}}
          ><i className="bi bi-arrow-clockwise"></i></button>
          <button
            onClick={onClose}
            style={{padding: '6px 10px', borderRadius: '6px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', fontSize: '12px', cursor: 'pointer'}}
          ><i className="bi bi-x-lg"></i></button>
        </div>
      </div>

      {!loading && filtered.length === 0 ? (
        <div style={{padding: '26px', textAlign: 'center', color: '#a1a5b7', fontSize: '13px'}}>
          <i className="bi bi-check2-circle" style={{fontSize: '26px', display: 'block', marginBottom: '6px', color: '#50cd89'}}></i>
          {tasks.length === 0
            ? 'אין לקוחות שממתינים לשיחה. סמן לקוח בכפתור "צריך ליצור קשר" בכרטיס שלו.'
            : `לא נמצאו תוצאות ל"${search}"`}
        </div>
      ) : (
        <div style={{display: 'grid', gap: '10px', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(420px, 1fr))'}}>
          {filtered.map(task => {
            const a = task.account || {}
            const vn = vnActivity[a._id]
            const verdictMeta = task.ai_review ? VERDICT_META[task.ai_review.verdict] : null
            const snap = task.snapshot || {}
            return (
              <div key={task._id} style={{
                border: '1px solid #ffe0a3', borderRadius: '10px', padding: '10px',
                background: '#fffdf7', opacity: busy === task._id ? 0.6 : 1
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '6px'}}>
                  <div style={{minWidth: 0}}>
                    <div style={{fontWeight: 700, fontSize: '14px', color: '#1e1e2d'}}>
                      {a.first_name} {a.last_name}
                      {a.company_name && <span style={{fontWeight: 400, color: '#a1a5b7', fontSize: '11px'}}> · {a.company_name}</span>}
                    </div>
                    <div style={{fontSize: '11px', color: '#a1a5b7', display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '2px'}}>
                      {a.phone && (
                        <a href={`tel:${a.phone}`} style={{color: '#3699ff', textDecoration: 'none', direction: 'ltr'}}>
                          <i className="bi bi-telephone" style={{marginLeft: '3px'}}></i>{a.phone}
                        </a>
                      )}
                      {a.phone && (
                        <a
                          href={`https://wa.me/972${String(a.phone).replace(/\D/g, '').replace(/^972/, '').replace(/^0/, '')}`}
                          target="_blank" rel="noreferrer"
                          style={{color: '#25a35a', textDecoration: 'none'}}
                        ><i className="bi bi-whatsapp" style={{marginLeft: '3px'}}></i>וואטסאפ</a>
                      )}
                      {a.email && <span>{a.email}</span>}
                    </div>
                  </div>
                  <div style={{textAlign: 'left', flexShrink: 0}}>
                    {a.monthly_payment_amount > 0 && (
                      <div style={{fontWeight: 700, fontSize: '13px', color: '#1e1e2d'}}>{fmt(a.monthly_payment_amount)}</div>
                    )}
                    <div style={{fontSize: '9px', color: '#a1a5b7'}}>{hebrewFromNow(task.createdAt)}</div>
                  </div>
                </div>

                {/* Why they're on the list */}
                {task.reason && (
                  <div style={{
                    fontSize: '11px', color: '#5e6278', background: '#fff8dd', borderRadius: '6px',
                    padding: '6px 8px', marginBottom: '6px', borderRight: '3px solid #ffa800', whiteSpace: 'pre-wrap'
                  }}>{task.reason}</div>
                )}

                {/* The AI's take, if it has one */}
                {task.ai_review && verdictMeta && (
                  <div style={{
                    fontSize: '11px', color: '#3f4254', background: verdictMeta.bg,
                    border: `1px solid ${verdictMeta.border}`, borderRadius: '6px', padding: '6px 8px', marginBottom: '6px'
                  }}>
                    <span style={{fontWeight: 700, color: verdictMeta.color, marginLeft: '6px'}}>
                      <i className={`bi ${verdictMeta.icon}`} style={{marginLeft: '4px'}}></i>{verdictMeta.label}
                    </span>
                    {task.ai_review.comment}
                  </div>
                )}

                {/* Snapshot + live virtual-number activity */}
                <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap', fontSize: '11px', marginBottom: '6px'}}>
                  <span><strong style={{color: (snap.posted_14d || 0) > 0 ? '#50cd89' : '#f64e60'}}>{snap.posted_14d ?? 0}</strong> <span style={{fontSize: '9px', color: '#999'}}>פוסטים בשבועיים</span></span>
                  <span><strong style={{color: snap.has_card ? '#50cd89' : '#f64e60'}}>{snap.has_card ? 'יש' : 'אין'}</strong> <span style={{fontSize: '9px', color: '#999'}}>כרטיס</span></span>
                  {snap.last_payment_failed && <span style={{color: '#f64e60', fontWeight: 700, fontSize: '10px'}}>התשלום האחרון נכשל</span>}
                  {vn && (
                    <span style={{color: vn.total_leads === 0 ? '#c2334a' : '#8950fc', fontWeight: 600}}>
                      <i className="bi bi-telephone-inbound" style={{marginLeft: '3px'}}></i>
                      {vn.phone_calls} שיחות · {vn.whatsapp_leads} וואטסאפ · {vn.post_comments} תגובות
                    </span>
                  )}
                </div>

                {/* Call log */}
                {task.notes.length > 0 && (
                  <div style={{marginBottom: '6px', display: 'flex', flexDirection: 'column', gap: '3px', maxHeight: '150px', overflowY: 'auto'}}>
                    {task.notes.map(n => (
                      <div key={n._id} style={{
                        fontSize: '11px', color: '#5e6278', background: '#fff', border: '1px solid #eef0f5',
                        borderRadius: '6px', padding: '5px 8px', display: 'flex', justifyContent: 'space-between', gap: '6px'
                      }}>
                        <div style={{minWidth: 0}}>
                          <div style={{whiteSpace: 'pre-wrap'}}>{n.text}</div>
                          <div style={{fontSize: '9px', color: '#a1a5b7', marginTop: '2px'}}>
                            {new Date(n.created_at).toLocaleString('he-IL', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'})}
                            {n.admin && ` · ${n.admin.first_name || ''} ${n.admin.last_name || ''}`}
                          </div>
                        </div>
                        <button
                          onClick={() => removeNote(task, n._id)}
                          title="מחק"
                          style={{border: 'none', background: 'transparent', color: '#f64e60', cursor: 'pointer', padding: '0 2px', fontSize: '10px', flexShrink: 0}}
                        ><i className="bi bi-trash"></i></button>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{display: 'flex', gap: '6px', marginBottom: '6px'}}>
                  <input
                    type="text"
                    value={noteDrafts[task._id] || ''}
                    onChange={e => setNoteDrafts(prev => ({...prev, [task._id]: e.target.value}))}
                    onKeyDown={e => { if (e.key === 'Enter') addNote(task) }}
                    placeholder="מה קרה בשיחה?"
                    style={{flex: 1, padding: '6px 10px', border: '1px solid #e4e6ef', borderRadius: '6px', fontSize: '12px', outline: 'none', direction: 'rtl'}}
                  />
                  <button
                    onClick={() => addNote(task)}
                    disabled={!(noteDrafts[task._id] || '').trim() || busy === task._id}
                    style={{
                      padding: '6px 12px', borderRadius: '6px', border: 'none',
                      background: (noteDrafts[task._id] || '').trim() ? '#3699ff' : '#e4e6ef',
                      color: (noteDrafts[task._id] || '').trim() ? '#fff' : '#a1a5b7',
                      fontSize: '12px', fontWeight: 600, cursor: (noteDrafts[task._id] || '').trim() ? 'pointer' : 'default'
                    }}
                  >הוסף</button>
                </div>

                <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                  <button
                    onClick={() => onOpenLinks(a)}
                    style={{flex: '1 1 140px', padding: '6px 12px', borderRadius: '6px', border: 'none', background: '#25a35a', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer'}}
                  >
                    <i className="bi bi-link-45deg" style={{marginLeft: '4px'}}></i>שלח קישורי תשלום
                  </button>
                  <button
                    onClick={() => resolve(task)}
                    disabled={busy === task._id}
                    title="הסר מהרשימה — הרשומה נשמרת להיסטוריה"
                    style={{flex: '1 1 120px', padding: '6px 12px', borderRadius: '6px', border: '1px solid #50cd89', background: '#fff', color: '#1a7f5a', fontSize: '12px', fontWeight: 700, cursor: busy === task._id ? 'default' : 'pointer'}}
                  >
                    <i className="bi bi-check2-circle" style={{marginLeft: '4px'}}></i>הסר מהרשימה
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ============================================
// PaymentLinksModal — WhatsApp both never-expiring links to one client
// ============================================
// Prices resolve override → the client's own agreed rate → global default. The
// admin can save an override for this client, or tweak the numbers for this send
// only. The links themselves are branded /pay/<id> URLs that mint a fresh
// Cardcom page per visit, so they never expire.
const PaymentLinksModal: FC<{
  account: PaymentAccount
  fmt: (n: number) => string
  onClose: () => void
  onSent: (waMessage: WaMessage) => void
}> = ({account, fmt, onClose, onSent}) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [prices, setPrices] = useState<ResolvedPrices | null>(null)
  const [override, setOverride] = useState<PriceOverride>({})
  // The message is edited as a TEMPLATE (tokens intact); `savedTemplate` is what
  // the server currently has, so we can tell whether there are unsaved edits.
  const [template, setTemplate] = useState('')
  const [savedTemplate, setSavedTemplate] = useState('')
  const [templateSource, setTemplateSource] = useState<'customer' | 'default_male' | 'default_female'>('default_male')
  const [hasCustomMessage, setHasCustomMessage] = useState(false)
  const [savingMessage, setSavingMessage] = useState(false)
  const [messageSaved, setMessageSaved] = useState(false)
  const [phone, setPhone] = useState('')
  const [existing, setExisting] = useState<Array<{_id: string; amount: number; period_label?: string; url: string}>>([])
  const [monthly, setMonthly] = useState(0)
  const [yearly, setYearly] = useState(0)
  const [monthlyInst, setMonthlyInst] = useState(1)
  const [yearlyInst, setYearlyInst] = useState(12)
  const [savingOverride, setSavingOverride] = useState(false)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState<{links: {monthly: string; yearly: string}; message: string} | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await getCustomerPaymentLinks(account._id)
        if (cancelled) return
        const d = res.data
        setPrices(d.prices)
        setOverride(d.override || {})
        setTemplate(d.template)
        setSavedTemplate(d.template)
        setTemplateSource(d.template_source)
        setHasCustomMessage(d.has_custom_message)
        setPhone(d.phone)
        setExisting(d.existing_links || [])
        setMonthly(d.prices.monthly)
        setYearly(d.prices.yearly)
        setMonthlyInst(d.prices.monthly_installments)
        setYearlyInst(d.prices.yearly_installments)
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.error || e?.message || 'שגיאה בטעינת המחירים')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [account._id])

  const livePrices = {monthly, yearly, monthly_installments: monthlyInst, yearly_installments: yearlyInst}
  const preview = renderLinksMessage(template, account, livePrices, {
    monthly: '‹קישור חודשי›', yearly: '‹קישור שנתי›',
  })
  const missingTokens = REQUIRED_LINK_TOKENS.filter(t => template.indexOf(t) === -1)
  const messageDirty = template !== savedTemplate

  const SOURCE_LABEL: Record<string, string> = {
    override: 'מחיר מותאם ללקוח',
    account: 'לפי החיוב החודשי של הלקוח',
    default: 'ברירת מחדל גלובלית',
    derived: 'מחושב מהמחיר החודשי',
  }

  const saveOverride = async () => {
    setSavingOverride(true)
    setError(null)
    try {
      const res = await saveCustomerPrices(account._id, {
        monthly, yearly, monthly_installments: monthlyInst, yearly_installments: yearlyInst,
      })
      setPrices(res.data.prices)
      setOverride(res.data.override || {})
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'שגיאה בשמירת המחירים')
    } finally { setSavingOverride(false) }
  }

  const clearOverride = async () => {
    setSavingOverride(true)
    setError(null)
    try {
      const res = await saveCustomerPrices(account._id, {monthly: 0, yearly: 0, monthly_installments: 0, yearly_installments: 0})
      setPrices(res.data.prices)
      setOverride({})
      setMonthly(res.data.prices.monthly)
      setYearly(res.data.prices.yearly)
      setMonthlyInst(res.data.prices.monthly_installments)
      setYearlyInst(res.data.prices.yearly_installments)
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'שגיאה באיפוס המחירים')
    } finally { setSavingOverride(false) }
  }

  // Persist this customer's own wording, or clear it back to the global default.
  const persistMessage = async (text: string) => {
    setSavingMessage(true); setError(null); setMessageSaved(false)
    try {
      const res = await saveCustomerMessage(account._id, text)
      setTemplate(res.data.template)
      setSavedTemplate(res.data.template)
      setTemplateSource(res.data.template_source)
      setHasCustomMessage(res.data.has_custom_message)
      setMessageSaved(true)
      window.setTimeout(() => setMessageSaved(false), 2000)
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'שגיאה בשמירת ההודעה')
    } finally { setSavingMessage(false) }
  }

  const send = async () => {
    if (!(monthly > 0) || !(yearly > 0)) { setError('יש להזין מחיר חודשי ומחיר שנתי'); return }
    if (missingTokens.length) {
      if (!window.confirm(`ההודעה לא כוללת ${missingTokens.join(' ו-')} — הלקוח לא יקבל את הקישור. לשלוח בכל זאת?`)) return
    }
    if (!window.confirm(`לשלוח ל${account.first_name} ${account.last_name} (${phone}) קישורי תשלום?\nחודשי: ₪${monthly}\nשנתי: ₪${yearly}`)) return
    setSending(true)
    setError(null)
    try {
      const res = await sendPaymentLinksWhatsapp(account._id, {
        monthly, yearly,
        monthly_installments: monthlyInst,
        yearly_installments: yearlyInst,
        // Sent as a template — the server substitutes the freshly minted links.
        message: template,
      })
      if (res.data.success && res.data.links) {
        setSent({links: res.data.links, message: res.data.message || ''})
        if (res.data.wa_message) onSent(res.data.wa_message)
      } else {
        setError(res.data.error || 'השליחה נכשלה')
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'השליחה נכשלה')
    } finally { setSending(false) }
  }

  const priceBox = (
    label: string, value: number, setValue: (n: number) => void,
    inst: number, setInst: (n: number) => void, source?: string, accent = '#3699ff'
  ) => (
    <div style={{flex: '1 1 200px', border: `1px solid ${accent}33`, borderRadius: '8px', padding: '10px', background: '#fff'}}>
      <div style={{fontSize: '12px', fontWeight: 700, color: accent, marginBottom: '6px'}}>{label}</div>
      <div style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
        <span style={{fontSize: '13px', color: '#7e8299'}}>₪</span>
        <input
          type="number" min={0} value={value}
          onChange={e => setValue(Number(e.target.value))}
          style={{width: '100%', padding: '6px 8px', border: '1px solid #e4e6ef', borderRadius: '6px', fontSize: '14px', fontWeight: 700, outline: 'none'}}
        />
      </div>
      <label style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#7e8299', marginTop: '6px'}}>
        <span>תשלומים:</span>
        <input
          type="number" min={1} max={36} value={inst}
          onChange={e => setInst(Math.min(36, Math.max(1, Number(e.target.value))))}
          style={{width: '60px', padding: '4px 6px', border: '1px solid #e4e6ef', borderRadius: '6px', fontSize: '12px', outline: 'none'}}
        />
      </label>
      {source && <div style={{fontSize: '9px', color: '#a1a5b7', marginTop: '4px'}}>{SOURCE_LABEL[source] || source}</div>}
    </div>
  )

  return (
    <Modal onClose={onClose} title={`קישורי תשלום — ${account.first_name} ${account.last_name}`}>
      {loading ? (
        <div style={{padding: '40px', textAlign: 'center', color: '#a1a5b7'}}>
          <div className="spinner-border" style={{color: '#25a35a', width: '32px', height: '32px'}} role="status" />
        </div>
      ) : sent ? (
        <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
          <div style={{background: '#e8fff3', border: '1px solid #b6ecd5', borderRadius: '8px', padding: '12px', color: '#1a7f5a', fontSize: '13px', fontWeight: 600}}>
            <i className="bi bi-check-circle-fill" style={{marginLeft: '6px'}}></i>
            ההודעה נשלחה ל-{phone}
          </div>
          {(['monthly', 'yearly'] as const).map(k => (
            <div key={k} style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
              <span style={{fontSize: '12px', fontWeight: 700, color: '#5e6278', minWidth: '52px'}}>{k === 'monthly' ? 'חודשי' : 'שנתי'}</span>
              <input readOnly value={sent.links[k]} style={{flex: 1, padding: '6px 10px', border: '1px solid #e4e6ef', borderRadius: '6px', fontSize: '12px', direction: 'ltr'}} />
              <button
                onClick={() => navigator.clipboard.writeText(sent.links[k])}
                style={{padding: '6px 10px', borderRadius: '6px', border: '1px solid #e4e6ef', background: '#fff', color: '#3699ff', fontSize: '12px', cursor: 'pointer'}}
              ><i className="bi bi-clipboard"></i></button>
            </div>
          ))}
          <pre style={{background: '#f9fafb', border: '1px solid #eef0f5', borderRadius: '8px', padding: '10px', fontSize: '11px', color: '#5e6278', whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, maxHeight: '220px', overflowY: 'auto'}}>{sent.message}</pre>
          <button onClick={onClose} style={{padding: '9px 16px', borderRadius: '8px', border: 'none', background: '#3699ff', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer'}}>סגור</button>
        </div>
      ) : (
        <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
          {error && (
            <div style={{background: '#fff5f8', border: '1px solid #f9c4cf', borderRadius: '8px', padding: '10px', color: '#c2334a', fontSize: '12px'}}>{error}</div>
          )}

          <div style={{fontSize: '12px', color: '#7e8299'}}>
            <i className="bi bi-whatsapp" style={{color: '#25a35a', marginLeft: '5px'}}></i>
            נשלח אל <strong style={{direction: 'ltr', display: 'inline-block'}}>{phone || '— אין טלפון —'}</strong>
            {account.gender ? '' : ' · מגדר לא הוגדר, ייעשה שימוש בנוסח ברירת המחדל'}
          </div>

          <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
            {priceBox('מחיר חודשי', monthly, setMonthly, monthlyInst, setMonthlyInst, prices?.monthly_source, '#3699ff')}
            {priceBox('מחיר שנתי', yearly, setYearly, yearlyInst, setYearlyInst, prices?.yearly_source, '#8950fc')}
          </div>

          <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center'}}>
            <button
              onClick={saveOverride}
              disabled={savingOverride}
              title="שמור את המחירים האלה כמחירים הקבועים של הלקוח הזה"
              style={{padding: '7px 14px', borderRadius: '8px', border: '1px solid #3699ff', background: '#fff', color: '#3699ff', fontSize: '12px', fontWeight: 600, cursor: savingOverride ? 'default' : 'pointer'}}
            >
              <i className="bi bi-pin-angle" style={{marginLeft: '5px'}}></i>
              {savingOverride ? 'שומר...' : 'קבע כמחיר של הלקוח'}
            </button>
            {(override.monthly || override.yearly) && (
              <button
                onClick={clearOverride}
                disabled={savingOverride}
                title="חזור למחירי ברירת המחדל"
                style={{padding: '7px 14px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', fontSize: '12px', fontWeight: 600, cursor: savingOverride ? 'default' : 'pointer'}}
              >
                <i className="bi bi-arrow-counterclockwise" style={{marginLeft: '5px'}}></i>אפס לברירת מחדל
              </button>
            )}
            <span style={{fontSize: '11px', color: '#a1a5b7'}}>
              שינוי כאן בלי שמירה ישפיע רק על השליחה הנוכחית
            </span>
          </div>

          {existing.length > 0 && (
            <details style={{fontSize: '11px', color: '#7e8299'}}>
              <summary style={{cursor: 'pointer', fontWeight: 600}}>קישורים קיימים ללקוח ({existing.length})</summary>
              <div style={{marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px'}}>
                {existing.map(l => (
                  <div key={l._id} style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                    <span style={{minWidth: '90px'}}>{fmt(l.amount)} · {l.period_label || ''}</span>
                    <input readOnly value={l.url} style={{flex: 1, padding: '4px 8px', border: '1px solid #eef0f5', borderRadius: '4px', fontSize: '10px', direction: 'ltr'}} />
                  </div>
                ))}
              </div>
            </details>
          )}

          <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px'}}>
              <span style={{fontSize: '12px', fontWeight: 700, color: '#5e6278'}}>
                נוסח ההודעה
                <span style={{fontWeight: 400, color: '#a1a5b7', marginInlineStart: 6}}>
                  {templateSource === 'customer'
                    ? '· נוסח אישי ללקוח זה'
                    : `· נוסח ברירת המחדל (${templateSource === 'default_female' ? 'נקבה' : 'זכר'})`}
                </span>
                {messageDirty && <span style={{color: '#ffa800', fontWeight: 600, marginInlineStart: 6}}>· לא נשמר</span>}
              </span>
              <div style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                {messageSaved && <span style={{fontSize: '11px', color: '#1a7f5a', fontWeight: 600}}><i className="bi bi-check2" style={{marginLeft: '3px'}}></i>נשמר</span>}
                <button
                  onClick={() => persistMessage(template)}
                  disabled={savingMessage || !template.trim()}
                  title="שמור את הנוסח הזה כנוסח הקבוע של הלקוח — ישמש גם בשליחות הבאות"
                  style={{padding: '4px 10px', borderRadius: '6px', border: '1px solid #3699ff', background: '#fff', color: '#3699ff', fontSize: '11px', fontWeight: 600, cursor: savingMessage ? 'default' : 'pointer'}}
                >
                  <i className="bi bi-pin-angle" style={{marginLeft: '4px'}}></i>
                  {savingMessage ? 'שומר...' : 'שמור כנוסח של הלקוח'}
                </button>
                {(hasCustomMessage || messageDirty) && (
                  <button
                    onClick={() => {
                      if (hasCustomMessage) {
                        if (!window.confirm('למחוק את הנוסח האישי ולחזור לנוסח ברירת המחדל?')) return
                        persistMessage('')
                      } else {
                        setTemplate(savedTemplate)
                      }
                    }}
                    disabled={savingMessage}
                    title={hasCustomMessage ? 'מחק את הנוסח האישי וחזור לברירת המחדל' : 'בטל את השינויים'}
                    style={{padding: '4px 10px', borderRadius: '6px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', fontSize: '11px', fontWeight: 600, cursor: savingMessage ? 'default' : 'pointer'}}
                  >
                    <i className="bi bi-arrow-counterclockwise" style={{marginLeft: '4px'}}></i>
                    {hasCustomMessage ? 'חזור לברירת מחדל' : 'בטל שינויים'}
                  </button>
                )}
              </div>
            </div>
            <textarea
              value={template}
              onChange={e => setTemplate(e.target.value)}
              rows={9}
              style={{width: '100%', padding: '10px', border: `1px solid ${missingTokens.length ? '#f9c4cf' : '#e4e6ef'}`, borderRadius: '8px', fontSize: '12px', direction: 'rtl', outline: 'none', resize: 'vertical', lineHeight: 1.5}}
            />
            {missingTokens.length > 0 && (
              <div style={{fontSize: '11px', color: '#c2334a', marginTop: '4px', fontWeight: 600}}>
                <i className="bi bi-exclamation-triangle-fill" style={{marginLeft: '4px'}}></i>
                חסר {missingTokens.join(' ו-')} — בלעדיהם הלקוח לא יקבל קישור לתשלום
              </div>
            )}
            <div style={{fontSize: '10px', color: '#a1a5b7', marginTop: '4px'}}>
              תגים זמינים: {'{first_name}'} · {'{last_name}'} · {'{monthly_price}'} · {'{yearly_price}'} · {'{monthly_link}'} · {'{yearly_link}'} · {'{monthly_installments_text}'} · {'{yearly_installments_text}'}
            </div>

            {/* Exactly what goes out, with the current prices filled in. */}
            <div style={{marginTop: '8px'}}>
              <div style={{fontSize: '11px', fontWeight: 700, color: '#7e8299', marginBottom: '3px'}}>
                <i className="bi bi-eye" style={{marginLeft: '4px'}}></i>תצוגה מקדימה
              </div>
              <pre style={{
                background: '#f2fbf6', border: '1px solid #e0f3e8', borderRadius: '8px', padding: '10px',
                fontSize: '11px', color: '#3f4254', whiteSpace: 'pre-wrap', fontFamily: 'inherit',
                margin: 0, maxHeight: '200px', overflowY: 'auto', direction: 'rtl'
              }}>{preview}</pre>
            </div>
          </div>

          <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
            <button
              onClick={onClose}
              style={{padding: '9px 16px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', fontSize: '13px', fontWeight: 600, cursor: 'pointer'}}
            >ביטול</button>
            <button
              onClick={send}
              disabled={sending || !phone}
              style={{
                padding: '9px 20px', borderRadius: '8px', border: 'none',
                background: sending || !phone ? '#a8d8bd' : '#25a35a', color: '#fff',
                fontSize: '13px', fontWeight: 700, cursor: sending || !phone ? 'default' : 'pointer'
              }}
            >
              <i className="bi bi-whatsapp" style={{marginLeft: '6px'}}></i>
              {sending ? 'שולח...' : 'שלח בוואטסאפ'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ============================================
// PricingSettingsModal — global defaults behind every payment-links message
// ============================================
const PricingSettingsModal: FC<{onClose: () => void}> = ({onClose}) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [pricing, setPricing] = useState<LinkPricing | null>(null)

  useEffect(() => {
    let cancelled = false
    getLinkPricing()
      .then(res => { if (!cancelled) setPricing(res.data.pricing) })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'שגיאה בטעינה') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const save = async () => {
    if (!pricing) return
    setSaving(true); setError(null); setSaved(false)
    try {
      const res = await saveLinkPricing(pricing)
      setPricing(res.data.pricing)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2000)
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'שגיאה בשמירה')
    } finally { setSaving(false) }
  }

  const field = (label: string, key: keyof LinkPricing, hint?: string) => (
    <label style={{flex: '1 1 150px', display: 'flex', flexDirection: 'column', gap: '4px'}}>
      <span style={{fontSize: '12px', fontWeight: 600, color: '#5e6278'}}>{label}</span>
      <input
        type="number" min={0}
        value={pricing ? (pricing[key] as number) : 0}
        onChange={e => setPricing(p => p ? {...p, [key]: Number(e.target.value)} : p)}
        style={{padding: '7px 10px', border: '1px solid #e4e6ef', borderRadius: '6px', fontSize: '13px', outline: 'none'}}
      />
      {hint && <span style={{fontSize: '10px', color: '#a1a5b7'}}>{hint}</span>}
    </label>
  )

  return (
    <Modal onClose={onClose} title="מחירי קישורי תשלום — ברירת מחדל">
      {loading || !pricing ? (
        <div style={{padding: '40px', textAlign: 'center'}}>
          <div className="spinner-border" style={{color: '#8950fc', width: '32px', height: '32px'}} role="status" />
        </div>
      ) : (
        <div style={{display: 'flex', flexDirection: 'column', gap: '14px'}}>
          {error && <div style={{background: '#fff5f8', border: '1px solid #f9c4cf', borderRadius: '8px', padding: '10px', color: '#c2334a', fontSize: '12px'}}>{error}</div>}

          <div style={{fontSize: '12px', color: '#7e8299', background: '#f9fafb', borderRadius: '8px', padding: '10px', lineHeight: 1.5}}>
            אלה מחירי ברירת המחדל. לכל לקוח נבחר קודם המחיר המותאם שלו, אחריו החיוב החודשי שכבר מוגדר לו, ורק אז המחירים כאן.
            אפשר לקבוע מחיר מותאם ללקוח מתוך חלון "שלח קישורי תשלום" בכרטיס שלו.
          </div>

          <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
            {field('מחיר חודשי', 'monthly', 'כשאין ללקוח חיוב חודשי מוגדר')}
            {field('מחיר שנתי', 'yearly', '0 = לחשב מהמחיר החודשי')}
            {field('מכפיל שנתי', 'yearly_multiplier', 'כמה חודשים נגבים בשנה (10 = חודשיים מתנה)')}
          </div>
          <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
            {field('תשלומים — חודשי', 'monthly_installments')}
            {field('תשלומים — שנתי', 'yearly_installments')}
          </div>

          {(['message_male', 'message_female'] as const).map(k => (
            <label key={k} style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
              <span style={{fontSize: '12px', fontWeight: 600, color: '#5e6278'}}>
                {k === 'message_male' ? 'נוסח ההודעה — זכר' : 'נוסח ההודעה — נקבה'}
              </span>
              <textarea
                value={pricing[k]}
                onChange={e => setPricing(p => p ? {...p, [k]: e.target.value} : p)}
                rows={8}
                style={{width: '100%', padding: '10px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '12px', direction: 'rtl', outline: 'none', resize: 'vertical', lineHeight: 1.5}}
              />
            </label>
          ))}

          <div style={{fontSize: '10px', color: '#a1a5b7'}}>
            תגים: {'{first_name}'} · {'{last_name}'} · {'{monthly_price}'} · {'{yearly_price}'} · {'{monthly_link}'} · {'{yearly_link}'} · {'{monthly_installments_text}'} · {'{yearly_installments_text}'}
          </div>

          <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center'}}>
            {saved && <span style={{fontSize: '12px', color: '#1a7f5a', fontWeight: 600}}><i className="bi bi-check2" style={{marginLeft: '4px'}}></i>נשמר</span>}
            <button
              onClick={onClose}
              style={{padding: '9px 16px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', fontSize: '13px', fontWeight: 600, cursor: 'pointer'}}
            >סגור</button>
            <button
              onClick={save}
              disabled={saving}
              style={{padding: '9px 20px', borderRadius: '8px', border: 'none', background: saving ? '#c9b6ff' : '#8950fc', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: saving ? 'default' : 'pointer'}}
            >{saving ? 'שומר...' : 'שמור'}</button>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default PaymentsPage
